import { PAEnvironment } from './PAEnvironment'
import { ESDataStore, ESDataStoreConstructorOptions, IESDataStoreContextExtensionOptions } from './lib/ESDataStore'
import { HttpAdapter } from 'js-data-http'
import { HttpAdapter as HttpAdapterNode } from 'js-data-http-node'
import { SapHanaAdapter } from 'js-data-saphana'
import { JSDataLoopbackRESTAdapterBrowser } from './lib/JSDataLoopbackRESTAdapterBrowser'
import { JSDataLoopbackRESTAdapterNode } from './lib/JSDataLoopbackRESTAdapterNode'
import * as WBSchemas from './PAdbSchema'
import * as WBSchemas_Other from './PAdbSchema.Other'
import * as WBSchemas_DataFactory from './PAdbSchema.DataFactory'
import * as WBSchemas_Cntrl from './PAdbSchema.ControlPoints'
import * as WBSchemas_Security from './PAdbSchema.Security'
import * as WBSchemas_DM from './PAdbSchema.DataMart'
import * as WBSchemas_PEA from './PAdbSchema.PEA'
import * as WBSchemas_PM from './PAdbSchema.PM'
import * as WBSchemas_PPA from './PAdbSchema.PPA'
import * as WBSchemaRows from './PAdbSchemaRows'
import * as WBSchemas_Process from './PAdbSchema.ProcessingFW'
import { IKeyGenerator } from './lib/libIF'
import {
    dctLogicalKeysByEntitySet,
    dctMapperToKeyGenSpecs,
    dctNonParentForeignKeysByEntitySet,
    dctSchemaByEntitySet,
    dctTableNameByEntitySet, esWB_External_Product_RelationShip,
    PAdbSchema_addCustomColumns
} from './PAdbSchema'
import {
    WB_External_Product_Relationship,
    WB_External_Product_Relationship_JSData_RELs
} from "./PAdbSchema.DataFactory";

interface PAdbContextConstructorOptions extends ESDataStoreConstructorOptions {
    ApiBaseURL?: string
}

class PAdbContext extends ESDataStore {
    static instance_count: number = 0

    sRESTBaseURL: string
    aHttpAdapterBrowser: HttpAdapter
    aHttpAdapterNode: HttpAdapterNode
    aSapHanaAdapter: SapHanaAdapter

    //create a dictionary to hold key generators by mapper name
    dctMapperToKeyGen: { [mapper_name: string]: IKeyGenerator }

    constructor(options?: PAdbContextConstructorOptions) {
        super(options)
        options = options || {}

        PAdbSchema_addCustomColumns()

        const bDebug = true
        const applySchema: boolean = true

        if ('ApiBaseURL' in options) this.sRESTBaseURL = options.ApiBaseURL
        else this.sRESTBaseURL = PAEnvironment.getValue(PAEnvironment.Network_URL_Base, '/')

        const contextExtensionOptions: IESDataStoreContextExtensionOptions = {
            dctSchemaByEntitySet: dctSchemaByEntitySet,
            dctTableNameByEntitySet: dctTableNameByEntitySet,
            dctLogicalKeysByEntitySet: dctLogicalKeysByEntitySet,
            dctNonParentForeignKeysByEntitySet: dctNonParentForeignKeysByEntitySet,
            dctMapperToKeyGenSpecs: dctMapperToKeyGenSpecs,
            keyGen_end_point: this.sRESTBaseURL + '/keygen'
        }

        this.setContextExtensionObjects(contextExtensionOptions) //use the esdatastore object to hold state values of dictionaries that will be used for KeyGens and such

        this.dctMapperToKeyGen = {}

        const bIsNode = PAEnvironment.isNode()
        const bIsBrowser = !bIsNode
        const bUseHanaClient = PAEnvironment.getValue(PAEnvironment.AdapterUseHanaClient, false)

        if (bIsBrowser) {
            this.aHttpAdapterBrowser = new JSDataLoopbackRESTAdapterBrowser()

            // Options
            // http://api.js-data.io/js-data-http/latest/HttpAdapter.html#HttpAdapter__anchor
            const httpOptions = {
                basePath: this.sRESTBaseURL,
                debug: true
            }
            this.registerAdapter('http', this.aHttpAdapterBrowser, httpOptions)
        } else if (bUseHanaClient) {
            // Setup for SAP Hana Client
            const hanaOpts = PAEnvironment.getValue(PAEnvironment.AdapterHanaClientOpts, {})
            this.aSapHanaAdapter = new SapHanaAdapter({
                hanaOpts
            })
            this.registerAdapter('hana', this.aSapHanaAdapter, { default: true })
        } // Setup for Node
        else {
            // This needs to be different, the node Http adapter is different..
            this.aHttpAdapterNode = new JSDataLoopbackRESTAdapterNode()

            // Options
            // http://api.js-data.io/js-data-http/latest/HttpAdapter.html#HttpAdapter__anchor
            const httpOptions = {
                basePath: this.sRESTBaseURL,
                default: true,
                debug: true
            }
            this.registerAdapter('http', this.aHttpAdapterNode, httpOptions)
        }

        //
        //  Setup DataStore
        //

        // <editor-fold desc="Define Mappers">

        // <editor-fold desc="Define Mappers - Master Dataset / Source">

        // Master Data Set - Source
        this.defineMapper(WBSchemas.esWB_MasterData_Set, {
            name: WBSchemas.esWB_MasterData_Set,
            idAttribute: 'WB_MDSET_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_MasterData_Set),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_MasterData_Set),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_MasterData_Set),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_MasterData_Set),
            endpoint: this.sRESTBaseURL + '/api/WB_MDSets',
            recordClass: WBSchemaRows.WB_MasterDataSetRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_MasterData_Set,
            relations: WBSchemas_DataFactory.WB_MasterData_Set_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Source, {
            name: WBSchemas.esWB_Source,
            idAttribute: 'WB_SOURCE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Source),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Source),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Source),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Source),
            endpoint: this.sRESTBaseURL + '/api/Sources',
            recordClass: WBSchemaRows.WB_SourceRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Source,
            relations: WBSchemas_DataFactory.WB_Source_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Source_SetType, {
            name: WBSchemas.esWB_Source_SetType,
            idAttribute: 'WB_SOURCESR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Source_SetType),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Source_SetType),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Source_SetType),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Source_SetType),
            endpoint: this.sRESTBaseURL + '/api/SourceSRs',
            recordClass: WBSchemaRows.WB_SourceSetTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Source_SetType,
            relations: WBSchemas_DataFactory.WB_Source_SetType_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Master DataSet - Key Sets">
        // Set
        this.defineMapper(WBSchemas.esWB_Set, {
            name: WBSchemas.esWB_Set,
            idAttribute: 'WB_SET_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Set),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Set),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Set),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Set),
            endpoint: this.sRESTBaseURL + '/api/Sets',
            recordClass: WBSchemaRows.WB_SetsRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Sets,
            relations: WBSchemas_DataFactory.WB_Sets_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Entity Attributes">

        this.defineMapper(WBSchemas.esWB_Entity, {
            name: WBSchemas.esWB_Entity,
            idAttribute: 'WB_ENTITY_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Entity),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Entity),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Entity),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Entity),
            endpoint: this.sRESTBaseURL + '/api/WB_Entities',
            recordClass: WBSchemaRows.WB_EntityRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Entity,
            relations: WBSchemas_Other.WB_Entity_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Entity_AttributeType, {
            name: WBSchemas.esWB_Entity_AttributeType,
            idAttribute: 'WB_ENTITYAMR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Entity_AttributeType),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Entity_AttributeType),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Entity_AttributeType),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Entity_AttributeType),
            endpoint: this.sRESTBaseURL + '/api/WB_EntityAMRs',
            recordClass: WBSchemaRows.WB_EntityAttributeTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Entity_AttributeType,
            relations: WBSchemas_Other.WB_Entity_AttributeType_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_AttributeType, {
            name: WBSchemas.esWB_AttributeType,
            idAttribute: 'WB_ATTRIBUTEM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_AttributeType),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_AttributeType),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_AttributeType),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_AttributeType),
            endpoint: this.sRESTBaseURL + '/api/WB_AttributeMs',
            recordClass: WBSchemaRows.WB_AttributeTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_AttributeType,
            relations: WBSchemas_Other.WB_AttributeType_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Master DataSet - Enterprise">

        this.defineMapper(WBSchemas.esWB_Division, {
            name: WBSchemas.esWB_Division,
            idAttribute: 'WB_DIVISION_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Division),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Division),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Division),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Division),
            endpoint: this.sRESTBaseURL + '/api/WB_Divisions',
            recordClass: WBSchemaRows.WB_DivisionRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Division,
            relations: WBSchemas_DataFactory.WB_Division_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Division_Text, {
            name: WBSchemas.esWB_Division_Text,
            idAttribute: 'WB_DIVISIONT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Division_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Division_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Division_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Division_Text),
            endpoint: this.sRESTBaseURL + '/api/WB_DivisionTs',
            recordClass: WBSchemaRows.WB_DivisionTextRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Division_Text,
            relations: WBSchemas_DataFactory.WB_Division_Text_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_DistribChannel, {
            name: WBSchemas.esWB_DistribChannel,
            idAttribute: 'WB_DISTRIBCHANNEL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_DistribChannel),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_DistribChannel),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_DistribChannel),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_DistribChannel),
            endpoint: this.sRESTBaseURL + '/api/WB_DistribChannels',
            recordClass: WBSchemaRows.WB_DistribChannelRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_DistribChannel,
            relations: WBSchemas_DataFactory.WB_DistribChannel_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_DistribChannel_Text, {
            name: WBSchemas.esWB_DistribChannel_Text,
            idAttribute: 'WB_DISTRIBCHANNELT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_DistribChannel_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_DistribChannel_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_DistribChannel_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_DistribChannel_Text),
            endpoint: this.sRESTBaseURL + '/api/WB_DistribChannelTs',
            recordClass: WBSchemaRows.WB_DistribChannelTextRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_DistribChannel_Text,
            relations: WBSchemas_DataFactory.WB_DistribChannel_Text_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Company, {
            name: WBSchemas.esWB_Company,
            idAttribute: 'WB_COMPANY_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Company),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Company),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Company),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Company),
            endpoint: this.sRESTBaseURL + '/api/WB_Companies',
            recordClass: WBSchemaRows.WB_CompanyRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Company,
            relations: WBSchemas_DataFactory.WB_Company_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Company_Text, {
            name: WBSchemas.esWB_Company_Text,
            idAttribute: 'WB_COMPANYT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Company_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Company_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Company_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Company_Text),
            endpoint: this.sRESTBaseURL + '/api/WB_CompanyTs',
            recordClass: WBSchemaRows.WB_CompanyTextRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Company_Text,
            relations: WBSchemas_DataFactory.WB_Company_Text_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_SalesOrg, {
            name: WBSchemas.esWB_SalesOrg,
            idAttribute: 'WB_SALESORG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_SalesOrg),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_SalesOrg),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_SalesOrg),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_SalesOrg),
            endpoint: this.sRESTBaseURL + '/api/WB_SalesOrgs',
            recordClass: WBSchemaRows.WB_SalesOrgRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_SalesOrg,
            relations: WBSchemas_DataFactory.WB_SalesOrg_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_SalesOrg_Text, {
            name: WBSchemas.esWB_SalesOrg_Text,
            idAttribute: 'WB_SALESORGT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_SalesOrg_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_SalesOrg_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_SalesOrg_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_SalesOrg_Text),
            endpoint: this.sRESTBaseURL + '/api/WB_SalesOrgTs',
            recordClass: WBSchemaRows.WB_SalesOrgTextRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_SalesOrg_Text,
            relations: WBSchemas_DataFactory.WB_SalesOrg_Text_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_SalesArea, {
            name: WBSchemas.esWB_SalesArea,
            idAttribute: 'WB_SALESAREA_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_SalesArea),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_SalesArea),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_SalesArea),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_SalesArea),
            endpoint: this.sRESTBaseURL + '/api/WB_SalesAreas',
            recordClass: WBSchemaRows.WB_SalesAreaRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_SalesArea,
            relations: WBSchemas_DataFactory.WB_SalesArea_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_SalesArea_Text, {
            name: WBSchemas.esWB_SalesArea_Text,
            idAttribute: 'WB_SALESAREAT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_SalesArea_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_SalesArea_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_SalesArea_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_SalesArea_Text),
            endpoint: this.sRESTBaseURL + '/api/WB_SalesAreaTs',
            recordClass: WBSchemaRows.WB_SalesAreaTextRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_SalesArea_Text,
            relations: WBSchemas_DataFactory.WB_SalesArea_Text_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Approval">
        // Approval
        this.defineMapper(WBSchemas.esWB_Approval, {
            name: WBSchemas.esWB_Approval,
            idAttribute: 'WB_APPROVAL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Approval),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Approval),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Approval),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Approval),
            endpoint: this.sRESTBaseURL + '/api/WB_Approvals',
            recordClass: WBSchemaRows.WB_ApprovalRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Approval,
            relations: WBSchemas_Other.WB_Approval_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Customer">
        // Customer
        this.defineMapper(WBSchemas.esWB_Customer, {
            name: WBSchemas.esWB_Customer,
            idAttribute: 'WB_CUST_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Customer),
            endpoint: this.sRESTBaseURL + '/api/WB_Custs',
            recordClass: WBSchemaRows.WB_CustomerRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer,
            relations: WBSchemas_DataFactory.WB_Customer_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_Attribute, {
            name: WBSchemas.esWB_Customer_Attribute,
            idAttribute: 'WB_CUSTATTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_Attribute),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_Attribute),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_Attribute),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Customer_Attribute),
            endpoint: this.sRESTBaseURL + '/api/WB_CustAttrs',
            recordClass: WBSchemaRows.WB_CustomerAttributeRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_Attribute,
            relations: WBSchemas_DataFactory.WB_Customer_Attribute_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_LogicalKey, {
            name: WBSchemas.esWB_Customer_LogicalKey,
            idAttribute: 'WB_CUSTLKR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_LogicalKey),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_LogicalKey),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_LogicalKey),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Customer_LogicalKey),
            endpoint: this.sRESTBaseURL + '/api/WB_CustLKRs',
            recordClass: WBSchemaRows.WB_CustomerLogicalKeyRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_LogicalKey,
            relations: WBSchemas_DataFactory.WB_Customer_LogicalKey_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_Hierarchy, {
            name: WBSchemas.esWB_Customer_Hierarchy,
            idAttribute: 'WB_CUSTH_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_Hierarchy),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_Hierarchy),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_Hierarchy),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Customer_Hierarchy),
            endpoint: this.sRESTBaseURL + '/api/WB_CustHs',
            recordClass: WBSchemaRows.WB_CustomerHierarchyRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_Hierarchy,
            relations: WBSchemas_DataFactory.WB_Customer_Hierarchy_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_Hierarchy_SalesArea, {
            name: WBSchemas.esWB_Customer_Hierarchy_SalesArea,
            idAttribute: 'WB_CUSTHSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_Hierarchy_SalesArea),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_Hierarchy_SalesArea),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_Hierarchy_SalesArea),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Customer_Hierarchy_SalesArea),
            endpoint: this.sRESTBaseURL + '/api/WB_CustHSRs',
            recordClass: WBSchemaRows.WB_CustomerHierarchySalesAreaRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_Hierarchy_SalesArea,
            relations: WBSchemas_DataFactory.WB_Customer_Hierarchy_SalesArea_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_Hierarchy_Level, {
            name: WBSchemas.esWB_Customer_Hierarchy_Level,
            idAttribute: 'WB_CUSTH_LEVEL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_Hierarchy_Level),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_Hierarchy_Level),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_Hierarchy_Level),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Customer_Hierarchy_Level),
            endpoint: this.sRESTBaseURL + '/api/WB_CustH_Levels',
            recordClass: WBSchemaRows.WB_CustomerHierarchyLevelRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_Hierarchy_Level,
            relations: WBSchemas_DataFactory.WB_Customer_Hierarchy_Level_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_Relationship, {
            name: WBSchemas.esWB_Customer_Relationship,
            idAttribute: 'WB_CUSTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Customer_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_CustRs',
            recordClass: WBSchemaRows.WB_CustomerRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_Relationship,
            relations: WBSchemas_DataFactory.WB_Customer_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_Tag_Relationship, {
            name: WBSchemas.esWB_Customer_Tag_Relationship,
            idAttribute: 'WB_CUST_TAGR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_Tag_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_Tag_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_Tag_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Customer_Tag_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_Cust_TagRs',
            recordClass: WBSchemaRows.WB_CustomerTagRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_Tag_Relationship,
            relations: WBSchemas_DataFactory.WB_Customer_Tag_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_External_Relationship, {
            name: WBSchemas.esWB_Customer_External_Relationship,
            idAttribute: 'WB_EXTCUSTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_External_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_External_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_External_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Customer_External_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_ExtCustRs',
            recordClass: WBSchemaRows.WB_CustomerExternalRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_External_Relationship,
            relations: WBSchemas_DataFactory.WB_Customer_External_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_Proxy_Relationship, {
            name: WBSchemas.esWB_Customer_Proxy_Relationship,
            idAttribute: 'WB_EXTCUSTP_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_Proxy_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_Proxy_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_Proxy_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Customer_Proxy_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_ExtCustPs',
            recordClass: WBSchemaRows.WB_CustomerProxyRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_Proxy_Relationship,
            relations: WBSchemas_DataFactory.WB_Customer_Proxy_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_Bill_To_Relationship, {
            name: WBSchemas.esWB_Customer_Bill_To_Relationship,
            idAttribute: 'WB_CUST_BILL_TO_REL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_Bill_To_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_Bill_To_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_Bill_To_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Customer_Bill_To_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_Cust_Bill_To_Rels',
            recordClass: WBSchemaRows.WB_CustomerBillToRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_Bill_To_Relationship,
            relations: WBSchemas_DataFactory.WB_Customer_Bill_To_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_Pay_To_Relationship, {
            name: WBSchemas.esWB_Customer_Pay_To_Relationship,
            idAttribute: 'WB_CUST_PAY_TO_REL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_Pay_To_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_Pay_To_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_Pay_To_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Customer_Pay_To_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_Cust_Pay_To_Rels',
            recordClass: WBSchemaRows.WB_CustomerPayToRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_Pay_To_Relationship,
            relations: WBSchemas_DataFactory.WB_Customer_Pay_To_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_Calendar, {
            name: WBSchemas.esWB_Customer_Calendar,
            idAttribute: 'WB_CUSTCAL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_Calendar),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_Calendar),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_Calendar),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Customer_Calendar),
            endpoint: this.sRESTBaseURL + '/api/WB_CustCals',
            recordClass: WBSchemaRows.WB_CustomerCalendarRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_Calendar,
            relations: WBSchemas_DataFactory.WB_Customer_Calendar_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_Product, {
            name: WBSchemas.esWB_Customer_Product,
            idAttribute: 'WB_CUSTPROD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_Product),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_Product),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_Product),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Customer_Product),
            endpoint: this.sRESTBaseURL + '/api/WB_CustPrdRs',
            recordClass: WBSchemaRows.WB_CustomerProductRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_Product,
            relations: WBSchemas_DataFactory.WB_Customer_Product_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_Distribution_Relationship, {
            name: WBSchemas.esWB_Customer_Distribution_Relationship,
            idAttribute: 'WB_CUSTRPG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_Distribution_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_Distribution_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_Distribution_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Customer_Distribution_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_CustRPGs',
            recordClass: WBSchemaRows.WB_CustomerDistributionRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_Distribution_Relationship,
            relations: WBSchemas_DataFactory.WB_Customer_Distribution_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_Type, {
            name: WBSchemas.esWB_Customer_Type,
            idAttribute: 'WB_CUSTM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Customer_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_CustMs',
            recordClass: WBSchemaRows.WB_CustomerTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_Type,
            relations: WBSchemas_DataFactory.WB_Customer_Type_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_Role, {
            name: WBSchemas.esWB_Customer_Role,
            idAttribute: 'WB_CUSTROLE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_Role),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_Role),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_Role),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Customer_Role),
            endpoint: this.sRESTBaseURL + '/api/WB_CustRoles',
            recordClass: WBSchemaRows.WB_CustomerRoleRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_Role,
            relations: WBSchemas_DataFactory.WB_Customer_Role_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_SalesArea_Relationship, {
            name: WBSchemas.esWB_Customer_SalesArea_Relationship,
            idAttribute: 'WB_CUSTSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_SalesArea_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_SalesArea_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_SalesArea_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Customer_SalesArea_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_CustSRs',
            recordClass: WBSchemaRows.WB_CustomerSalesAreaRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_SalesArea,
            relations: WBSchemas_DataFactory.WB_Customer_SalesArea_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Product_Group_LogicalKey, {
            name: WBSchemas.esWB_Product_Group_LogicalKey,
            idAttribute: 'WB_PRDGRPLKR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Group_LogicalKey),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Group_LogicalKey),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Group_LogicalKey),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_Group_LogicalKey),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdGrpLKRs',
            recordClass: WBSchemaRows.WB_ProductGroupLogicalKeyRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Group_LogicalKey,
            relations: WBSchemas_DataFactory.WB_Product_Group_LogicalKey_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_Role_Relationship, {
            name: WBSchemas.esWB_Customer_Role_Relationship,
            idAttribute: 'WB_CUSTRSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_Role_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_Role_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_Role_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Customer_Role_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_CustRSRs',
            recordClass: WBSchemaRows.WB_CustomerRoleRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_Role_Relationship,
            relations: WBSchemas_DataFactory.WB_Customer_Role_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_Role_Customer_Relationship, {
            name: WBSchemas.esWB_Customer_Role_Customer_Relationship,
            idAttribute: 'WB_CUSTRSCR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_Role_Customer_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_Role_Customer_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_Role_Customer_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Customer_Role_Customer_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_CustRSCRs',
            recordClass: WBSchemaRows.WB_CustomerRoleCustomerRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_Role_Customer_Relationship,
            relations: WBSchemas_DataFactory.WB_Customer_Role_Customer_Relationship_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Product BOMS ">

        this.defineMapper(WBSchemas.esWB_Product_BOM, {
            name: WBSchemas.esWB_Product_BOM,
            idAttribute: 'WB_PROD_BOM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_BOM),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_BOM),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_BOM),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_BOM),
            endpoint: this.sRESTBaseURL + '/api/WB_ProdBOMs',
            recordClass: WBSchemaRows.WB_ProductBOMRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_BOM,
            relations: WBSchemas_DataFactory.WB_Product_BOM_JSData_RELS,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Product_BOM_Component, {
            name: WBSchemas.esWB_Product_BOM_Component,
            idAttribute: 'WB_PROD_BOM_COMP_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_BOM_Component),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_BOM_Component),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_BOM_Component),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_BOM_Component),
            endpoint: this.sRESTBaseURL + '/api/WB_ProdBOM_Comps',
            recordClass: WBSchemaRows.WB_ProductBOMComponentRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_BOM_Component,
            relations: WBSchemas_DataFactory.WB_Product_BOM_Component_JSData_RELS,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Product UOMS ">

        this.defineMapper(WBSchemas.esWB_Product_UOM, {
            name: WBSchemas.esWB_Product_UOM,
            idAttribute: 'WB_PROD_UOM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_UOM),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_UOM),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_UOM),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_UOM),
            endpoint: this.sRESTBaseURL + '/api/WB_ProdUOMs',
            recordClass: WBSchemaRows.WB_ProductUOMRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_UOM,
            relations: WBSchemas_DataFactory.WB_Product_UOM_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Product_Group_UOM, {
            name: WBSchemas.esWB_Product_Group_UOM,
            idAttribute: 'WB_PRDGRP_UOM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Group_UOM),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Group_UOM),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Group_UOM),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_Group_UOM),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdGrp_UOMs',
            recordClass: WBSchemaRows.WB_ProductGroupUOMRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Group_UOM,
            relations: WBSchemas_DataFactory.WB_Product_Group_UOM_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_UOM, {
            name: WBSchemas.esWB_UOM,
            idAttribute: 'WB_UOM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_UOM),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_UOM),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_UOM),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_UOM),
            endpoint: this.sRESTBaseURL + '/api/WB_UOMs',
            recordClass: WBSchemaRows.WB_UOMRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_UOM,
            relations: WBSchemas_DataFactory.WB_UOM_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Finished Product ">
        // Finished Product

        this.defineMapper(WBSchemas.esWB_Finished_Product, {
            name: WBSchemas.esWB_Finished_Product,
            idAttribute: 'WB_PROD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Finished_Product),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Finished_Product),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Finished_Product),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Finished_Product),
            endpoint: this.sRESTBaseURL + '/api/WB_Prods',
            recordClass: WBSchemaRows.WB_FinishedProductRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Finished_Product,
            relations: WBSchemas_DataFactory.WB_Finished_Product_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Finished_Product_SalesArea, {
            name: WBSchemas.esWB_Finished_Product_SalesArea,
            idAttribute: 'WB_PRODSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Finished_Product_SalesArea),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Finished_Product_SalesArea),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Finished_Product_SalesArea),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Finished_Product_SalesArea),
            endpoint: this.sRESTBaseURL + '/api/WB_ProdSRs',
            recordClass: WBSchemaRows.WB_FinishedProductSalesAreaRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Finished_Product_SalesArea,
            relations: WBSchemas_DataFactory.WB_Finished_Product_SalesArea_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Product_Attribute, {
            name: WBSchemas.esWB_Product_Attribute,
            idAttribute: 'WB_PRDATTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Attribute),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Attribute),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Attribute),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_Attribute),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdAttrs',
            recordClass: WBSchemaRows.WB_ProductAttributeRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Attribute,
            relations: WBSchemas_DataFactory.WB_Product_Attribute_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Finished_Product_Relationship, {
            name: WBSchemas.esWB_Finished_Product_Relationship,
            idAttribute: 'WB_PRODR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Finished_Product_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Finished_Product_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Finished_Product_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Finished_Product_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_ProdRs',
            recordClass: WBSchemaRows.WB_FinishedProductRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Finished_Product_Relationship,
            relations: WBSchemas_DataFactory.WB_Finished_Product_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Product_Type, {
            name: WBSchemas.esWB_Product_Type,
            idAttribute: 'WB_PRDM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdMs',
            recordClass: WBSchemaRows.WB_ProductTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Type,
            relations: WBSchemas_DataFactory.WB_Product_Type_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Finished_Product_Tag_Relationship, {
            name: WBSchemas.esWB_Finished_Product_Tag_Relationship,
            idAttribute: 'WB_PROD_TAGR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Finished_Product_Tag_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Finished_Product_Tag_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Finished_Product_Tag_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Finished_Product_Tag_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Prod_TagRs',
            recordClass: WBSchemaRows.WB_FinishedProductTagRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Finished_Product_Tag_Relationship,
            relations: WBSchemas_DataFactory.WB_Finished_Product_Tag_Relationship_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Product Group ">
        // Product Group
        this.defineMapper(WBSchemas.esWB_Product_Group, {
            name: WBSchemas.esWB_Product_Group,
            idAttribute: 'WB_PRDGRP_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Group),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Group),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Group),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_Group),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdGrps',
            recordClass: WBSchemaRows.WB_ProductGroupRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Group,
            relations: WBSchemas_DataFactory.WB_Product_Group_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Product_Group_SalesArea, {
            name: WBSchemas.esWB_Product_Group_SalesArea,
            idAttribute: 'WB_PRDGRPSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Group_SalesArea),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Group_SalesArea),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Group_SalesArea),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_Group_SalesArea),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdGrpSRs',
            recordClass: WBSchemaRows.WB_ProductGroupSalesAreaRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Group_SalesArea,
            relations: WBSchemas_DataFactory.WB_Product_Group_SalesArea_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Product_Group_Relationship, {
            name: WBSchemas.esWB_Product_Group_Relationship,
            idAttribute: 'WB_PRDGRPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Group_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Group_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Group_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_Group_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdGrpRs',
            recordClass: WBSchemaRows.WB_ProductGroupRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Group_Relationship,
            relations: WBSchemas_DataFactory.WB_Product_Group_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Product_Group_Tag_Relationship, {
            name: WBSchemas.esWB_Product_Group_Tag_Relationship,
            idAttribute: 'WB_PRDGRP_TAGR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Group_Tag_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Group_Tag_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Group_Tag_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Product_Group_Tag_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdGrp_TagRs',
            recordClass: WBSchemaRows.WB_ProductGroupTagRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Group_Tag_Relationship,
            relations: WBSchemas_DataFactory.WB_Product_Group_Tag_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Product_Group_Attribute, {
            name: WBSchemas.esWB_Product_Group_Attribute,
            idAttribute: 'WB_PRDGRPAR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Group_Attribute),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Group_Attribute),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Group_Attribute),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_Group_Attribute),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdGrpARs',
            recordClass: WBSchemaRows.WB_ProductGroupAttributeRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Group_Attribute,
            relations: WBSchemas_DataFactory.WB_Product_Group_Attribute_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Product_Group_External_Relationship, {
            name: WBSchemas.esWB_Product_Group_External_Relationship,
            idAttribute: 'WB_PRDGRPEXTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Group_External_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Group_External_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Group_External_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Product_Group_External_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdGrp_ExtRs',
            recordClass: WBSchemaRows.WB_ProductGroupExternalRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Group_External_Relationship,
            relations: WBSchemas_DataFactory.WB_Product_Group_External_Relationship_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Product Hierarchy ">
        // Product Hierarchy
        this.defineMapper(WBSchemas.esWB_Product_Hierarchy, {
            name: WBSchemas.esWB_Product_Hierarchy,
            idAttribute: 'WB_PRDH_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Hierarchy),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Hierarchy),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Hierarchy),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_Hierarchy),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdHs',
            recordClass: WBSchemaRows.WB_ProductHierarchyRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Hierarchy,
            relations: WBSchemas_DataFactory.WB_Product_Hierarchy_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Product_Hierarchy_SalesArea, {
            name: WBSchemas.esWB_Product_Hierarchy_SalesArea,
            idAttribute: 'WB_PRDHSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Hierarchy_SalesArea),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Hierarchy_SalesArea),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Hierarchy_SalesArea),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_Hierarchy_SalesArea),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdHSRs',
            recordClass: WBSchemaRows.WB_ProductHierarchySalesAreaRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Hierarchy_SalesArea,
            relations: WBSchemas_DataFactory.WB_Product_Hierarchy_SalesArea_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Product_Hierarchy_Level, {
            name: WBSchemas.esWB_Product_Hierarchy_Level,
            idAttribute: 'WB_PRDH_LEVEL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Hierarchy_Level),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Hierarchy_Level),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Hierarchy_Level),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_Hierarchy_Level),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdH_Levels',
            recordClass: WBSchemaRows.WB_ProductHierarchyLevelRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Hierarchy_Level,
            relations: WBSchemas_DataFactory.WB_Product_Hierarchy_Level_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold desc="Define Mappers - Consumer Product Alignment">
        // Consumer Product

        this.defineMapper(WBSchemas.esWB_Consumer_Product_Alignment, {
            name: WBSchemas.esWB_Consumer_Product_Alignment,
            idAttribute: 'WB_UPCPRDR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Consumer_Product_Alignment),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Consumer_Product_Alignment),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Consumer_Product_Alignment),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Consumer_Product_Alignment),
            endpoint: this.sRESTBaseURL + '/api/WB_UpcPrdRs',
            recordClass: WBSchemaRows.WB_ConsumerProductAlignmentRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Consumer_Product_Alignment,
            relations: WBSchemas_DataFactory.WB_Consumer_Product_Alignment_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Consumer_Product_Customer_Alignment, {
            name: WBSchemas.esWB_Consumer_Product_Customer_Alignment,
            idAttribute: 'WB_UPCPRDPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Consumer_Product_Customer_Alignment),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Consumer_Product_Customer_Alignment),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Consumer_Product_Customer_Alignment),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Consumer_Product_Customer_Alignment
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_UpcPrdPRs',
            recordClass: WBSchemaRows.WB_ConsumerProductCustomerAlignmentRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Consumer_Product_Customer_Alignment,
            relations: WBSchemas_DataFactory.WB_Consumer_Product_Customer_Alignment_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Attribute Group ">

        this.defineMapper(WBSchemas.esWB_Product_Attribute_Group, {
            name: WBSchemas.esWB_Product_Attribute_Group,
            idAttribute: 'WB_PRDATTRGRP_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Attribute_Group),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Attribute_Group),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Attribute_Group),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_Attribute_Group),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdAttrGrps',
            recordClass: WBSchemaRows.WB_ProductAttributeGroupRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Attribute_Group,
            relations: WBSchemas_DataFactory.WB_Product_Attribute_Group_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Product_Attribute_Group_Relationship, {
            name: WBSchemas.esWB_Product_Attribute_Group_Relationship,
            idAttribute: 'WB_PRDATTRGRPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Attribute_Group_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Attribute_Group_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Attribute_Group_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Product_Attribute_Group_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdAttrGrpRs',
            recordClass: WBSchemaRows.WB_ProductAttributeGroupRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Attribute_Group_Relationship,
            relations: WBSchemas_DataFactory.WB_Product_Attribute_Group_Relationship_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Attribute Map ">

        this.defineMapper(WBSchemas.esWB_Product_Attribute_Map, {
            name: WBSchemas.esWB_Product_Attribute_Map,
            idAttribute: 'WB_PRDATTRMAP_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Attribute_Map),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Attribute_Map),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Attribute_Map),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_Attribute_Map),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdAttrMaps',
            recordClass: WBSchemaRows.WB_ProductAttributeMapRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Attribute_Map,
            relations: WBSchemas_DataFactory.WB_Product_Attribute_Map_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Product_Attribute_Map_External_Relationship, {
            name: WBSchemas.esWB_Product_Attribute_Map_External_Relationship,
            idAttribute: 'WB_PRDATTRMAPEXTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Attribute_Map_External_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Attribute_Map_External_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Attribute_Map_External_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Product_Attribute_Map_External_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdAttrMap_ExtRs',
            recordClass: WBSchemaRows.WB_ProductAttributeMapExternalRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Attribute_Map_External_Relationship,
            relations: WBSchemas_DataFactory.WB_Product_Attribute_Map_External_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Product_Attribute_Map_Value_Relationship, {
            name: WBSchemas.esWB_Product_Attribute_Map_Value_Relationship,
            idAttribute: 'WB_PRDATTRMAPVALUER_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Attribute_Map_Value_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Attribute_Map_Value_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Attribute_Map_Value_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Product_Attribute_Map_Value_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdAttrMap_ValueRs',
            recordClass: WBSchemaRows.WB_ProductAttributeMapValueRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Attribute_Map_Value_Relationship,
            relations: WBSchemas_DataFactory.WB_Product_Attribute_Map_Value_Relationship_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Period ">
        // Period / Period Type
        this.defineMapper(WBSchemas.esWB_Period_Type, {
            name: WBSchemas.esWB_Period_Type,
            idAttribute: 'WB_PERIODM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Period_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Period_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Period_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Period_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_PeriodMs',
            recordClass: WBSchemaRows.WB_PeriodTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Period_Type,
            relations: WBSchemas_DataFactory.WB_Period_Type_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Period, {
            name: WBSchemas.esWB_Period,
            idAttribute: 'WB_PERIOD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Period),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Period),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Period),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Period),
            endpoint: this.sRESTBaseURL + '/api/WB_Periods',
            recordClass: WBSchemaRows.WB_PeriodRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Period,
            relations: WBSchemas_DataFactory.WB_Period_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Period_Attribute, {
            name: WBSchemas.esWB_Period_Attribute,
            idAttribute: 'WB_PERIODATTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Period_Attribute),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Period_Attribute),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Period_Attribute),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Period_Attribute),
            endpoint: this.sRESTBaseURL + '/api/WB_PeriodAttrs',
            recordClass: WBSchemaRows.WB_PeriodAttributeRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Period_Attribute,
            relations: WBSchemas_DataFactory.WB_Period_Attribute_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Period_Relationship, {
            name: WBSchemas.esWB_Period_Relationship,
            idAttribute: 'WB_PERIODR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Period_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Period_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Period_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Period_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_PeriodRs',
            recordClass: WBSchemaRows.WB_PeriodRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Period_Relationship,
            relations: WBSchemas_DataFactory.WB_Period_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Period_Tag_Relationship, {
            name: WBSchemas.esWB_Period_Tag_Relationship,
            idAttribute: 'WB_PERIOD_TAGR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Period_Tag_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Period_Tag_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Period_Tag_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Period_Tag_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_Period_TagRs',
            recordClass: WBSchemaRows.WB_PeriodTagRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Period_Tag_Relationship,
            relations: WBSchemas_DataFactory.WB_Period_Tag_Relationship_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Period Hiearchy">
        // SnapshotMsar
        this.defineMapper(WBSchemas.esWB_Period_Hierarchy, {
            name: WBSchemas.esWB_Period_Hierarchy,
            idAttribute: 'WB_PERIODH_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Period_Hierarchy),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Period_Hierarchy),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Period_Hierarchy),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Period_Hierarchy),
            endpoint: this.sRESTBaseURL + '/api/WB_Periodhs',
            recordClass: WBSchemaRows.WB_PeriodHierarchyRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Period_Hierarchy,
            relations: WBSchemas_DataFactory.WB_Period_Hierarchy_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Period_Hierarchy_Level, {
            name: WBSchemas.esWB_Period_Hierarchy_Level,
            idAttribute: 'WB_PERIODH_LEVEL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Period_Hierarchy_Level),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Period_Hierarchy_Level),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Period_Hierarchy_Level),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Period_Hierarchy_Level),
            endpoint: this.sRESTBaseURL + '/api/WB_PeriodH_Levels',
            recordClass: WBSchemaRows.WB_PeriodHierarchyLevelRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Period_Hierarchy_Level,
            relations: WBSchemas_DataFactory.WB_Period_Hierarchy_Level_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        this.defineMapper(WBSchemas.esWB_Period_Hierarchy_Company_Relationship, {
            name: WBSchemas.esWB_Period_Hierarchy_Company_Relationship,
            idAttribute: 'WB_PERIODHCR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Period_Hierarchy_Company_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Period_Hierarchy_Company_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Period_Hierarchy_Company_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Period_Hierarchy_Company_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_PeriodHCRs',
            recordClass: WBSchemaRows.WB_PeriodHierarchyCompanyRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Period_Hierarchy_Company_Relationship,
            relations: WBSchemas_DataFactory.WB_Period_Hierarchy_Company_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Period_Company_Relationship, {
            name: WBSchemas.esWB_Period_Company_Relationship,
            idAttribute: 'WB_PERIODCR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Period_Company_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Period_Company_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Period_Company_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Period_Company_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_PeriodCRs',
            recordClass: WBSchemaRows.WB_PeriodCompanyRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Period_Company_Relationship,
            relations: WBSchemas_DataFactory.WB_Period_Company_Relationship_JSData_RELs,
            debug: bDebug
        })

        // <editor-fold desc="Define Mappers - Competitive Product Group - External Product Attributes - Empty">
        // Competitive Product Group - External Product Attributes

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Domain">
        // Domains
        this.defineMapper(WBSchemas.esWB_Domain, {
            name: WBSchemas.esWB_Domain,
            idAttribute: 'WB_DOM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Domain),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Domain),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Domain),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Domain),
            endpoint: this.sRESTBaseURL + '/api/WB_Doms',
            recordClass: WBSchemaRows.WB_DomainRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Domain,
            relations: WBSchemas_Other.WB_Domain_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Domain_Text, {
            name: WBSchemas.esWB_Domain_Text,
            idAttribute: 'WB_DOMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Domain_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Domain_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Domain_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Domain_Text),
            endpoint: this.sRESTBaseURL + '/api/WB_DomTs',
            recordClass: WBSchemaRows.WB_DomainTextRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Domain_Text,
            relations: WBSchemas_Other.WB_Domain_Text_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Domain_Item, {
            name: WBSchemas.esWB_Domain_Item,
            idAttribute: 'WB_DOMITEM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Domain_Item),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Domain_Item),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Domain_Item),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Domain_Item),
            endpoint: this.sRESTBaseURL + '/api/WB_DomItems',
            recordClass: WBSchemaRows.WB_DomainItemRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Domain_Item,
            relations: WBSchemas_Other.WB_Domain_Item_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Domain_Item_Text, {
            name: WBSchemas.esWB_Domain_Item_Text,
            idAttribute: 'WB_DOMITEMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Domain_Item_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Domain_Item_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Domain_Item_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Domain_Item_Text),
            endpoint: this.sRESTBaseURL + '/api/WB_DomItemTs',
            recordClass: WBSchemaRows.WB_DomainItemTextRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Domain_Item_Text,
            relations: WBSchemas_Other.WB_Domain_Item_Text_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Product Condition ">
        // Product Condition
        this.defineMapper(WBSchemas.esWB_Product_Condition, {
            name: WBSchemas.esWB_Product_Condition,
            idAttribute: 'PROD_CND_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Condition),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Condition),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Condition),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_Condition),
            recModifiedDtTsFieldName: 'BUSINESS_MODIFIED',
            endpoint: this.sRESTBaseURL + '/api/Prod_Cnds',
            recordClass: WBSchemaRows.WB_ProductConditionRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Product_Condition,
            relations: WBSchemas_Other.WB_Product_Condition_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold desc="Define Mappers - Product Condition Type">
        // Product Condition Type
        this.defineMapper(WBSchemas.esWB_Product_Condition_Type, {
            name: WBSchemas.esWB_Product_Condition_Type,
            idAttribute: 'PRD_CNDM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Condition_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Condition_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Condition_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_Condition_Type),
            endpoint: this.sRESTBaseURL + '/api/Prod_CndMs',
            recordClass: WBSchemaRows.WB_ProductConditionTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Product_Condition_Type,
            relations: WBSchemas_Other.WB_Product_Condition_Type_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Consumption Adjustments PG/ Prd">
        //
        // Consumption Overrides/ Adjustments
        this.defineMapper(WBSchemas.esWB_Consumption_Product_Group, {
            name: WBSchemas.esWB_Consumption_Product_Group,
            idAttribute: 'WB_CNSPG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Consumption_Product_Group),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Consumption_Product_Group),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Consumption_Product_Group),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Consumption_Product_Group),
            endpoint: this.sRESTBaseURL + '/api/WB_CnsPGs',
            recordClass: WBSchemaRows.WB_ConsumptionProductGroupRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Consumption_Product_Group,
            relations: WBSchemas_DataFactory.WB_Consumption_Product_Group_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Consumption_Product, {
            name: WBSchemas.esWB_Consumption_Product,
            idAttribute: 'WB_CNSPRD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Consumption_Product),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Consumption_Product),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Consumption_Product),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Consumption_Product),
            endpoint: this.sRESTBaseURL + '/api/WB_CnsPrds',
            recordClass: WBSchemaRows.WB_ConsumptionProductRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Consumption_Product,
            relations: WBSchemas_DataFactory.WB_Consumption_Product_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Spend Allocation Rules">
        //
        // Spend Allocation Rules
        this.defineMapper(WBSchemas.esWB_Spend_Allocation_Rule, {
            name: WBSchemas.esWB_Spend_Allocation_Rule,
            idAttribute: 'WB_SPENDRL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Spend_Allocation_Rule),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Spend_Allocation_Rule),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Spend_Allocation_Rule),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Spend_Allocation_Rule),
            endpoint: this.sRESTBaseURL + '/api/WB_SpendRls',
            recordClass: WBSchemaRows.WB_SpendAllocationRuleRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Spend_Allocation_Rule,
            relations: WBSchemas_Other.WB_Spend_Allocation_Rule_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Spend_Allocation_Rule_Item, {
            name: WBSchemas.esWB_Spend_Allocation_Rule_Item,
            idAttribute: 'WB_SPENDRLR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Spend_Allocation_Rule_Item),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Spend_Allocation_Rule_Item),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Spend_Allocation_Rule_Item),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Spend_Allocation_Rule_Item),
            endpoint: this.sRESTBaseURL + '/api/WB_SpendRlRs',
            recordClass: WBSchemaRows.WB_SpendAllocationRuleItemRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Spend_Allocation_Rule_Item,
            relations: WBSchemas_Other.WB_Spend_Allocation_Rule_Item_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Spend Method Rules">
        //
        // Spend Method Rules
        this.defineMapper(WBSchemas.esWB_Spend_Method_Rule, {
            name: WBSchemas.esWB_Spend_Method_Rule,
            idAttribute: 'WB_SPENDCLR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Spend_Method_Rule),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Spend_Method_Rule),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Spend_Method_Rule),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Spend_Method_Rule),
            endpoint: this.sRESTBaseURL + '/api/WB_SpendClRs',
            recordClass: WBSchemaRows.WB_SpendMethodRuleRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Spend_Method_Rule,
            relations: WBSchemas_Other.WB_Spend_Method_Rule_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold desc="Define Mappers - Meta">
        //
        // Meta
        this.defineMapper(WBSchemas.esWB_Meta, {
            name: WBSchemas.esWB_Meta,
            idAttribute: 'WB_META_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Meta),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Meta),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Meta),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Meta),
            endpoint: this.sRESTBaseURL + '/api/WB_Meta',
            recordClass: WBSchemaRows.WB_MetaRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Meta,
            relations: WBSchemas_Other.WB_Meta_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Meta_Text, {
            name: WBSchemas.esWB_Meta_Text,
            idAttribute: 'WB_METAT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Meta_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Meta_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Meta_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Meta_Text),
            endpoint: this.sRESTBaseURL + '/api/WB_MetaTs',
            recordClass: WBSchemaRows.WB_MetaTextRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Meta_Text,
            relations: WBSchemas_Other.WB_Meta_Text_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Meta_Tags, {
            name: WBSchemas.esWB_Meta_Tags,
            idAttribute: 'WB_TAGMETAR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Meta_Tags),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Meta_Tags),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Meta_Tags),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Meta_Tags),
            endpoint: this.sRESTBaseURL + '/api/WB_TagMetaRs',
            recordClass: WBSchemaRows.WB_MetaTagRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Meta_Tags,
            relations: WBSchemas_Other.WB_Meta_Tags_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Tags">
        // Tags
        this.defineMapper(WBSchemas.esWB_Tag, {
            name: WBSchemas.esWB_Tag,
            idAttribute: 'WB_TAG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Tag),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Tag),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Tag),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Tag),
            endpoint: this.sRESTBaseURL + '/api/WB_Tags',
            recordClass: WBSchemaRows.WB_TagRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Tag,
            relations: WBSchemas_Other.WB_Tag_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Tag_Source, {
            name: WBSchemas.esWB_Tag_Source,
            idAttribute: 'WB_TAG_SOURCE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Tag_Source),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Tag_Source),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Tag_Source),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Tag_Source),
            endpoint: this.sRESTBaseURL + '/api/WB_Tag_Sources',
            recordClass: WBSchemaRows.WB_TagSourceRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Tag_Source,
            relations: WBSchemas_Other.WB_Tag_Source_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Tag_Function, {
            name: WBSchemas.esWB_Tag_Function,
            idAttribute: 'WB_TAG_FUNCTION_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Tag_Function),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Tag_Function),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Tag_Function),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Tag_Function),
            endpoint: this.sRESTBaseURL + '/api/WB_Tag_Functions',
            recordClass: WBSchemaRows.WB_TagFunctionRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Tag_Function,
            relations: WBSchemas_Other.WB_Tag_Function_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Tag_Function_Param, {
            name: WBSchemas.esWB_Tag_Function_Param,
            idAttribute: 'WB_TAG_FUNCTION_PARAM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Tag_Function_Param),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Tag_Function_Param),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Tag_Function_Param),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Tag_Function_Param),
            endpoint: this.sRESTBaseURL + '/api/WB_Tag_Function_Params',
            recordClass: WBSchemaRows.WB_TagFunctionParamRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Tag_Function_Param,
            relations: WBSchemas_Other.WB_Tag_Function_Param_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Tag_Relationship, {
            name: WBSchemas.esWB_Tag_Relationship,
            idAttribute: 'WB_TAGR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Tag_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Tag_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Tag_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Tag_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_TagRs',
            recordClass: WBSchemaRows.WB_TagRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Tag_Relationship,
            relations: WBSchemas_Other.WB_Tag_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Tag_Text, {
            name: WBSchemas.esWB_Tag_Text,
            idAttribute: 'WB_TAGT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Tag_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Tag_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Tag_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Tag_Text),
            endpoint: this.sRESTBaseURL + '/api/WB_TagTs',
            recordClass: WBSchemaRows.WB_TagTextRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Tag_Text,
            relations: WBSchemas_Other.WB_Tag_Text_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Tag_Metric, {
            name: WBSchemas.esWB_Tag_Metric,
            idAttribute: 'WB_TAGMTRCR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Tag_Metric),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Tag_Metric),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Tag_Metric),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Tag_Metric),
            endpoint: this.sRESTBaseURL + '/api/WB_TagMtrcRs',
            recordClass: WBSchemaRows.WB_TagMetricRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Tag_Metric,
            relations: WBSchemas_Other.WB_Tag_Metric_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Tag_Rule, {
            name: WBSchemas.esWB_Tag_Rule,
            idAttribute: 'WB_TAG_RULE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Tag_Rule),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Tag_Rule),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Tag_Rule),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Tag_Rule),
            endpoint: this.sRESTBaseURL + '/api/WB_Tag_Rules',
            recordClass: WBSchemaRows.WB_TagRuleRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Tag_Rule,
            relations: WBSchemas_Other.WB_Tag_Rule_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Tag_Rule_Param, {
            name: WBSchemas.esWB_Tag_Rule_Param,
            idAttribute: 'WB_TAG_RULE_PARAM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Tag_Rule_Param),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Tag_Rule_Param),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Tag_Rule_Param),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Tag_Rule_Param),
            endpoint: this.sRESTBaseURL + '/api/WB_Tag_Rule_Params',
            recordClass: WBSchemaRows.WB_TagRuleParamRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Tag_Rule_Param,
            relations: WBSchemas_Other.WB_Tag_Rule_Param_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Metrics">
        // TPM Metrics
        this.defineMapper(WBSchemas.esWB_Metric, {
            name: WBSchemas.esWB_Metric,
            idAttribute: 'WB_METRIC_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Metric),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Metric),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Metric),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Metric),
            endpoint: this.sRESTBaseURL + '/api/WB_Metrics',
            recordClass: WBSchemaRows.WB_MetricRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Metric,
            relations: WBSchemas_Other.WB_Metric_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold desc="Define Mappers - MetricsR">

        this.defineMapper(WBSchemas.esWB_MetricDefinition_Component, {
            name: WBSchemas.esWB_MetricDefinition_Component,
            idAttribute: 'WB_MTRDFNR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_MetricDefinition_Component),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_MetricDefinition_Component),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_MetricDefinition_Component),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_MetricDefinition_Component),
            endpoint: this.sRESTBaseURL + '/api/WB_MtrDfnRs',
            recordClass: WBSchemaRows.WB_MetricDefinitionComponentRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Metric_Definition_Component,
            relations: WBSchemas_Other.WB_MetricR_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold desc="Define Mappers - Plan Group">
        // TPM Plan Group
        this.defineMapper(WBSchemas.esWB_Plan_Group, {
            name: WBSchemas.esWB_Plan_Group,
            idAttribute: 'WB_PLANGRP_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_Group),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_Group),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_Group),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_Group),
            endpoint: this.sRESTBaseURL + '/api/WB_PlanGrps',
            recordClass: WBSchemaRows.WB_PlanGroupRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_Group,
            relations: WBSchemas_Other.WB_Plan_Group_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold desc="Define Mappers - TPM Plan">
        // TPM Plan
        this.defineMapper(WBSchemas.esWB_Plan, {
            name: WBSchemas.esWB_Plan,
            idAttribute: 'WB_PLAN_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan),
            endpoint: this.sRESTBaseURL + '/api/WB_Plans',
            recordClass: WBSchemaRows.WB_PlanRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan,
            relations: WBSchemas_Other.WB_Plan_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Plan_Product_Group, {
            name: WBSchemas.esWB_Plan_Product_Group,
            idAttribute: 'WB_PLANPG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_Product_Group),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_Product_Group),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_Product_Group),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_Product_Group),
            endpoint: this.sRESTBaseURL + '/api/WB_PlanPGs',
            recordClass: WBSchemaRows.WB_PlanProductGroupRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_Product_Group,
            relations: WBSchemas_Other.WB_Plan_Product_Group_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Plan_Product, {
            name: WBSchemas.esWB_Plan_Product,
            idAttribute: 'WB_PLANPRD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_Product),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_Product),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_Product),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_Product),
            endpoint: this.sRESTBaseURL + '/api/WB_PlanPrds',
            recordClass: WBSchemaRows.WB_PlanProductRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_Product,
            relations: WBSchemas_Other.WB_Plan_Product_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Plan_Override, {
            name: WBSchemas.esWB_Plan_Override,
            idAttribute: 'WB_PLAN_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_Override),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_Override),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_Override),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_Override),
            endpoint: this.sRESTBaseURL + '/api/WB_PlanOs',
            recordClass: WBSchemaRows.WB_PlanOverrideRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_Override,
            relations: WBSchemas_Other.WB_Plan_Override_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Plan_Volume_Metrics, {
            name: WBSchemas.esWB_Plan_Volume_Metrics,
            idAttribute: 'WB_PLANVMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_Volume_Metrics),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_Volume_Metrics),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_Volume_Metrics),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_Volume_Metrics),
            endpoint: this.sRESTBaseURL + '/api/WB_PlanVMts',
            recordClass: WBSchemaRows.WB_PlanVolumeMetricsRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_Volume_Metrics,
            relations: WBSchemas_Other.WB_Plan_Volume_Metrics_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Plan_Redistrib_Volume_Metrics, {
            name: WBSchemas.esWB_Plan_Redistrib_Volume_Metrics,
            idAttribute: 'WB_PLANRVMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_Redistrib_Volume_Metrics),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_Redistrib_Volume_Metrics),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_Redistrib_Volume_Metrics),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_Redistrib_Volume_Metrics),
            endpoint: this.sRESTBaseURL + '/api/WB_PlanRVMts',
            recordClass: WBSchemaRows.WB_PlanRedistributedVolumeMetricsRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_Redistrib_Volume_Metrics,
            relations: WBSchemas_Other.WB_Plan_Redistrib_Volume_Metrics_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Plan_Volume_Monthly_Metrics, {
            name: WBSchemas.esWB_Plan_Volume_Monthly_Metrics,
            idAttribute: 'WB_PLANMMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_Volume_Monthly_Metrics),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_Volume_Monthly_Metrics),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_Volume_Monthly_Metrics),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_Volume_Monthly_Metrics),
            endpoint: this.sRESTBaseURL + '/api/WB_PlanMMts',
            recordClass: WBSchemaRows.WB_PlanVolumeMonthlyMetricsRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_Volume_Monthly_Metrics,
            relations: WBSchemas_Other.WB_Plan_Volume_Monthly_Metrics_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Plan_Funding_Metrics, {
            name: WBSchemas.esWB_Plan_Funding_Metrics,
            idAttribute: 'WB_PLANFMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_Funding_Metrics),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_Funding_Metrics),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_Funding_Metrics),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_Funding_Metrics),
            endpoint: this.sRESTBaseURL + '/api/WB_PlanFMts',
            recordClass: WBSchemaRows.WB_PlanFundingMetricsRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_Funding_Metrics,
            relations: WBSchemas_Other.WB_Plan_Funding_Metrics_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Plan_Approval_Metrics, {
            name: WBSchemas.esWB_Plan_Approval_Metrics,
            idAttribute: 'WB_PLANAMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_Approval_Metrics),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_Approval_Metrics),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_Approval_Metrics),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_Approval_Metrics),
            endpoint: this.sRESTBaseURL + '/api/WB_PlanAMts',
            recordClass: WBSchemaRows.WB_PlanApprovalMetricsRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_Approval_Metrics,
            relations: WBSchemas_Other.WB_Plan_Approval_Metrics_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Plan_Product_Metrics, {
            name: WBSchemas.esWB_Plan_Product_Metrics,
            idAttribute: 'WB_PLANPRDMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_Product_Metrics),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_Product_Metrics),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_Product_Metrics),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_Product_Metrics),
            endpoint: this.sRESTBaseURL + '/api/WB_PlanPrdMts',
            recordClass: WBSchemaRows.WB_PlanProductMetricsRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_Product_Metrics,
            relations: WBSchemas_Other.WB_Plan_Product_Metrics_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - SourceType">
        // SourceType
        this.defineMapper(WBSchemas.esWB_SourceType, {
            name: WBSchemas.esWB_SourceType,
            idAttribute: 'WB_SOURCETYPE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_SourceType),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_SourceType),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_SourceType),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_SourceType),
            endpoint: this.sRESTBaseURL + '/api/WB_SourceTypes',
            recordClass: WBSchemaRows.WB_SourceTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_SourceType,
            relations: WBSchemas_DataFactory.WB_SourceType_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_SourceTypeSTR, {
            name: WBSchemas.esWB_SourceTypeSTR,
            idAttribute: 'WB_SOURCETYPESTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_SourceTypeSTR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_SourceTypeSTR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_SourceTypeSTR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_SourceTypeSTR),
            endpoint: this.sRESTBaseURL + '/api/WB_SourceTypeSTRs',
            recordClass: WBSchemaRows.WB_SourceTypeSTRRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_SourceTypeSTR,
            relations: WBSchemas_DataFactory.WB_SourceTypeSTR_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold desc="Define Mappers - EventShipProduct">
        // EventShipProduct
        this.defineMapper(WBSchemas.esWB_EvtShipPrd, {
            name: WBSchemas.esWB_EvtShipPrd,
            idAttribute: 'WB_EVTSHIPPRD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_EvtShipPrd),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_EvtShipPrd),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_EvtShipPrd),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_EvtShipPrd),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtShipPrds',
            recordClass: WBSchemaRows.WB_EvtShipPrdRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_EvtShipPrd,
            relations: WBSchemas_Other.WB_EvtShipPrd_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - EventShipCProduct">
        // EventShipCProduct
        this.defineMapper(WBSchemas.esWB_EvtShipCPrd, {
            name: WBSchemas.esWB_EvtShipCPrd,
            idAttribute: 'WB_EVTSHIPCPRD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_EvtShipCPrd),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_EvtShipCPrd),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_EvtShipCPrd),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_EvtShipCPrd),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtShipCPrds',
            recordClass: WBSchemaRows.WB_EvtShipCPrdRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_EvtShipCPrd,
            relations: WBSchemas_Other.WB_EvtShipCPrd_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>
        // <editor-fold desc="Define Mappers - Event">
        // Event
        this.defineMapper(WBSchemas.esWB_Event, {
            name: WBSchemas.esWB_Event,
            idAttribute: 'WB_EVT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event),

            endpoint: this.sRESTBaseURL + '/api/WB_Evts',
            recordClass: WBSchemaRows.WB_EventRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event,
            relations: WBSchemas_Other.WB_Event_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_EventFR, {
            name: WBSchemas.esWB_EventFR,
            idAttribute: 'WB_EVTFR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_EventFR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_EventFR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_EventFR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_EventFR),

            endpoint: this.sRESTBaseURL + '/api/WB_EvtFRs',
            recordClass: WBSchemaRows.WB_EventFRRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_EvtFR,
            relations: WBSchemas_Other.WB_EvtFR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Product_Group, {
            name: WBSchemas.esWB_Event_Product_Group,
            idAttribute: 'WB_EVTPG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Product_Group),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Product_Group),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Product_Group),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Product_Group),

            endpoint: this.sRESTBaseURL + '/api/WB_EvtPGs',
            recordClass: WBSchemaRows.WB_EventProductGroupRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event_Product_Group,
            relations: WBSchemas_Other.WB_Event_Product_Group_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Product, {
            name: WBSchemas.esWB_Event_Product,
            idAttribute: 'WB_EVTPRD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Product),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Product),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Product),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Product),

            endpoint: this.sRESTBaseURL + '/api/WB_EvtPrds',
            recordClass: WBSchemaRows.WB_EventProductRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event_Product,
            relations: WBSchemas_Other.WB_Event_Product_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Customer, {
            name: WBSchemas.esWB_Event_Customer,
            idAttribute: 'WB_EVTSCUST_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Customer),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Customer),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Customer),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Customer),

            endpoint: this.sRESTBaseURL + '/api/WB_EvtSCsts',
            recordClass: WBSchemaRows.WB_EventCustomerRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event_Customer,
            relations: WBSchemas_Other.WB_Event_Customer_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Meta, {
            name: WBSchemas.esWB_Event_Meta,
            idAttribute: 'WB_EVTMETAR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Meta),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Meta),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Meta),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Meta),

            endpoint: this.sRESTBaseURL + '/api/WB_EvtMetaRs',
            recordClass: WBSchemaRows.WB_EventMetaRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event_Meta,
            relations: WBSchemas_Other.WB_Event_Meta_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Relationship, {
            name: WBSchemas.esWB_Event_Relationship,
            idAttribute: 'WB_EVTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtRs',
            recordClass: WBSchemaRows.WB_EventRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event_Relationship,
            relations: WBSchemas_Other.WB_Event_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Tag, {
            name: WBSchemas.esWB_Event_Tag,
            idAttribute: 'WB_EVTTAG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Tag),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Tag),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Tag),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Tag),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtTagRs',
            recordClass: WBSchemaRows.WB_EventTagRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event_Tag,
            relations: WBSchemas_Other.WB_Event_Tag_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Override, {
            name: WBSchemas.esWB_Event_Override,
            idAttribute: 'WB_EVT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Override),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Override),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Override),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Override),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtOs',
            recordClass: WBSchemaRows.WB_EventOverrideRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event_Override,
            relations: WBSchemas_Other.WB_Event_Override_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Product_Group_Override, {
            name: WBSchemas.esWB_Event_Product_Group_Override,
            idAttribute: 'WB_EVTPG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Product_Group_Override),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Product_Group_Override),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Product_Group_Override),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Product_Group_Override),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtPGOs',
            recordClass: WBSchemaRows.WB_EventProductGroupOverrideRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event_Product_Group_Override,
            relations: WBSchemas_Other.WB_Event_Product_Group_Override_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Portfolio, {
            name: WBSchemas.esWB_Event_Portfolio,
            idAttribute: 'WB_EVT_PORTFOLIO_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Portfolio),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Portfolio),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Portfolio),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Portfolio),

            endpoint: this.sRESTBaseURL + '/api/WB_Evt_Portfolios',
            recordClass: WBSchemaRows.WB_EventPortfolioRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_Event_Portfolio,
            relations: WBSchemas_PEA.WB_Event_Portfolio_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Portfolio_Relationship, {
            name: WBSchemas.esWB_Event_Portfolio_Relationship,
            idAttribute: 'WB_EVT_PORTFOLIOR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Portfolio_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Portfolio_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Portfolio_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Portfolio_Relationship),

            endpoint: this.sRESTBaseURL + '/api/WB_Evt_PortfolioRs',
            recordClass: WBSchemaRows.WB_EventPortfolioRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_Event_Portfolio_Relationship,
            relations: WBSchemas_PEA.WB_Event_Portfolio_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Portfolio_Customer_Relationship, {
            name: WBSchemas.esWB_Event_Portfolio_Customer_Relationship,
            idAttribute: 'WB_EVT_PORTFOLIOCR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Portfolio_Customer_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Portfolio_Customer_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Portfolio_Customer_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Event_Portfolio_Customer_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Evt_PortfolioCRs',
            recordClass: WBSchemaRows.WB_EventPortfolioCustomerRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_Event_Portfolio_Customer_Relationship,
            relations: WBSchemas_PEA.WB_Event_Portfolio_Customer_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Portfolio_Product_Relationship, {
            name: WBSchemas.esWB_Event_Portfolio_Product_Relationship,
            idAttribute: 'WB_EVT_PORTFOLIOPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Portfolio_Product_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Portfolio_Product_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Portfolio_Product_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Event_Portfolio_Product_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Evt_PortfolioPRs',
            recordClass: WBSchemaRows.WB_EventPortfolioProductRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_Event_Portfolio_Product_Relationship,
            relations: WBSchemas_PEA.WB_Event_Portfolio_Product_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Portfolio_Period_Relationship, {
            name: WBSchemas.esWB_Event_Portfolio_Period_Relationship,
            idAttribute: 'WB_EVT_PORTFOLIOTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Portfolio_Period_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Portfolio_Period_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Portfolio_Period_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Event_Portfolio_Period_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Evt_PortfolioTRs',
            recordClass: WBSchemaRows.WB_EventPortfolioPeriodRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_Event_Portfolio_Period_Relationship,
            relations: WBSchemas_PEA.WB_Event_Portfolio_Period_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Portfolio_Tag_Relationship, {
            name: WBSchemas.esWB_Event_Portfolio_Tag_Relationship,
            idAttribute: 'WB_EVT_PORTFOLIOTGR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Portfolio_Tag_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Portfolio_Tag_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Portfolio_Tag_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Event_Portfolio_Tag_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Evt_PortfolioTGRs',
            recordClass: WBSchemaRows.WB_EventPortfolioTagRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_Event_Portfolio_Tag_Relationship,
            relations: WBSchemas_PEA.WB_Event_Portfolio_Tag_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Portfolio_EventType_Relationship, {
            name: WBSchemas.esWB_Event_Portfolio_EventType_Relationship,
            idAttribute: 'WB_EVT_PORTFOLIOETR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Portfolio_EventType_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Portfolio_EventType_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Portfolio_EventType_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Event_Portfolio_EventType_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Evt_PortfolioETRs',
            recordClass: WBSchemaRows.WB_EventPortfolioEventTypeRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_Event_Portfolio_EventType_Relationship,
            relations: WBSchemas_PEA.WB_Event_Portfolio_EventType_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Portfolio_LinkedAnalysis, {
            name: WBSchemas.esWB_Event_Portfolio_LinkedAnalysis,
            idAttribute: 'WB_EVT_PORTFOLIOLA_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Portfolio_LinkedAnalysis),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Portfolio_LinkedAnalysis),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Portfolio_LinkedAnalysis),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Event_Portfolio_LinkedAnalysis
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Evt_PortfolioLAs',
            recordClass: WBSchemaRows.WB_EventPortfolioLinkedAnalysisRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_Event_Portfolio_LinkedAnalysis,
            relations: WBSchemas_PEA.WB_Event_Portfolio_LinkedAnalysis_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Portfolio_LinkedAnalysis_Relationship, {
            name: WBSchemas.esWB_Event_Portfolio_LinkedAnalysis_Relationship,
            idAttribute: 'WB_EVT_PORTFOLIOLAR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Portfolio_LinkedAnalysis_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Portfolio_LinkedAnalysis_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Portfolio_LinkedAnalysis_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Event_Portfolio_LinkedAnalysis_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Evt_PortfolioLARs',
            recordClass: WBSchemaRows.WB_EventPortfolioLinkedAnalysisRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_Event_Portfolio_LinkedAnalysis_Relationship,
            relations: WBSchemas_PEA.WB_Event_Portfolio_LinkedAnalysis_Relationship_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Event Spend">
        // TPM Plan Event Spend
        this.defineMapper(WBSchemas.esWB_Event_Spend, {
            name: WBSchemas.esWB_Event_Spend,
            idAttribute: 'WB_EVTS_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Spend),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Spend),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Spend),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Spend),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtSes',
            recordClass: WBSchemaRows.WB_EventSpendRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event_Spend,
            relations: WBSchemas_Other.WB_Event_Spend_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Spend_PrdGrp, {
            name: WBSchemas.esWB_Event_Spend_PrdGrp,
            idAttribute: 'WB_EVTSPG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Spend_PrdGrp),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Spend_PrdGrp),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Spend_PrdGrp),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Spend_PrdGrp),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtSPGs',
            recordClass: WBSchemaRows.WB_EventSpendPrdGrpRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event_Spend_PrdGrp,
            relations: WBSchemas_Other.WB_Event_Spend_PrdGrp_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Spend_Prd, {
            name: WBSchemas.esWB_Event_Spend_Prd,
            idAttribute: 'WB_EVTSPRD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Spend_Prd),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Spend_Prd),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Spend_Prd),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Spend_Prd),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtSPrds',
            recordClass: WBSchemaRows.WB_EventSpendPrdRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event_Spend_Prd,
            relations: WBSchemas_Other.WB_Event_Spend_Prd_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Spend_PrdGrp_Customer, {
            name: WBSchemas.esWB_Event_Spend_PrdGrp_Customer,
            idAttribute: 'WB_EVTSPGC_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Spend_PrdGrp_Customer),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Spend_PrdGrp_Customer),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Spend_PrdGrp_Customer),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Spend_PrdGrp_Customer),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtSPGCs',
            recordClass: WBSchemaRows.WB_EventSpendPrdGrpCustomerRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event_Spend_PrdGrp_Customer,
            relations: WBSchemas_Other.WB_Event_Spend_PrdGrp_Customer_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Spend_PrdGrp_Override, {
            name: WBSchemas.esWB_Event_Spend_PrdGrp_Override,
            idAttribute: 'WB_EVTSPG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Spend_PrdGrp_Override),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Spend_PrdGrp_Override),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Spend_PrdGrp_Override),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Spend_PrdGrp_Override),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtSPGOs',
            recordClass: WBSchemaRows.WB_EventSpendPrdGrpOverrideRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event_Spend_PrdGrp_Override,
            relations: WBSchemas_Other.WB_Event_Spend_PrdGrp_Override_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Spend_PrdGrp_Redistrib, {
            name: WBSchemas.esWB_Event_Spend_PrdGrp_Redistrib,
            idAttribute: 'WB_EVTRSPG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Spend_PrdGrp_Redistrib),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Spend_PrdGrp_Redistrib),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Spend_PrdGrp_Redistrib),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Spend_PrdGrp_Redistrib),

            endpoint: this.sRESTBaseURL + '/api/WB_EvtRSPGs',
            recordClass: WBSchemaRows.WB_EventSpendPrdGrpRedistribRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event_Spend_PrdGrp_Redistrib,
            relations: WBSchemas_Other.WB_Event_Spend_PrdGrp_Redistrib_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Spend_Metrics_Snapshot, {
            name: WBSchemas.esWB_Event_Spend_Metrics_Snapshot,
            idAttribute: 'WB_TEVTSMTS_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Spend_Metrics_Snapshot),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Spend_Metrics_Snapshot),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Spend_Metrics_Snapshot),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Spend_Metrics_Snapshot),
            endpoint: this.sRESTBaseURL + '/api/WB_TEvtSMtSes',
            recordClass: WBSchemaRows.WB_EventSpendMetricsSnapshotRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event_Spend_Metrics_Snapshot,
            relations: WBSchemas_Other.WB_Event_Spend_Metrics_Snapshot_JSData_RELs,
            debug: bDebug
        })

        // Shipper Spend
        this.defineMapper(WBSchemas.esWB_EvtSShpPrd, {
            name: WBSchemas.esWB_EvtSShpPrd,
            idAttribute: 'WB_EVTSSHPPRD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_EvtSShpPrd),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_EvtSShpPrd),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_EvtSShpPrd),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_EvtSShpPrd),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtSShpPrds',
            recordClass: WBSchemaRows.WB_EvtSShpPrdRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_EvtSShpPrd,
            relations: WBSchemas_Other.WB_EvtSShpPrd_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_EvtSShpPrdO, {
            name: WBSchemas.esWB_EvtSShpPrdO,
            idAttribute: 'WB_EVTSSHPPRD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_EvtSShpPrdO),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_EvtSShpPrdO),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_EvtSShpPrdO),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_EvtSShpPrdO),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtSShpPrdOs',
            recordClass: WBSchemaRows.WB_EvtSShpPrdOverrideRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_EvtSShpPrdO,
            relations: WBSchemas_Other.WB_EvtSShpPrdO_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Circular, {
            name: WBSchemas.esWB_Event_Circular,
            idAttribute: 'WB_EVTCIR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Circular),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Circular),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Circular),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Circular),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtCirs',
            recordClass: WBSchemaRows.WB_EventCircularRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event_Circular,
            relations: WBSchemas_Other.WB_Event_Circular_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Plan_NFP_Snapshot, {
            name: WBSchemas.esWB_Plan_NFP_Snapshot,
            idAttribute: 'WB_PLANNFPS_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_NFP_Snapshot),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_NFP_Snapshot),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_NFP_Snapshot),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_NFP_Snapshot),
            endpoint: this.sRESTBaseURL + '/api/WB_PlanNFPSs',
            recordClass: WBSchemaRows.WB_EventSpendMetricsSnapshotRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_NFP_Snapshot,
            relations: WBSchemas_Other.WB_Plan_NFP_Snapshot_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Spend Types">
        // Spend Meta
        this.defineMapper(WBSchemas.esWB_Spend_Type, {
            name: WBSchemas.esWB_Spend_Type,
            idAttribute: 'WB_SPENDM_ID',

            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Spend_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Spend_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Spend_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Spend_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_SpendMs',
            recordClass: WBSchemaRows.WB_SpendTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Spend_Type,
            relations: WBSchemas_Other.WB_Spend_Type_JSData_RELs,
            debug: bDebug
        })

        // TPM Spend Type
        this.defineMapper(WBSchemas.esWB_Event_Spend_Type, {
            name: WBSchemas.esWB_Event_Spend_Type,
            idAttribute: 'WB_EVTSTYPE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Spend_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Spend_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Spend_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Spend_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtSTypes',
            recordClass: WBSchemaRows.WB_TPM_EventSpendTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_TPM_Event_Spend_Type,
            relations: WBSchemas_Other.WB_TPM_Event_Spend_Type_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Spend_Type_Account, {
            name: WBSchemas.esWB_Event_Spend_Type_Account,
            idAttribute: 'WB_EVTSACCT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Spend_Type_Account),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Spend_Type_Account),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Spend_Type_Account),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Spend_Type_Account),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtSTypeAccts',
            recordClass: WBSchemaRows.WB_TPM_EventSpendTypeAccountRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_TPM_Event_Spend_Type_Account,
            relations: WBSchemas_Other.WB_TPM_Event_Spend_Type_Account_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Plan Type ">
        // Plan Type
        this.defineMapper(WBSchemas.esWB_Plan_Type, {
            name: WBSchemas.esWB_Plan_Type,
            idAttribute: 'WB_PLANM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_PlanMs',
            recordClass: WBSchemaRows.WB_PlanTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_Type,
            relations: WBSchemas_Other.WB_Plan_Type_JSData_RELs,
            debug: bDebug
        })

        // Plan Type Fund Type
        this.defineMapper(WBSchemas.esWB_Plan_Type_Fund_Type, {
            name: WBSchemas.esWB_Plan_Type_Fund_Type,
            idAttribute: 'WB_PLANMFND_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_Type_Fund_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_Type_Fund_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_Type_Fund_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_Type_Fund_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_PlanMFnds',
            recordClass: WBSchemaRows.WB_PlanTypeFundTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_Type_Fund_Type,
            relations: WBSchemas_Other.WB_Plan_Type_Fund_Type_JSData_RELs,
            debug: bDebug
        })

        // Plan Type Event Type
        this.defineMapper(WBSchemas.esWB_Plan_Type_Event_Type, {
            name: WBSchemas.esWB_Plan_Type_Event_Type,
            idAttribute: 'WB_PLANMEVTT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_Type_Event_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_Type_Event_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_Type_Event_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_Type_Event_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_PlanMEvts',
            recordClass: WBSchemaRows.WB_PlanTypeEventTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_Type_Event_Type,
            relations: WBSchemas_Other.WB_Plan_Type_Event_Type_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - TPM Event ">
        // TPM Event
        this.defineMapper(WBSchemas.esWB_TPM_Event, {
            name: WBSchemas.esWB_TPM_Event,
            idAttribute: 'WB_TEVT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_TPM_Event),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_TPM_Event),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_TPM_Event),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_TPM_Event),
            endpoint: this.sRESTBaseURL + '/api/WB_TEvts',
            recordClass: WBSchemaRows.WB_TPM_EventRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_TPM_Event,
            relations: WBSchemas_Other.WB_TPM_Event_JSData_RELs,
            debug: bDebug
        })

        // TPM Event Product Group
        this.defineMapper(WBSchemas.esWB_TPM_Event_Product_Group, {
            name: WBSchemas.esWB_TPM_Event_Product_Group,
            idAttribute: 'WB_TEVTPG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_TPM_Event_Product_Group),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_TPM_Event_Product_Group),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_TPM_Event_Product_Group),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_TPM_Event_Product_Group),
            endpoint: this.sRESTBaseURL + '/api/WB_TEvtPGs',
            recordClass: WBSchemaRows.WB_TPM_EventProductGroupRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_TPM_Event_Product_Group,
            relations: WBSchemas_Other.WB_TPM_Event_Product_Group_JSData_RELs,
            debug: bDebug
        })

        // TPM Event Product
        this.defineMapper(WBSchemas.esWB_TPM_Event_Product, {
            name: WBSchemas.esWB_TPM_Event_Product,
            idAttribute: 'WB_TEVTPRD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_TPM_Event_Product),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_TPM_Event_Product),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_TPM_Event_Product),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_TPM_Event_Product),
            endpoint: this.sRESTBaseURL + '/api/WB_TEvtPrds',
            recordClass: WBSchemaRows.WB_TPM_EventProductRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_TPM_Event_Product,
            relations: WBSchemas_Other.WB_TPM_Event_Product_JSData_RELs,
            debug: bDebug
        })

        // TPM Event Tag
        this.defineMapper(WBSchemas.esWB_TPM_Event_Tag, {
            name: WBSchemas.esWB_TPM_Event_Tag,
            idAttribute: 'WB_TEVTTAGR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_TPM_Event_Tag),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_TPM_Event_Tag),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_TPM_Event_Tag),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_TPM_Event_Tag),
            endpoint: this.sRESTBaseURL + '/api/WB_TEvtTagRs',
            recordClass: WBSchemaRows.WB_TPM_EventTagRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_TPM_Event_Tag,
            relations: WBSchemas_Other.WB_TPM_Event_Tag_JSData_RELs,
            debug: bDebug
        })

        // TPM Event Metrics
        this.defineMapper(WBSchemas.esWB_TPM_Event_Metrics, {
            name: WBSchemas.esWB_TPM_Event_Metrics,
            idAttribute: 'WB_TEVTMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_TPM_Event_Metrics),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_TPM_Event_Metrics),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_TPM_Event_Metrics),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_TPM_Event_Metrics),
            endpoint: this.sRESTBaseURL + '/api/WB_TEvtMts',
            recordClass: WBSchemaRows.WB_TPM_EventMetricsRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_TPM_Event_Metrics,
            relations: WBSchemas_Other.WB_TPM_Event_Metrics_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - TPM Event Spend">
        // TPM Event Spend
        this.defineMapper(WBSchemas.esWB_TPM_Event_Spend, {
            name: WBSchemas.esWB_TPM_Event_Spend,
            idAttribute: 'WB_TEVTS_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_TPM_Event_Spend),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_TPM_Event_Spend),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_TPM_Event_Spend),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_TPM_Event_Spend),
            endpoint: this.sRESTBaseURL + '/api/WB_TEvtSs',
            recordClass: WBSchemaRows.WB_TPM_EventSpendRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_TPM_Event_Spend,
            relations: WBSchemas_Other.WB_TPM_Event_Spend_JSData_RELs,
            debug: bDebug
        })

        // TPM Event Spend Product Group
        this.defineMapper(WBSchemas.esWB_TPM_Event_Spend_Product_Group, {
            name: WBSchemas.esWB_TPM_Event_Spend_Product_Group,
            idAttribute: 'WB_TEVTSPG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_TPM_Event_Spend_Product_Group),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_TPM_Event_Spend_Product_Group),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_TPM_Event_Spend_Product_Group),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_TPM_Event_Spend_Product_Group),
            endpoint: this.sRESTBaseURL + '/api/WB_TEvtSPGs',
            recordClass: WBSchemaRows.WB_TPM_EventSpendPrdGrpRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_TPM_Event_Spend_PrdGrp,
            relations: WBSchemas_Other.WB_TPM_Event_Spend_PrdGrp_JSData_RELs,
            debug: bDebug
        })

        // TPM Event Spend Product
        this.defineMapper(WBSchemas.esWB_TPM_Event_Spend_Product, {
            name: WBSchemas.esWB_TPM_Event_Spend_Product,
            idAttribute: 'WB_TEVTSPRD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_TPM_Event_Spend_Product),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_TPM_Event_Spend_Product),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_TPM_Event_Spend_Product),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_TPM_Event_Spend_Product),
            endpoint: this.sRESTBaseURL + '/api/WB_TEvtSPrds',
            recordClass: WBSchemaRows.WB_TPM_EventSpendPrdRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_TPM_Event_Spend_Prd,
            relations: WBSchemas_Other.WB_TPM_Event_Spend_Prd_JSData_RELs,
            debug: bDebug
        })

        // TPM Event Spend Metrics
        this.defineMapper(WBSchemas.esWB_TPM_Event_Spend_Metrics, {
            name: WBSchemas.esWB_TPM_Event_Spend_Metrics,
            idAttribute: 'WB_TEVTSMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_TPM_Event_Spend_Metrics),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_TPM_Event_Spend_Metrics),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_TPM_Event_Spend_Metrics),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_TPM_Event_Spend_Metrics),
            endpoint: this.sRESTBaseURL + '/api/WB_TEvtSMts',
            recordClass: WBSchemaRows.WB_TPM_EventSpendMetricsRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_TPM_Event_Spend_Metrics,
            relations: WBSchemas_Other.WB_TPM_Event_Spend_Metrics_JSData_RELs,
            debug: bDebug
        })

        // TPM Event Period Metrics
        this.defineMapper(WBSchemas.esWB_TPM_Event_Product_Metrics, {
            name: WBSchemas.esWB_TPM_Event_Product_Metrics,
            idAttribute: 'WB_TEVTPRDMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_TPM_Event_Product_Metrics),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_TPM_Event_Product_Metrics),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_TPM_Event_Product_Metrics),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_TPM_Event_Product_Metrics),
            endpoint: this.sRESTBaseURL + '/api/WB_TEvtPrdMts',
            recordClass: WBSchemaRows.WB_TPM_EventProductMetricsRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_TPM_Event_Product_Metrics,
            relations: WBSchemas_Other.WB_TPM_Event_Product_Metrics_JSData_RELs,
            debug: bDebug
        })

        // TPM Event Spend Customer

        this.defineMapper(WBSchemas.esWB_TPM_Event_Spend_Customer, {
            name: WBSchemas.esWB_TPM_Event_Spend_Customer,
            idAttribute: 'WB_TEVTSCST_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_TPM_Event_Spend_Customer),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_TPM_Event_Spend_Customer),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_TPM_Event_Spend_Customer),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_TPM_Event_Spend_Customer),
            endpoint: this.sRESTBaseURL + '/api/WB_TEvtSCsts',
            recordClass: WBSchemaRows.WB_TPM_EventSpendCustomerRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_TPM_Event_Spend_Customer,
            relations: WBSchemas_Other.WB_TPM_Event_Spend_Customer_JSData_RELs,
            debug: bDebug
        })

        // OI Notify Email
        this.defineMapper(WBSchemas.esWB_OI_Notify_Email, {
            name: WBSchemas.esWB_OI_Notify_Email,
            idAttribute: 'WB_OI_NOTIFY_EMAIL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_OI_Notify_Email),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_OI_Notify_Email),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_OI_Notify_Email),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_OI_Notify_Email),
            endpoint: this.sRESTBaseURL + '/api/WB_OI_Notify_Emails',
            recordClass: WBSchemaRows.WB_OI_NotifyEmailRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_OI_Notify_Email,
            relations: WBSchemas_Other.WB_OI_Notify_Email_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Event Lift">
        this.defineMapper(WBSchemas.esWB_Lift, {
            name: WBSchemas.esWB_Lift,
            idAttribute: 'WB_LIFT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Lift),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Lift),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Lift),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Lift),
            endpoint: this.sRESTBaseURL + '/api/WB_Lifts',
            recordClass: WBSchemaRows.WB_LiftRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Lift,
            relations: WBSchemas_Other.WB_Lift_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Event Accrual">
        this.defineMapper(WBSchemas.esWB_Event_Accrual, {
            name: WBSchemas.esWB_Event_Accrual,
            idAttribute: 'WB_EVTACR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Accrual),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Accrual),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Accrual),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Accrual),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtAcrs',
            recordClass: WBSchemaRows.WB_EventAccrualRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event_Accrual,
            relations: WBSchemas_Other.WB_Event_Accrual_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Claims">
        // Claim Type
        this.defineMapper(WBSchemas.esWB_Claim_Type, {
            name: WBSchemas.esWB_Claim_Type,
            idAttribute: 'WB_CLMTYPE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Claim_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Claim_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Claim_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Claim_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_ClmTypes',
            recordClass: WBSchemaRows.WB_ClaimTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Claim_Type,
            relations: WBSchemas_Other.WB_Claim_Type_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Claim_Type_Payment_Type, {
            name: WBSchemas.esWB_Claim_Type_Payment_Type,
            idAttribute: 'WB_CLMTYPEPMTTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Claim_Type_Payment_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Claim_Type_Payment_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Claim_Type_Payment_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Claim_Type_Payment_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_ClmTypePmtTRs',
            recordClass: WBSchemaRows.WB_ClaimTypePaymentTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Claim_Type_Payment_Type,
            relations: WBSchemas_Other.WB_Claim_Type_Payment_Type_JSData_RELs,
            debug: bDebug
        })

        // Claim
        this.defineMapper(WBSchemas.esWB_Claim, {
            name: WBSchemas.esWB_Claim,
            idAttribute: 'WB_CLM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Claim),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Claim),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Claim),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Claim),
            endpoint: this.sRESTBaseURL + '/api/WB_Clms',
            recordClass: WBSchemaRows.WB_ClaimRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Claim,
            relations: WBSchemas_Other.WB_Claim_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Claim_Plan_Match, {
            name: WBSchemas.esWB_Claim_Plan_Match,
            idAttribute: 'WB_CLMPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Claim_Plan_Match),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Claim_Plan_Match),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Claim_Plan_Match),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Claim_Plan_Match),
            endpoint: this.sRESTBaseURL + '/api/WB_ClmPRs',
            recordClass: WBSchemaRows.WB_ClaimPlanMatchRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Claim_Plan_Match,
            relations: WBSchemas_Other.WB_Claim_Plan_Match_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Payments">
        // Payment Type
        this.defineMapper(WBSchemas.esWB_Payment_Type, {
            name: WBSchemas.esWB_Payment_Type,
            idAttribute: 'WB_PMTTYPE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Payment_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Payment_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Payment_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Payment_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_PmtTypes',
            recordClass: WBSchemaRows.WB_PaymentTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Payment_Type,
            relations: WBSchemas_Other.WB_Payment_Type_JSData_RELs,
            debug: bDebug
        })

        // Payment
        this.defineMapper(WBSchemas.esWB_Payment, {
            name: WBSchemas.esWB_Payment,
            idAttribute: 'WB_PMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Payment),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Payment),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Payment),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Payment),
            endpoint: this.sRESTBaseURL + '/api/WB_Pmts',
            recordClass: WBSchemaRows.WB_PaymentRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Payment,
            relations: WBSchemas_Other.WB_Payment_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Payment_Claim, {
            name: WBSchemas.esWB_Payment_Claim,
            idAttribute: 'WB_PMTCLMR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Payment_Claim),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Payment_Claim),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Payment_Claim),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Payment_Claim),
            endpoint: this.sRESTBaseURL + '/api/WB_PmtClmRs',
            recordClass: WBSchemaRows.WB_PaymentClaimRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Payment_Claims,
            relations: WBSchemas_Other.WB_Payment_Claims_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Payment_Event_Spend, {
            name: WBSchemas.esWB_Payment_Event_Spend,
            idAttribute: 'WB_PMTEVTS_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Payment_Event_Spend),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Payment_Event_Spend),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Payment_Event_Spend),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Payment_Event_Spend),
            endpoint: this.sRESTBaseURL + '/api/WB_PmtEvtSs',
            recordClass: WBSchemaRows.WB_PaymentEventSpendRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Payment_Event_Spend,
            relations: WBSchemas_Other.WB_Payment_Event_Spend_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Payment_Event_Spend_Product_Group, {
            name: WBSchemas.esWB_Payment_Event_Spend_Product_Group,
            idAttribute: 'WB_PMTEVTSP_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Payment_Event_Spend_Product_Group),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Payment_Event_Spend_Product_Group),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Payment_Event_Spend_Product_Group),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Payment_Event_Spend_Product_Group
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_PmtEvtSPs',
            recordClass: WBSchemaRows.WB_PaymentEventSpendProductGroupRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Payment_Event_Spend_Product_Group,
            relations: WBSchemas_Other.WB_Payment_Event_Spend_Product_Group_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Payment_Fund, {
            name: WBSchemas.esWB_Payment_Fund,
            idAttribute: 'WB_PMTFND_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Payment_Fund),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Payment_Fund),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Payment_Fund),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Payment_Fund),
            endpoint: this.sRESTBaseURL + '/api/WB_PmtFnds',
            recordClass: WBSchemaRows.WB_PaymentFundRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Payment_Fund,
            relations: WBSchemas_Other.WB_Payment_Fund_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Payment_Fund_Product_Group, {
            name: WBSchemas.esWB_Payment_Fund_Product_Group,
            idAttribute: 'WB_PMTFNDP_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Payment_Fund_Product_Group),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Payment_Fund_Product_Group),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Payment_Fund_Product_Group),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Payment_Fund_Product_Group),
            endpoint: this.sRESTBaseURL + '/api/WB_PmtFndPs',
            recordClass: WBSchemaRows.WB_PaymentFundProductGroupRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Payment_Fund_Product_Group,
            relations: WBSchemas_Other.WB_Payment_Fund_Product_Group_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Auth Provider">
        // Authentication and Authorization Provider
        this.defineMapper(WBSchemas.esWB_Auth_Provider, {
            name: WBSchemas.esWB_Auth_Provider,
            idAttribute: 'WB_AUTHPRVD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Auth_Provider),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Auth_Provider),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Auth_Provider),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Auth_Provider),
            endpoint: this.sRESTBaseURL + '/api/WB_AuthPrvds',
            recordClass: WBSchemaRows.WB_AuthProviderRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Auth_Provider,
            relations: WBSchemas_Other.WB_Auth_Provider_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - User">
        // User
        this.defineMapper(WBSchemas.esWB_User, {
            name: WBSchemas.esWB_User,
            idAttribute: 'WB_AUTHUSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_User),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_User),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_User),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_User),
            endpoint: this.sRESTBaseURL + '/api/WB_AuthUsrs',
            recordClass: WBSchemaRows.WB_UserRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_User,
            relations: WBSchemas_Other.WB_User_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_User_Role, {
            name: WBSchemas.esWB_User_Role,
            idAttribute: 'WB_USRROLER_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_User_Role),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_User_Role),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_User_Role),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_User_Role),
            endpoint: this.sRESTBaseURL + '/api/WB_UsrRoleRs',
            recordClass: WBSchemaRows.WB_UserRoleRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_User_Role,
            relations: WBSchemas_Other.WB_User_Role_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_User_Dimension, {
            name: WBSchemas.esWB_User_Dimension,
            idAttribute: 'WB_USRDIMR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_User_Dimension),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_User_Dimension),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_User_Dimension),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_User_Dimension),
            endpoint: this.sRESTBaseURL + '/api/WB_UsrDimRs',
            recordClass: WBSchemaRows.WB_UserDimensionRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_User_Dimension,
            relations: WBSchemas_Other.WB_User_Dimension_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_User_Customer, {
            name: WBSchemas.esWB_User_Customer,
            idAttribute: 'WB_USRCUSTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_User_Customer),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_User_Customer),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_User_Customer),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_User_Customer),
            endpoint: this.sRESTBaseURL + '/api/WB_UsrCustRs',
            recordClass: WBSchemaRows.WB_UserCustomerRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_User_Customer,
            relations: WBSchemas_Other.WB_User_Customer_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Session">
        // Session
        this.defineMapper(WBSchemas.esWB_Session, {
            name: WBSchemas.esWB_Session,
            idAttribute: 'WB_SESSION_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Session),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Session),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Session),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Session),
            endpoint: this.sRESTBaseURL + '/api/WB_Sessions',
            recordClass: WBSchemaRows.WB_SessionRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Session,
            relations: WBSchemas_Other.WB_Session_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Audit Log">
        // Audit Log
        this.defineMapper(WBSchemas.esWB_Audit_Log, {
            name: WBSchemas.esWB_Audit_Log,
            idAttribute: 'WB_AUDITLOG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Audit_Log),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Audit_Log),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Audit_Log),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Audit_Log),
            endpoint: this.sRESTBaseURL + '/api/WB_AuditLogs',
            recordClass: WBSchemaRows.WB_AuditLogRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Audit_Log,
            relations: WBSchemas_Other.WB_Audit_Log_JSData_RELs,
            debug: bDebug,
            recAutoTrimFields: ['LOG_MESSAGE', 'NEW_VALUE', 'PREVIOUS_VALUE', 'SECREF_ID']
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - BusinessUnit">
        // BusinessUnit
        this.defineMapper(WBSchemas.esWB_BusinessUnit, {
            name: WBSchemas.esWB_BusinessUnit,
            idAttribute: 'WB_BUSINESSUNIT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_BusinessUnit),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_BusinessUnit),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_BusinessUnit),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_BusinessUnit),
            endpoint: this.sRESTBaseURL + '/api/WB_BusinessUnits',
            recordClass: WBSchemaRows.WB_BusinessUnitRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_BusinessUnit,
            relations: WBSchemas_Other.WB_BusinessUnit_JSData_RELs,
            debug: bDebug
        })

        // BusinessUnitSR
        this.defineMapper(WBSchemas.esWB_BusinessUnit_Source, {
            name: WBSchemas.esWB_BusinessUnit_Source,
            idAttribute: 'WB_BUSINESSUNITSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_BusinessUnit_Source),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_BusinessUnit_Source),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_BusinessUnit_Source),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_BusinessUnit_Source),
            endpoint: this.sRESTBaseURL + '/api/WB_BusinessUnitSRs',
            recordClass: WBSchemaRows.WB_BusinessUnitSourceRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_BusinessUnit_Source_Relationship,
            relations: WBSchemas_Other.WB_BusinessUnit_Source_Relationship_JSData_RELs,
            debug: bDebug
        })

        // UsrBusinessUnitR
        this.defineMapper(WBSchemas.esWB_BusinessUnit_User, {
            name: WBSchemas.esWB_BusinessUnit_User,
            idAttribute: 'WB_USRBUSINESSUNITR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_BusinessUnit_User),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_BusinessUnit_User),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_BusinessUnit_User),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_BusinessUnit_User),
            endpoint: this.sRESTBaseURL + '/api/WB_UsrBusinessUnitRs',
            recordClass: WBSchemaRows.WB_UsrBusinessUnitRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_BusinessUnit_User_Relationship,
            relations: WBSchemas_Other.WB_BusinessUnit_User_Relationship_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Pricing Engine">
        // Pricing Engine
        this.defineMapper(WBSchemas.esWB_Pricing_Engine, {
            name: WBSchemas.esWB_Pricing_Engine,
            idAttribute: 'WB_PRICINGENGINE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Pricing_Engine),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Pricing_Engine),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Pricing_Engine),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Pricing_Engine),
            endpoint: this.sRESTBaseURL + '/api/WB_Pricing_Engines',
            recordClass: WBSchemaRows.WB_PricingEngineRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Pricing_Engine,
            relations: WBSchemas_Other.WB_Pricing_Engine_JSData_Rels,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Pricing_Engine_Price_Relationship, {
            name: WBSchemas.esWB_Pricing_Engine_Price_Relationship,
            idAttribute: 'WB_PRICINGENGINEPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Pricing_Engine_Price_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Pricing_Engine_Price_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Pricing_Engine_Price_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Pricing_Engine_Price_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Pricing_Engine_PRs',
            recordClass: WBSchemaRows.WB_PricingEnginePriceRelRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Pricing_Engine_Price_Relationship,
            relations: WBSchemas_Other.WB_Pricing_Engine_Price_Relationship_JSData_Rels,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Job">
        // Job
        this.defineMapper(WBSchemas.esWB_Job, {
            name: WBSchemas.esWB_Job,
            idAttribute: 'WB_JOB_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Job),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Job),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Job),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Job),
            endpoint: this.sRESTBaseURL + '/api/WB_Jobs',
            recordClass: WBSchemaRows.WB_JobRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Job,
            relations: WBSchemas_Process.WB_Job_JSData_RELs,
            debug: bDebug
        })

        // Job Trigger
        this.defineMapper(WBSchemas.esWB_Job_Trigger, {
            name: WBSchemas.esWB_Job_Trigger,
            idAttribute: 'WB_JOB_TRIGGER_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Job_Trigger),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Job_Trigger),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Job_Trigger),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Job_Trigger),
            endpoint: this.sRESTBaseURL + '/api/WB_JobTriggers',
            recordClass: WBSchemaRows.WB_JobTriggerRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Job_Trigger,
            relations: WBSchemas_Process.WB_Job_Trigger_JSData_RELs,
            debug: bDebug
        })

        // Job Run
        this.defineMapper(WBSchemas.esWB_Job_Run, {
            name: WBSchemas.esWB_Job_Run,
            idAttribute: 'WB_JOB_RUN_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Job_Run),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Job_Run),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Job_Run),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Job_Run),
            endpoint: this.sRESTBaseURL + '/api/WB_JobRuns',
            recordClass: WBSchemaRows.WB_JobRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_JobRun,
            relations: WBSchemas_Process.WB_Job_Run_JSData_RELs,
            debug: bDebug
        })

        // Job Process
        this.defineMapper(WBSchemas.esWB_Job_Process, {
            name: WBSchemas.esWB_Job_Process,
            idAttribute: 'WB_JOBPROCESS_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Job_Process),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Job_Process),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Job_Process),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Job_Process),
            endpoint: this.sRESTBaseURL + '/api/WB_Job_Processes',
            recordClass: WBSchemaRows.WB_JobProcessRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Job_Process,
            relations: WBSchemas_Process.WB_Job_Process_JSData_RELs,
            debug: bDebug
        })

        // Job Process Queue
        this.defineMapper(WBSchemas.esWB_Job_Process_Queue, {
            name: WBSchemas.esWB_Job_Process_Queue,
            idAttribute: 'WB_JOB_PROCESS_QUEUE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Job_Process_Queue),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Job_Process_Queue),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Job_Process_Queue),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Job_Process_Queue),
            endpoint: this.sRESTBaseURL + '/api/WB_Job_Processes_Queues',
            recordClass: WBSchemaRows.WB_JobProcessQueueRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Job_Process_Queue,
            relations: WBSchemas_Process.WB_Job_Process_Queue_JSData_RELs,
            debug: bDebug,
            recAutoTrimFields: ['MESSAGE']
        })

        // Job Processor
        this.defineMapper(WBSchemas.esWB_Job_Processor, {
            name: WBSchemas.esWB_Job_Processor,
            idAttribute: 'WB_JOBPROCESSOR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Job_Processor),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Job_Processor),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Job_Processor),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Job_Processor),
            endpoint: this.sRESTBaseURL + '/api/WB_Job_Processors',
            recordClass: WBSchemaRows.WB_JobProcessorRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Job_Processor,
            relations: WBSchemas_Process.WB_Job_Processor_JSData_RELs,
            debug: bDebug
        })

        // Job Processor Config Relationship
        this.defineMapper(WBSchemas.esWB_Job_Processor_ConfigR, {
            name: WBSchemas.esWB_Job_Processor_ConfigR,
            idAttribute: 'WB_JOBPROCESSOR_CONFIGR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Job_Processor_ConfigR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Job_Processor_ConfigR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Job_Processor_ConfigR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Job_Processor_ConfigR),
            endpoint: this.sRESTBaseURL + '/api/WB_Job_ProcessorConfigRs',
            recordClass: WBSchemaRows.WB_JobProcessorConfigRRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Job_Processor_ConfigR,
            relations: WBSchemas_Process.WB_Job_Processor_ConfigR_JSData_RELs,
            debug: bDebug
        })

        // Job Param
        this.defineMapper(WBSchemas.esWB_Job_Param, {
            name: WBSchemas.esWB_Job_Param,
            idAttribute: 'WB_JOB_PARAM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Job_Param),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Job_Param),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Job_Param),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Job_Param),
            endpoint: this.sRESTBaseURL + '/api/WB_Job_Params',
            recordClass: WBSchemaRows.WB_JobParamRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Job_Param,
            relations: WBSchemas_Process.WB_Job_Param_JSData_RELs,
            debug: bDebug
        })

        // Job Param SR
        this.defineMapper(WBSchemas.esWB_Job_ParamSR, {
            name: WBSchemas.esWB_Job_ParamSR,
            idAttribute: 'WB_JOB_PARAMSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Job_ParamSR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Job_ParamSR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Job_ParamSR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Job_ParamSR),
            endpoint: this.sRESTBaseURL + '/api/WB_Job_ParamSRs',
            recordClass: WBSchemaRows.WB_JobParamSRRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Job_ParamSR,
            relations: WBSchemas_Process.WB_Job_ParamSR_JSData_RELs,
            debug: bDebug
        })

        // Automation
        this.defineMapper(WBSchemas.esWB_Automation_Domain, {
            name: WBSchemas.esWB_Automation_Domain,
            idAttribute: 'WB_AUTOMATION_DOMAIN_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Automation_Domain),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Automation_Domain),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Automation_Domain),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Automation_Domain),
            endpoint: this.sRESTBaseURL + '/api/WB_Automation_Domains',
            recordClass: WBSchemaRows.WB_AutomationDomainRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Automation_Domain,
            relations: WBSchemas_Process.WB_Automation_Domain_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Automation_Flow, {
            name: WBSchemas.esWB_Automation_Flow,
            idAttribute: 'WB_AUTOMATION_FLOW_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Automation_Flow),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Automation_Flow),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Automation_Flow),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Automation_Flow),
            endpoint: this.sRESTBaseURL + '/api/WB_Automation_Flows',
            recordClass: WBSchemaRows.WB_AutomationFlowRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Automation_Flow,
            relations: WBSchemas_Process.WB_Automation_Flow_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Automation_FlowADR, {
            name: WBSchemas.esWB_Automation_FlowADR,
            idAttribute: 'WB_AUTOMATION_FLOWADR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Automation_FlowADR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Automation_FlowADR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Automation_FlowADR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Automation_FlowADR),
            endpoint: this.sRESTBaseURL + '/api/WB_Automation_FlowADRs',
            recordClass: WBSchemaRows.WB_AutomationFlowDomainRRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Automation_Flow_ADR,
            relations: WBSchemas_Process.WB_Automation_Flow_ADR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Automation_FlowGraph, {
            name: WBSchemas.esWB_Automation_FlowGraph,
            idAttribute: 'WB_AUTOMATION_FLOWGRAPH_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Automation_FlowGraph),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Automation_FlowGraph),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Automation_FlowGraph),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Automation_FlowGraph),
            endpoint: this.sRESTBaseURL + '/api/WB_Automation_FlowGraphs',
            recordClass: WBSchemaRows.WB_AutomationFlowGraphRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Automation_Flow_Graph,
            relations: WBSchemas_Process.WB_Automation_Flow_Graph_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Automation_StateMachine, {
            name: WBSchemas.esWB_Automation_StateMachine,
            idAttribute: 'WB_AUTOMATION_STATEMACHINE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Automation_StateMachine),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Automation_StateMachine),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Automation_StateMachine),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Automation_StateMachine),
            endpoint: this.sRESTBaseURL + '/api/WB_Automation_StateMachines',
            recordClass: WBSchemaRows.WB_AutomationStateMachineRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Automation_State_Machine,
            relations: WBSchemas_Process.WB_Automation_State_Machine_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Automation_Run_History, {
            name: WBSchemas.esWB_Automation_Run_History,
            idAttribute: 'WB_AUTOMATION_RUN_HISTORY_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Automation_Run_History),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Automation_Run_History),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Automation_Run_History),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Automation_Run_History),
            endpoint: this.sRESTBaseURL + '/api/WB_Automation_Run_Histories',
            recordClass: WBSchemaRows.WB_AutomationRunHistoryRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Automation_Run_History,
            relations: WBSchemas_Process.WB_Automation_Run_History_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Automation_Run_HistorySR, {
            name: WBSchemas.esWB_Automation_Run_HistorySR,
            idAttribute: 'WB_AUTOMATION_RUN_HISTORYSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Automation_Run_HistorySR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Automation_Run_HistorySR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Automation_Run_HistorySR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Automation_Run_HistorySR),
            endpoint: this.sRESTBaseURL + '/api/WB_Automation_Run_HistorySRs',
            recordClass: WBSchemaRows.WB_AutomationRunHistorySRRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Automation_Run_History_SR,
            relations: WBSchemas_Process.WB_Automation_Run_History_SR_JSData_RELs,
            debug: bDebug
        })


        // Batch Process
        this.defineMapper(WBSchemas.esWB_BatchProcess, {
            name: WBSchemas.esWB_BatchProcess,
            idAttribute: 'WB_BATCHPROCESS_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_BatchProcess),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_BatchProcess),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_BatchProcess),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_BatchProcess),
            endpoint: this.sRESTBaseURL + '/api/WB_BatchProcesses',
            recordClass: WBSchemaRows.WB_BatchProcessRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_BatchProcess,
            relations: WBSchemas_Process.WB_BatchProcess_JSData_RELs,
            debug: bDebug
        })

        // Batch Scope
        this.defineMapper(WBSchemas.esWB_BatchScope, {
            name: WBSchemas.esWB_BatchScope,
            idAttribute: 'WB_BATCHSCOPE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_BatchScope),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_BatchScope),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_BatchScope),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_BatchScope),
            endpoint: this.sRESTBaseURL + '/api/WB_BatchScopes',
            recordClass: WBSchemaRows.WB_BatchScopeRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_BatchScope,
            relations: WBSchemas_Process.WB_BatchScope_JSData_RELs,
            debug: bDebug
        })

        // Batch Scope Company Rel
        this.defineMapper(WBSchemas.esWB_BatchScopeCR, {
            name: WBSchemas.esWB_BatchScopeCR,
            idAttribute: 'WB_BATCHSCOPECR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_BatchScopeCR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_BatchScopeCR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_BatchScopeCR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_BatchScopeCR),
            endpoint: this.sRESTBaseURL + '/api/WB_BatchScopeCRs',
            recordClass: WBSchemaRows.WB_BatchScopeCRRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_BatchScopeCR,
            relations: WBSchemas_Process.WB_BatchScopeCR_JSData_RELs,
            debug: bDebug
        })

        // Batch Scope Master Dataset Rel
        this.defineMapper(WBSchemas.esWB_BatchScopeMR, {
            name: WBSchemas.esWB_BatchScopeMR,
            idAttribute: 'WB_BATCHSCOPEMR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_BatchScopeMR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_BatchScopeMR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_BatchScopeMR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_BatchScopeMR),
            endpoint: this.sRESTBaseURL + '/api/WB_BatchScopeMRs',
            recordClass: WBSchemaRows.WB_BatchScopeMRRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_BatchScopeMR,
            relations: WBSchemas_Process.WB_BatchScopeMR_JSData_RELs,
            debug: bDebug
        })

        // Batch Scope Sales Org Rel
        this.defineMapper(WBSchemas.esWB_BatchScopeSR, {
            name: WBSchemas.esWB_BatchScopeSR,
            idAttribute: 'WB_BATCHSCOPESR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_BatchScopeSR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_BatchScopeSR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_BatchScopeSR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_BatchScopeSR),
            endpoint: this.sRESTBaseURL + '/api/WB_BatchScopeSRs',
            recordClass: WBSchemaRows.WB_BatchScopeSRRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_BatchScopeSR,
            relations: WBSchemas_Process.WB_BatchScopeSR_JSData_RELs,
            debug: bDebug
        })

        // Queue
        this.defineMapper(WBSchemas.esWB_Queue, {
            name: WBSchemas.esWB_Queue,
            idAttribute: 'WB_QUEUE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Queue),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Queue),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Queue),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Queue),
            endpoint: this.sRESTBaseURL + '/api/WB_Queues',
            recordClass: WBSchemaRows.WB_QueueRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Queue,
            relations: WBSchemas_Process.WB_Queue_JSData_RELs,
            debug: bDebug,
            recAutoTrimFields: ['MESSAGE']
        })

        //Queue Run ID (only used to instantiate the KeyGenerator for PAQueueUtils)
        this.defineMapper(WBSchemas.esWB_Queue_RunId, {
            name: WBSchemas.esWB_Queue_RunId,
            idAttribute: 'WB_QUEUE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Queue_RunId),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Queue),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Queue),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Queue),
            endpoint: this.sRESTBaseURL + '/api/WB_Queues',
            recordClass: WBSchemaRows.WB_QueueRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Queue,
            debug: bDebug,
            recAutoTrimFields: ['MESSAGE']
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Job Scheduling">
        // Job Schedule Process
        this.defineMapper(WBSchemas.esWB_Job_Schedule_Process, {
            name: WBSchemas.esWB_Job_Schedule_Process,
            idAttribute: 'WB_JOBSCHEDULEPROCESS_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Job_Schedule_Process),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Job_Schedule_Process),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Job_Schedule_Process),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Job_Schedule_Process),
            endpoint: this.sRESTBaseURL + '/api/WB_Job_Schedule_Processes',
            recordClass: WBSchemaRows.WB_JobScheduleProcessRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Job_Schedule_Process_RelationShip,
            relations: WBSchemas_Process.WB_Job_Schedule_Process_RelationShip_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold desc="Define Mappers - Connection, Dimension, Report ">
        // Connection
        this.defineMapper(WBSchemas.esWB_Connection, {
            name: WBSchemas.esWB_Connection,
            idAttribute: 'WB_CONNECTION_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Connection),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Connection),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Connection),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Connection),
            endpoint: this.sRESTBaseURL + '/api/WB_Connections',
            recordClass: WBSchemaRows.WB_ConnectionRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Connection,
            relations: WBSchemas_Other.WB_Connection_JSData_RELs,
            debug: bDebug
        })

        // Dimension
        this.defineMapper(WBSchemas.esWB_Dimension, {
            name: WBSchemas.esWB_Dimension,
            idAttribute: 'WB_DIMENSION_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Dimension),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Dimension),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Dimension),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Dimension),
            endpoint: this.sRESTBaseURL + '/api/WB_Dimensions',
            recordClass: WBSchemaRows.WB_DimensionRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Dimension,
            relations: WBSchemas_Other.WB_Dimension_JSData_RELs,
            debug: bDebug
        })

        // Report
        this.defineMapper(WBSchemas.esWB_ReportTile, {
            name: WBSchemas.esWB_ReportTile,
            idAttribute: 'WB_REPORTTILE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ReportTile),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ReportTile),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ReportTile),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ReportTile),
            endpoint: this.sRESTBaseURL + '/api/WB_ReportTiles',
            recordClass: WBSchemaRows.WB_ReportTileRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_ReportTile,
            relations: WBSchemas_Other.WB_ReportTile_JSData_RELs,
            debug: bDebug
        })

        // Report Tile Dimension
        this.defineMapper(WBSchemas.esWB_ReportTile_Dimension, {
            name: WBSchemas.esWB_ReportTile_Dimension,
            idAttribute: 'WB_RPTTLDR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ReportTile_Dimension),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ReportTile_Dimension),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ReportTile_Dimension),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ReportTile_Dimension),
            endpoint: this.sRESTBaseURL + '/api/WB_ReportTile_Dimensions',
            recordClass: WBSchemaRows.WB_ReportTileDimensionRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_ReportTile_Dimension,
            relations: WBSchemas_Other.WB_ReportTile_Dimension_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Subject_Area, {
            name: WBSchemas.esWB_Subject_Area,
            idAttribute: 'WB_SUBJECTAREA_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Subject_Area),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Subject_Area),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Subject_Area),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Subject_Area),
            endpoint: this.sRESTBaseURL + '/api/WB_Subject_Areas',
            recordClass: WBSchemaRows.WB_SubjectAreaRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Subject_Area,
            relations: WBSchemas_Other.WB_Subject_Area_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        //<editor-fold desc="Reporting Hierarchies">
        this.defineMapper(WBSchemas.esWB_Report_Customer_Hierarchy, {
            name: WBSchemas.esWB_Report_Customer_Hierarchy,
            idAttribute: 'WB_RPT_CUSTH_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Report_Customer_Hierarchy),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Report_Customer_Hierarchy),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Report_Customer_Hierarchy),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Report_Customer_Hierarchy),
            endpoint: this.sRESTBaseURL + '/api/WB_Report_CustHs',
            recordClass: WBSchemaRows.WB_ReportCustomerHierarchyRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Report_CustomerH,
            relations: WBSchemas_Other.WB_Report_CustomerH_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Report_Product_Group_Hierarchy, {
            name: WBSchemas.esWB_Report_Product_Group_Hierarchy,
            idAttribute: 'WB_RPT_PRDGRPH_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Report_Product_Group_Hierarchy),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Report_Product_Group_Hierarchy),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Report_Product_Group_Hierarchy),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Report_Product_Group_Hierarchy
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Report_PrdGrpHs',
            recordClass: WBSchemaRows.WB_ReportProductGroupHierarchyRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Report_ProductGroupH,
            relations: WBSchemas_Other.WB_Report_ProductGroupH_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Report_Time_Hierarchy, {
            name: WBSchemas.esWB_Report_Time_Hierarchy,
            idAttribute: 'WB_RPT_TIMEH_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Report_Time_Hierarchy),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Report_Time_Hierarchy),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Report_Time_Hierarchy),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Report_Time_Hierarchy),
            endpoint: this.sRESTBaseURL + '/api/WB_Report_TimeHs',
            recordClass: WBSchemaRows.WB_ReportTimeHierarchyRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Report_TimeH,
            relations: WBSchemas_Other.WB_Report_TimeH_JSData_RELs,
            debug: bDebug
        })
        //</editor-fold>

        // <editor-fold desc="Define Mappers - Loader ">
        // Loader

        this.defineMapper(WBSchemas.esWB_Loader, {
            name: WBSchemas.esWB_Loader,
            idAttribute: 'WB_LOADER_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Loader),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Loader),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Loader),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Loader),
            endpoint: this.sRESTBaseURL + '/api/WB_Loaders',
            recordClass: WBSchemaRows.WB_LoaderRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Loader,
            relations: WBSchemas_Other.WB_Loader_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Loader_KeyMap, {
            name: WBSchemas.esWB_Loader_KeyMap,
            idAttribute: 'WB_LOADERKEYMAP_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Loader_KeyMap),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Loader_KeyMap),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Loader_KeyMap),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Loader_KeyMap),
            endpoint: this.sRESTBaseURL + '/api/WB_Loader_KeyMaps',
            recordClass: WBSchemaRows.WB_LoaderKeyMapRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Loader_KeyMap,
            relations: WBSchemas_Other.WB_Loader_KeyMap_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Loader_MetricMap, {
            name: WBSchemas.esWB_Loader_MetricMap,
            idAttribute: 'WB_LOADERMETRICMAP_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Loader_MetricMap),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Loader_MetricMap),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Loader_MetricMap),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Loader_MetricMap),
            endpoint: this.sRESTBaseURL + '/api/WB_Loader_MetricMaps',
            recordClass: WBSchemaRows.WB_LoaderMetricMapRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Loader_MetricMap,
            relations: WBSchemas_Other.WB_Loader_MetricMap_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Loader_MetricTerm, {
            name: WBSchemas.esWB_Loader_MetricTerm,
            idAttribute: 'WB_LOADERMETRICTERM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Loader_MetricTerm),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Loader_MetricTerm),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Loader_MetricTerm),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Loader_MetricTerm),
            endpoint: this.sRESTBaseURL + '/api/WB_Loader_MetricTerms',
            recordClass: WBSchemaRows.WB_LoaderMetricTermRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Loader_MetricTerm,
            relations: WBSchemas_Other.WB_Loader_MetricTerm_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Loader_Operation, {
            name: WBSchemas.esWB_Loader_Operation,
            idAttribute: 'WB_LOADEROPERATION_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Loader_Operation),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Loader_Operation),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Loader_Operation),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Loader_Operation),
            endpoint: this.sRESTBaseURL + '/api/WB_Loader_Operations',
            recordClass: WBSchemaRows.WB_LoaderOperationRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Loader_Operation,
            relations: WBSchemas_Other.WB_Loader_Operation_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Loader_Source, {
            name: WBSchemas.esWB_Loader_Source,
            idAttribute: 'WB_LOADERSOURCE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Loader_Source),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Loader_Source),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Loader_Source),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Loader_Source),
            endpoint: this.sRESTBaseURL + '/api/WB_Loader_Sources',
            recordClass: WBSchemaRows.WB_LoaderSourceRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Loader_Source,
            relations: WBSchemas_Other.WB_Loader_Source_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Workspace ">
        // Workspace
        this.defineMapper(WBSchemas.esWB_Workspace, {
            name: WBSchemas.esWB_Workspace,
            idAttribute: 'WB_WORKSPACE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Workspace),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Workspace),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Workspace),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Workspace),
            endpoint: this.sRESTBaseURL + '/api/WB_Workspaces',
            recordClass: WBSchemaRows.WB_WorkspaceRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Workspace,
            relations: WBSchemas_Other.WB_Workspace_JSData_RELs,
            debug: bDebug
        })

        // Workspace Dimension
        this.defineMapper(WBSchemas.esWB_Workspace_Dimension, {
            name: WBSchemas.esWB_Workspace_Dimension,
            idAttribute: 'WB_WORKSPACE_DIM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Workspace_Dimension),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Workspace_Dimension),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Workspace_Dimension),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Workspace_Dimension),
            endpoint: this.sRESTBaseURL + '/api/WB_WorkspaceDimRs',
            recordClass: WBSchemaRows.WB_WorkspaceDimensionRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Workspace_Dimension,
            relations: WBSchemas_Other.WB_Workspace_Dimension_JSData_RELs,
            debug: bDebug
        })

        // Workspace Report Tile
        this.defineMapper(WBSchemas.esWB_Workspace_Report, {
            name: WBSchemas.esWB_Workspace_Report,
            idAttribute: 'WB_WORKSPACE_RPTL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Workspace_Report),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Workspace_Report),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Workspace_Report),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Workspace_Report),
            endpoint: this.sRESTBaseURL + '/api/WB_WorkspaceRptRs',
            recordClass: WBSchemaRows.WB_WorkspaceReportRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Workspace_Report,
            relations: WBSchemas_Other.WB_Workspace_Report_JSData_RELs,
            debug: bDebug
        })

        // Workspace Report Tile Dimension
        this.defineMapper(WBSchemas.esWB_Workspace_Report_Dimension, {
            name: WBSchemas.esWB_Workspace_Report_Dimension,
            idAttribute: 'WB_WORKSPACE_RTDM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Workspace_Report_Dimension),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Workspace_Report_Dimension),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Workspace_Report_Dimension),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Workspace_Report_Dimension),
            endpoint: this.sRESTBaseURL + '/api/WB_WorkspaceRptDimRs',
            recordClass: WBSchemaRows.WB_WorkspaceReportDimensionRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Workspace_Report_Dimension,
            relations: WBSchemas_Other.WB_Workspace_Report_Dimension_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - User Note ">
        // Note
        this.defineMapper(WBSchemas.esWB_Note, {
            name: WBSchemas.esWB_Note,
            idAttribute: 'WB_NOTE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Note),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Note),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Note),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Note),
            endpoint: this.sRESTBaseURL + '/api/WB_Notes',
            recordClass: WBSchemaRows.WB_NoteRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Note,
            relations: WBSchemas_Other.WB_Note_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Note_User, {
            name: WBSchemas.esWB_Note_User,
            idAttribute: 'WB_NOTEUSRR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Note_User),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Note_User),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Note_User),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Note_User),
            endpoint: this.sRESTBaseURL + '/api/WB_NoteUsrRs',
            recordClass: WBSchemaRows.WB_NoteUserRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Note_User,
            relations: WBSchemas_Other.WB_Note_User_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - File ">
        // File
        this.defineMapper(WBSchemas.esWB_File, {
            name: WBSchemas.esWB_File,
            idAttribute: 'WB_FILE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_File),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_File),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_File),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_File),
            endpoint: this.sRESTBaseURL + '/api/WB_Files',
            recordClass: WBSchemaRows.WB_FileRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_File,
            relations: WBSchemas_Other.WB_File_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Cannibalization Set ">

        this.defineMapper(WBSchemas.esWB_Cannibalization_Set, {
            name: WBSchemas.esWB_Cannibalization_Set,
            idAttribute: 'WB_CANN_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Cannibalization_Set),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Cannibalization_Set),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Cannibalization_Set),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Cannibalization_Set),
            endpoint: this.sRESTBaseURL + '/api/WB_Canns',
            recordClass: WBSchemaRows.WB_CannibalizationSetRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Cannibalization_Set,
            relations: WBSchemas_PM.WB_Cannibalization_Set_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Cannibalization_Set_Customer, {
            name: WBSchemas.esWB_Cannibalization_Set_Customer,
            idAttribute: 'WB_CANNCST_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Cannibalization_Set_Customer),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Cannibalization_Set_Customer),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Cannibalization_Set_Customer),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Cannibalization_Set_Customer),
            endpoint: this.sRESTBaseURL + '/api/WB_CannCsts',
            recordClass: WBSchemaRows.WB_CannibalizationSetCustomerRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Cannibalization_Set_Customer,
            relations: WBSchemas_PM.WB_Cannibalization_Set_Customer_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Cannibalization_Set_Product_Group, {
            name: WBSchemas.esWB_Cannibalization_Set_Product_Group,
            idAttribute: 'WB_CANNPG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Cannibalization_Set_Product_Group),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Cannibalization_Set_Product_Group),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Cannibalization_Set_Product_Group),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Cannibalization_Set_Product_Group
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_CannPGs',
            recordClass: WBSchemaRows.WB_CannibalizationSetProductGroupRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Cannibalization_Set_Product_Group,
            relations: WBSchemas_PM.WB_Cannibalization_Set_Product_Group_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Cannibalization_Set_Product, {
            name: WBSchemas.esWB_Cannibalization_Set_Product,
            idAttribute: 'WB_CANNPRD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Cannibalization_Set_Product),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Cannibalization_Set_Product),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Cannibalization_Set_Product),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Cannibalization_Set_Product),
            endpoint: this.sRESTBaseURL + '/api/WB_CannPrds',
            recordClass: WBSchemaRows.WB_CannibalizationSetProductRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Cannibalization_Set_Product,
            relations: WBSchemas_PM.WB_Cannibalization_Set_Product_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Snapshot, {
            name: WBSchemas.esWB_Snapshot,
            idAttribute: 'WB_SNAPSHOT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Snapshot),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Snapshot),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Snapshot),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Snapshot),
            endpoint: this.sRESTBaseURL + '/api/WB_Snapshots',
            recordClass: WBSchemaRows.WB_SnapshotRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Snapshot,
            relations: WBSchemas_Other.WB_Snapshot_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Snapshot_Type, {
            name: WBSchemas.esWB_Snapshot_Type,
            idAttribute: 'WB_SNAPSHOTM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Snapshot_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Snapshot_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Snapshot_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Snapshot_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_SnapshotMs',
            recordClass: WBSchemaRows.WB_Snapshot_TypeRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Snapshot_Type,
            relations: WBSchemas_Other.WB_Snapshot_Type_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Snapshot_Param, {
            name: WBSchemas.esWB_Snapshot_Param,
            idAttribute: 'WB_SNAPSHOT_PARAM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Snapshot_Param),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Snapshot_Param),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Snapshot_Param),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Snapshot_Param),
            endpoint: this.sRESTBaseURL + '/api/WB_Snapshot_Params',
            recordClass: WBSchemaRows.WB_Snapshot_ParamRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Snapshot_Param,
            relations: WBSchemas_Other.WB_Snapshot_Param_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Snapshot_ParamGLR, {
            name: WBSchemas.esWB_Snapshot_ParamGLR,
            idAttribute: 'WB_SNAPSHOT_PARAMGLR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Snapshot_ParamGLR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Snapshot_ParamGLR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Snapshot_ParamGLR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Snapshot_ParamGLR),
            endpoint: this.sRESTBaseURL + '/api/WB_Snapshot_ParamGLRs',
            recordClass: WBSchemaRows.WB_Snapshot_ParamGLRRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Snapshot_ParamGLR,
            relations: WBSchemas_Other.WB_Snapshot_ParamGLR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Snapshot_ParamGLELR, {
            name: WBSchemas.esWB_Snapshot_ParamGLELR,
            idAttribute: 'WB_SNAPSHOT_PARAMGLELR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Snapshot_ParamGLELR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Snapshot_ParamGLELR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Snapshot_ParamGLELR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Snapshot_ParamGLELR),
            endpoint: this.sRESTBaseURL + '/api/WB_Snapshot_ParamGLELRs',
            recordClass: WBSchemaRows.WB_Snapshot_ParamGLELRRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Snapshot_ParamGLELR,
            relations: WBSchemas_Other.WB_Snapshot_ParamGLELR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Snapshot_ParamSAR, {
            name: WBSchemas.esWB_Snapshot_ParamSAR,
            idAttribute: 'WB_SNAPSHOT_PARAMSAR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Snapshot_ParamSAR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Snapshot_ParamSAR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Snapshot_ParamSAR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Snapshot_ParamSAR),
            endpoint: this.sRESTBaseURL + '/api/WB_Snapshot_ParamSARs',
            recordClass: WBSchemaRows.WB_Snapshot_ParamSARRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Snapshot_ParamSAR,
            relations: WBSchemas_Other.WB_Snapshot_ParamSAR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Snapshot_ParamAMR, {
            name: WBSchemas.esWB_Snapshot_ParamAMR,
            idAttribute: 'WB_SNAPSHOT_PARAMAMR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Snapshot_ParamAMR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Snapshot_ParamAMR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Snapshot_ParamAMR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Snapshot_ParamAMR),
            endpoint: this.sRESTBaseURL + '/api/WB_Snapshot_ParamAMRs',
            recordClass: WBSchemaRows.WB_Snapshot_ParamAMRRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Snapshot_ParamAMR,
            relations: WBSchemas_Other.WB_Snapshot_ParamAMR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Snapshot_ParamCHR, {
            name: WBSchemas.esWB_Snapshot_ParamCHR,
            idAttribute: 'WB_SNAPSHOT_PARAMCHR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Snapshot_ParamCHR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Snapshot_ParamCHR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Snapshot_ParamCHR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Snapshot_ParamCHR),
            endpoint: this.sRESTBaseURL + '/api/WB_Snapshot_ParamCHRs',
            recordClass: WBSchemaRows.WB_Snapshot_ParamCHRRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Snapshot_ParamCHR,
            relations: WBSchemas_Other.WB_Snapshot_ParamCHR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Snapshot_ParamPHR, {
            name: WBSchemas.esWB_Snapshot_ParamPHR,
            idAttribute: 'WB_SNAPSHOT_PARAMPHR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Snapshot_ParamPHR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Snapshot_ParamPHR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Snapshot_ParamPHR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Snapshot_ParamPHR),
            endpoint: this.sRESTBaseURL + '/api/WB_Snapshot_ParamPHRs',
            recordClass: WBSchemaRows.WB_Snapshot_ParamPHRRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Snapshot_ParamPHR,
            relations: WBSchemas_Other.WB_Snapshot_ParamPHR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Snapshot_ParamGLCHR, {
            name: WBSchemas.esWB_Snapshot_ParamGLCHR,
            idAttribute: 'WB_SNAPSHOT_PARAMGLCHR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Snapshot_ParamGLCHR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Snapshot_ParamGLCHR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Snapshot_ParamGLCHR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Snapshot_ParamGLCHR),
            endpoint: this.sRESTBaseURL + '/api/WB_Snapshot_ParamGLCHRs',
            recordClass: WBSchemaRows.WB_Snapshot_ParamGLCHRRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Snapshot_ParamGLCHR,
            relations: WBSchemas_Other.WB_Snapshot_ParamGLCHR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Snapshot_ParamGLPHR, {
            name: WBSchemas.esWB_Snapshot_ParamGLPHR,
            idAttribute: 'WB_SNAPSHOT_PARAMGLPHR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Snapshot_ParamGLPHR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Snapshot_ParamGLPHR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Snapshot_ParamGLPHR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Snapshot_ParamGLPHR),
            endpoint: this.sRESTBaseURL + '/api/WB_Snapshot_ParamGLPHRs',
            recordClass: WBSchemaRows.WB_Snapshot_ParamGLPHRRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Snapshot_ParamGLPHR,
            relations: WBSchemas_Other.WB_Snapshot_ParamGLPHR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Snapshot_ParamPlnR, {
            name: WBSchemas.esWB_Snapshot_ParamPlnR,
            idAttribute: 'WB_SNAPSHOT_PARAMPLNR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Snapshot_ParamPlnR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Snapshot_ParamPlnR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Snapshot_ParamPlnR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Snapshot_ParamPlnR),
            endpoint: this.sRESTBaseURL + '/api/WB_Snapshot_ParamPlnRs',
            recordClass: WBSchemaRows.WB_Snapshot_ParamPlnRRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Snapshot_ParamPlnR,
            relations: WBSchemas_Other.WB_Snapshot_ParamPlnR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Snapshot_ParamETR, {
            name: WBSchemas.esWB_Snapshot_ParamETR,
            idAttribute: 'WB_SNAPSHOT_PARAMETR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Snapshot_ParamETR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Snapshot_ParamETR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Snapshot_ParamETR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Snapshot_ParamETR),
            endpoint: this.sRESTBaseURL + '/api/WB_Snapshot_ParamETRs',
            recordClass: WBSchemaRows.WB_Snapshot_ParamETRRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Snapshot_ParamETR,
            relations: WBSchemas_Other.WB_Snapshot_ParamETR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Snapshot_Msar, {
            name: WBSchemas.esWB_Snapshot_Msar,
            idAttribute: 'WB_SNAPSHOTMSAR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Snapshot_Msar),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Snapshot_Msar),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Snapshot_Msar),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Snapshot_Msar),
            endpoint: this.sRESTBaseURL + '/api/WB_SnapshotMSARs',
            recordClass: WBSchemaRows.WB_SnapshotTypeSubjectAreaRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_SnapshotMSAR,
            relations: WBSchemas_Other.WB_SnapshotMSAR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Snapshot_Customer_Relationship, {
            name: WBSchemas.esWB_Snapshot_Customer_Relationship,
            idAttribute: 'WB_SNAPSHOT_CUSTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Snapshot_Customer_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Snapshot_Customer_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Snapshot_Customer_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Snapshot_Customer_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Snapshot_CustRs',
            recordClass: WBSchemaRows.WB_SnapshotCustomerRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Snapshot_Customer_Relationship,
            relations: WBSchemas_Other.WB_Snapshot_Customer_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Snapshot_ProductGroup_Relationship, {
            name: WBSchemas.esWB_Snapshot_ProductGroup_Relationship,
            idAttribute: 'WB_SNAPSHOT_PRDR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Snapshot_ProductGroup_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Snapshot_ProductGroup_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Snapshot_ProductGroup_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Snapshot_ProductGroup_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Snapshot_PrdRs',
            recordClass: WBSchemaRows.WB_SnapshotProductGroupRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Snapshot_ProductGroup_Relationship,
            relations: WBSchemas_Other.WB_Snapshot_ProductGroup_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Snapshot_Period_Relationship, {
            name: WBSchemas.esWB_Snapshot_Period_Relationship,
            idAttribute: 'WB_SNAPSHOT_PERIODR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Snapshot_Period_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Snapshot_Period_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Snapshot_Period_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Snapshot_Period_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_Snapshot_PeriodRs',
            recordClass: WBSchemaRows.WB_SnapshotPeriodRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Snapshot_Period_Relationship,
            relations: WBSchemas_Other.WB_Snapshot_Period_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Snapshot_Plan_Relationship, {
            name: WBSchemas.esWB_Snapshot_Plan_Relationship,
            idAttribute: 'WB_SNAPSHOT_PLANR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Snapshot_Plan_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Snapshot_Plan_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Snapshot_Plan_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Snapshot_Plan_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_Snapshot_PlanRs',
            recordClass: WBSchemaRows.WB_SnapshotPlanRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Snapshot_Plan_Relationship,
            relations: WBSchemas_Other.WB_Snapshot_Plan_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Plan_Volume_Metrics_Snapshot, {
            name: WBSchemas.esWB_Plan_Volume_Metrics_Snapshot,
            idAttribute: 'WB_PLANVMTS_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_Volume_Metrics_Snapshot),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_Volume_Metrics_Snapshot),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_Volume_Metrics_Snapshot),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_Volume_Metrics_Snapshot),
            endpoint: this.sRESTBaseURL + '/api/WB_PlanVMtSes',
            recordClass: WBSchemaRows.WB_PlanVolumeMetricsSnapshotRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_Volume_Metrics_Snapshot,
            relations: WBSchemas_Other.WB_Plan_Volume_Metrics_Snapshot_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Event_Type, {
            name: WBSchemas.esWB_Event_Type,
            idAttribute: 'WB_EVTTYPE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtTypes',
            recordClass: WBSchemaRows.WB_EventTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Event_Type,
            relations: WBSchemas_Other.WB_Event_Type_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_General_Ledger, {
            name: WBSchemas.esWB_General_Ledger,
            idAttribute: 'WB_GL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_General_Ledger),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_General_Ledger),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_General_Ledger),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_General_Ledger),
            endpoint: this.sRESTBaseURL + '/api/WB_GLs',
            recordClass: WBSchemaRows.WB_GeneralLedgerRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_General_Ledger,
            relations: WBSchemas_Other.WB_General_Ledger_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_General_Ledger_Account, {
            name: WBSchemas.esWB_General_Ledger_Account,
            idAttribute: 'WB_GLACCT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_General_Ledger_Account),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_General_Ledger_Account),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_General_Ledger_Account),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_General_Ledger_Account),
            endpoint: this.sRESTBaseURL + '/api/WB_GLAccts',
            recordClass: WBSchemaRows.WB_GeneralLedgerAccountRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_General_Ledger_Acct,
            relations: WBSchemas_Other.WB_General_Ledger_Account_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_General_Ledger_Policy, {
            name: WBSchemas.esWB_General_Ledger_Policy,
            idAttribute: 'WB_GLRL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_General_Ledger_Policy),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_General_Ledger_Policy),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_General_Ledger_Policy),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_General_Ledger_Policy),
            endpoint: this.sRESTBaseURL + '/api/WB_GLRLs',
            recordClass: WBSchemaRows.WB_GeneralLedgerPolicyRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_General_Ledger_Rule,
            relations: WBSchemas_Other.WB_General_Ledger_Policy_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_General_Ledger_Policy_Account, {
            name: WBSchemas.esWB_General_Ledger_Policy_Account,
            idAttribute: 'WB_GLRLACCT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_General_Ledger_Policy_Account),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_General_Ledger_Policy_Account),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_General_Ledger_Policy_Account),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_General_Ledger_Policy_Account),
            endpoint: this.sRESTBaseURL + '/api/WB_GLRLAccts',
            recordClass: WBSchemaRows.WB_GeneralLedgerPolicyAccountRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_General_Ledger_Rule_Acct,
            relations: WBSchemas_Other.WB_General_Ledger_Policy_Account_JSData_RELs,
            debug: bDebug
        })

        //Funding Account
        this.defineMapper(WBSchemas.esWB_Funding_Account, {
            name: WBSchemas.esWB_Funding_Account,
            idAttribute: 'WB_FUNDINGACCOUNT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Funding_Account),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Funding_Account),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Funding_Account),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Funding_Account),
            endpoint: this.sRESTBaseURL + '/api/WB_FundingAccounts',
            recordClass: WBSchemaRows.WB_FundingAccountRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Funding_Account,
            relations: WBSchemas_Other.WB_Funding_Account_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Fund_Type, {
            name: WBSchemas.esWB_Fund_Type,
            idAttribute: 'WB_FUNDTYPE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Fund_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Fund_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Fund_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Fund_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_FundTypes',
            recordClass: WBSchemaRows.WB_FundTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Fund_Type,
            relations: WBSchemas_Other.WB_Fund_Type_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Transaction_Type, {
            name: WBSchemas.esWB_Transaction_Type,
            idAttribute: 'WB_TXNTYPE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Transaction_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Transaction_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Transaction_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Transaction_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_TxnTypes',
            recordClass: WBSchemaRows.WB_TransactionTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Transaction_Type,
            relations: WBSchemas_Other.WB_Transaction_Type_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Transaction, {
            name: WBSchemas.esWB_Transaction,
            idAttribute: 'WB_TXN_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Transaction),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Transaction),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Transaction),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Transaction),
            endpoint: this.sRESTBaseURL + '/api/WB_Txns',
            recordClass: WBSchemaRows.WB_TransactionRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Transaction,
            relations: WBSchemas_Other.WB_Transaction_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Transaction_Item, {
            name: WBSchemas.esWB_Transaction_Item,
            idAttribute: 'WB_TXNITEM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Transaction_Item),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Transaction_Item),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Transaction_Item),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Transaction_Item),
            endpoint: this.sRESTBaseURL + '/api/WB_TxnItems',
            recordClass: WBSchemaRows.WB_TransactionItemRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Transaction_Item,
            relations: WBSchemas_Other.WB_Transaction_Item_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_GLTransaction, {
            name: WBSchemas.esWB_GLTransaction,
            idAttribute: 'WB_GLTXN_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_GLTransaction),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_GLTransaction),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_GLTransaction),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_GLTransaction),
            endpoint: this.sRESTBaseURL + '/api/WB_GLTxns',
            recordClass: WBSchemaRows.WB_GLTransactionRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_GLTransaction,
            relations: WBSchemas_Other.WB_GLTransaction_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_GLTransaction_Item, {
            name: WBSchemas.esWB_GLTransaction_Item,
            idAttribute: 'WB_GLTXN_ITEM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_GLTransaction_Item),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_GLTransaction_Item),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_GLTransaction_Item),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_GLTransaction_Item),
            endpoint: this.sRESTBaseURL + '/api/WB_GLTxn_Items',
            recordClass: WBSchemaRows.WB_GLTransactionItemRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_GLTransaction_Item,
            relations: WBSchemas_Other.WB_GLTransaction_Item_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_GLAccount_Balance, {
            name: WBSchemas.esWB_GLAccount_Balance,
            idAttribute: 'WB_GLACCT_BAL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_GLAccount_Balance),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_GLAccount_Balance),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_GLAccount_Balance),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_GLAccount_Balance),
            endpoint: this.sRESTBaseURL + '/api/WB_GLAcct_Bals',
            recordClass: WBSchemaRows.WB_GLAccountBalRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_GLAccount_Balance,
            relations: WBSchemas_Other.WB_GLAccount_Balance_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Workflow, {
            name: WBSchemas.esWB_Workflow,
            idAttribute: 'WB_WORKFLOW_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Workflow),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Workflow),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Workflow),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Workflow),
            endpoint: this.sRESTBaseURL + '/api/WB_Workflows',
            recordClass: WBSchemaRows.WB_WorkflowRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Workflow,
            relations: WBSchemas_Other.WB_Workflow_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Workflow_Status, {
            name: WBSchemas.esWB_Workflow_Status,
            idAttribute: 'WB_STATUS_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Workflow_Status),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Workflow_Status),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Workflow_Status),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Workflow_Status),
            endpoint: this.sRESTBaseURL + '/api/WB_Statuses',
            recordClass: WBSchemaRows.WB_WorkflowStatusRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Workflow_Status,
            relations: WBSchemas_Other.WB_Workflow_Status_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Status_Relationship, {
            name: WBSchemas.esWB_Status_Relationship,
            idAttribute: 'WB_STATUSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Status_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Status_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Status_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Status_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_StatusRs',
            recordClass: WBSchemaRows.WB_WorkflowStatusRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Status_Relationship,
            relations: WBSchemas_Other.WB_Status_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_BaseLn, {
            name: WBSchemas.esWB_BaseLn,
            idAttribute: 'WB_BASELINE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_BaseLn),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_BaseLn),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_BaseLn),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_BaseLn),
            endpoint: this.sRESTBaseURL + '/api/WB_BaseLns',
            recordClass: WBSchemaRows.WB_BaseLnRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_BaseLn,
            relations: WBSchemas_Other.WB_BaseLn_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_BaseLnMt, {
            name: WBSchemas.esWB_BaseLnMt,
            idAttribute: 'WB_BASELINE_METRIC_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_BaseLnMt),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_BaseLnMt),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_BaseLnMt),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_BaseLnMt),
            endpoint: this.sRESTBaseURL + '/api/WB_BaseLnMts',
            recordClass: WBSchemaRows.WB_BaseLnMtRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_BaseLnMt,
            relations: WBSchemas_Other.WB_BaseLnMt_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Baseline, {
            name: WBSchemas.esWB_Baseline,
            idAttribute: 'WB_BASELINE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Baseline),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Baseline),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Baseline),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Baseline),
            endpoint: this.sRESTBaseURL + '/api/WB_Baselines',
            recordClass: WBSchemaRows.WB_BaselineRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Baseline,
            relations: WBSchemas_Other.WB_Baseline_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Baseline_Metrics, {
            name: WBSchemas.esWB_Baseline_Metrics,
            idAttribute: 'WB_BASELINEMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Baseline_Metrics),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Baseline_Metrics),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Baseline_Metrics),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Baseline_Metrics),
            endpoint: this.sRESTBaseURL + '/api/WB_BaselineMTs',
            recordClass: WBSchemaRows.WB_BaselineMetricsRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Baseline_Metrics,
            relations: WBSchemas_Other.WB_Baseline_Metrics_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Plan_Management, {
            name: WBSchemas.esWB_Plan_Management,
            idAttribute: 'WB_PLNMGMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_Management),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_Management),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_Management),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_Management),
            endpoint: this.sRESTBaseURL + '/api/WB_PlnMgmts',
            recordClass: WBSchemaRows.WB_PlanManagementRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_Management,
            relations: WBSchemas_Other.WB_Plan_Management_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Plan_Management_Period, {
            name: WBSchemas.esWB_Plan_Management_Period,
            idAttribute: 'WB_PLNMGMTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_Management_Period),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_Management_Period),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_Management_Period),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_Management_Period),
            endpoint: this.sRESTBaseURL + '/api/WB_PlnMgmtRs',
            recordClass: WBSchemaRows.WB_PlanManagementPeriodRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_Management_Period,
            relations: WBSchemas_Other.WB_Plan_Management_Period_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Plan_Management_User, {
            name: WBSchemas.esWB_Plan_Management_User,
            idAttribute: 'WB_PLNMGMTU_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_Management_User),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_Management_User),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_Management_User),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_Management_User),
            endpoint: this.sRESTBaseURL + '/api/WB_PlnMgmtUs',
            recordClass: WBSchemaRows.WB_PlanManagementUserRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_Management_User,
            relations: WBSchemas_Other.WB_Plan_Management_User_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Plan_Management_User_Group, {
            name: WBSchemas.esWB_Plan_Management_User_Group,
            idAttribute: 'WB_PLNMGMTG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Plan_Management_User_Group),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Plan_Management_User_Group),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Plan_Management_User_Group),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Plan_Management_User_Group),
            endpoint: this.sRESTBaseURL + '/api/WB_PlnMgmtGs',
            recordClass: WBSchemaRows.WB_PlanManagementUserGroupRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Plan_Management_User_Group,
            relations: WBSchemas_Other.WB_Plan_Management_User_Group_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Workflow History ">
        // Workflow Status History
        this.defineMapper(WBSchemas.esWB_Status_History, {
            name: WBSchemas.esWB_Status_History,
            idAttribute: 'WB_STATUSH_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Status_History),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Status_History),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Status_History),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Status_History),
            endpoint: this.sRESTBaseURL + '/api/WB_StatusHs',
            recordClass: WBSchemaRows.WB_WorflowStatusHistoryRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Status_History,
            relations: WBSchemas_Other.WB_Status_History_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Status_History_Log, {
            name: WBSchemas.esWB_Status_History_Log,
            idAttribute: 'WB_STATUSHL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Status_History_Log),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Status_History_Log),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Status_History_Log),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Status_History_Log),
            endpoint: this.sRESTBaseURL + '/api/WB_StatusHLs',
            recordClass: WBSchemaRows.WB_WorflowStatusHistoryLogRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Status_History_Log,
            relations: WBSchemas_Other.WB_Status_History_Log_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - User Reviewer ">

        this.defineMapper(WBSchemas.esWB_User_Reviewers, {
            name: WBSchemas.esWB_User_Reviewers,
            idAttribute: 'WB_USRRVWR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_User_Reviewers),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_User_Reviewers),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_User_Reviewers),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_User_Reviewers),
            endpoint: this.sRESTBaseURL + '/api/WB_UsrRvwrs',
            recordClass: WBSchemaRows.WB_UserReviewerRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_User_Reviewer,
            relations: WBSchemas_Other.WB_User_Reviewer_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Event Spend Type ">

        // Plan Event Spend Type
        this.defineMapper(WBSchemas.esWB_Event_Spend_Type_Relationship, {
            name: WBSchemas.esWB_Event_Spend_Type_Relationship,
            idAttribute: 'WB_EVTSTYPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Event_Spend_Type_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Event_Spend_Type_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Event_Spend_Type_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Event_Spend_Type_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtSTypeRs',
            recordClass: WBSchemaRows.WB_EventSpendTypeRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_TPM_Event_Spend_Type_Relationship,
            relations: WBSchemas_Other.WB_TPM_Event_Spend_Type_Relationship_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - User Groups ">

        this.defineMapper(WBSchemas.esWB_User_Group, {
            name: WBSchemas.esWB_User_Group,
            idAttribute: 'WB_USRGRP_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_User_Group),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_User_Group),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_User_Group),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_User_Group),
            endpoint: this.sRESTBaseURL + '/api/WB_UsrGrps',
            recordClass: WBSchemaRows.WB_UserGroupRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_User_Group,
            relations: WBSchemas_Other.WB_User_Group_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_User_Group_Customer, {
            name: WBSchemas.esWB_User_Group_Customer,
            idAttribute: 'WB_USRGRPCR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_User_Group_Customer),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_User_Group_Customer),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_User_Group_Customer),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_User_Group_Customer),
            endpoint: this.sRESTBaseURL + '/api/WB_UsrGrpCRs',
            recordClass: WBSchemaRows.WB_UserGroupCustomerRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_User_Group_CR,
            relations: WBSchemas_Other.WB_User_Group_CR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_User_Group_Product, {
            name: WBSchemas.esWB_User_Group_Product,
            idAttribute: 'WB_USRGRPPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_User_Group_Product),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_User_Group_Product),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_User_Group_Product),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_User_Group_Product),
            endpoint: this.sRESTBaseURL + '/api/WB_UsrGrpPRs',
            recordClass: WBSchemaRows.WB_UserGroupProductRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_User_Group_PR,
            relations: WBSchemas_Other.WB_User_Group_PR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_User_Group_User, {
            name: WBSchemas.esWB_User_Group_User,
            idAttribute: 'WB_USRGRPUR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_User_Group_User),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_User_Group_User),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_User_Group_User),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_User_Group_User),
            endpoint: this.sRESTBaseURL + '/api/WB_UsrGrpURs',
            recordClass: WBSchemaRows.WB_UserGroupUserRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_User_Group_UR,
            relations: WBSchemas_Other.WB_User_Group_UR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_User_Group_Role, {
            name: WBSchemas.esWB_User_Group_Role,
            idAttribute: 'WB_USRGRPRR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_User_Group_Role),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_User_Group_Role),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_User_Group_Role),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_User_Group_Role),
            endpoint: this.sRESTBaseURL + '/api/WB_UsrGrpRRs',
            recordClass: WBSchemaRows.WB_UserGroupRoleRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_User_Group_RR,
            relations: WBSchemas_Other.WB_User_Group_RR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_User_Group_Relationship, {
            name: WBSchemas.esWB_User_Group_Relationship,
            idAttribute: 'WB_USRGRPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_User_Group_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_User_Group_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_User_Group_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_User_Group_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_UsrGrpRs',
            recordClass: WBSchemaRows.WB_UserGroupRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_Security.WB_User_Group_Relationship,
            relations: WBSchemas_Security.WB_User_Group_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_User_Group_SalesArea, {
            name: WBSchemas.esWB_User_Group_SalesArea,
            idAttribute: 'WB_USRGRPSAR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_User_Group_SalesArea),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_User_Group_SalesArea),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_User_Group_SalesArea),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_User_Group_SalesArea),
            endpoint: this.sRESTBaseURL + '/api/WB_UsrGrpSARs',
            recordClass: WBSchemaRows.WB_UserGroupSalesAreaRow,
            applySchema: applySchema,
            schema: WBSchemas_Security.WB_User_Group_SalesArea,
            relations: WBSchemas_Security.WB_User_Group_SalesArea_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_User_Group_Type, {
            name: WBSchemas.esWB_User_Group_Type,
            idAttribute: 'WB_USRGRPM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_User_Group_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_User_Group_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_User_Group_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_User_Group_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_UsrGrpMs',
            recordClass: WBSchemaRows.WB_UserGroupTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_Security.WB_User_Group_Type,
            relations: WBSchemas_Security.WB_User_Group_Type_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_User_Group_Hierarchy, {
            name: WBSchemas.esWB_User_Group_Hierarchy,
            idAttribute: 'WB_USRGRPH_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_User_Group_Hierarchy),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_User_Group_Hierarchy),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_User_Group_Hierarchy),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_User_Group_Hierarchy),
            endpoint: this.sRESTBaseURL + '/api/WB_UsrGrpHs',
            recordClass: WBSchemaRows.WB_UserGroupHierarchyRow,
            applySchema: applySchema,
            schema: WBSchemas_Security.WB_User_Group_Hierarchy,
            relations: WBSchemas_Security.WB_User_Group_Hierarchy_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold desc="Define Mappers - Metric Definition ">

        this.defineMapper(WBSchemas.esWB_Metric_Definition, {
            name: WBSchemas.esWB_Metric_Definition,
            idAttribute: 'WB_MTRDFN_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Metric_Definition),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Metric_Definition),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Metric_Definition),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Metric_Definition),
            endpoint: this.sRESTBaseURL + '/api/WB_MtrDfns',
            recordClass: WBSchemaRows.WB_MetricDefinitionRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Metric_Definition,
            relations: WBSchemas_Other.WB_Metric_Definition_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_MetricDefinition_GLAccount, {
            name: WBSchemas.esWB_MetricDefinition_GLAccount,
            idAttribute: 'WB_MTRDFNGLR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_MetricDefinition_GLAccount),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_MetricDefinition_GLAccount),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_MetricDefinition_GLAccount),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_MetricDefinition_GLAccount),
            endpoint: this.sRESTBaseURL + '/api/WB_MtrDfnGLRs',
            recordClass: WBSchemaRows.WB_MetricDefinitionGLAccountRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_MetricDefinition_GLAccount,
            relations: WBSchemas_Other.WB_MetricDefinition_GLAccount_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_MetricDefinition_EventSpend_Type, {
            name: WBSchemas.esWB_MetricDefinition_EventSpend_Type,
            idAttribute: 'WB_MTRDFNEVTSTYPER_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_MetricDefinition_EventSpend_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_MetricDefinition_EventSpend_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_MetricDefinition_EventSpend_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_MetricDefinition_EventSpend_Type
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_MtrDfnEvtSTypeRs',
            recordClass: WBSchemaRows.WB_MetricDefinitionEventSpendRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_MetricDefinition_EventSpend_Type,
            relations: WBSchemas_Other.WB_MetricDefinition_EventSpend_Type_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define CubeSource">

        this.defineMapper(WBSchemas.esWB_CubeSource, {
            name: WBSchemas.esWB_CubeSource,
            idAttribute: 'WB_CUBESOURCE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_CubeSource),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_CubeSource),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_CubeSource),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_CubeSource),
            endpoint: this.sRESTBaseURL + '/api/WB_CubeSources',
            recordClass: WBSchemaRows.WB_CubeSourceRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_CubeSource,
            relations: WBSchemas_Other.WB_CubeSource_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_CubeSource_Dimension, {
            name: WBSchemas.esWB_CubeSource_Dimension,
            idAttribute: 'WB_CUBESOURCEDR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_CubeSource_Dimension),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_CubeSource_Dimension),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_CubeSource_Dimension),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_CubeSource_Dimension),
            endpoint: this.sRESTBaseURL + '/api/WB_CubeSourceDRs',
            recordClass: WBSchemaRows.WB_CubeSourceDimensionRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_CubeSource_Dimension,
            relations: WBSchemas_Other.WB_CubeSource_Dimension_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_CubeSource_DataSource, {
            name: WBSchemas.esWB_CubeSource_DataSource,
            idAttribute: 'WB_CUBESOURCEDSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_CubeSource_DataSource),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_CubeSource_DataSource),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_CubeSource_DataSource),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_CubeSource_DataSource),
            endpoint: this.sRESTBaseURL + '/api/WB_CubeSourceDSRs',
            recordClass: WBSchemaRows.WB_CubeSourceDataSourceRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_CubeSource_DataSource,
            relations: WBSchemas_Other.WB_CubeSource_DataSource_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_CubeSource_DataSource_Dimension, {
            name: WBSchemas.esWB_CubeSource_DataSource_Dimension,
            idAttribute: 'WB_CUBESOURCEDSDR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_CubeSource_DataSource_Dimension),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_CubeSource_DataSource_Dimension),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_CubeSource_DataSource_Dimension),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_CubeSource_DataSource_Dimension
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_CubeSourceDSDRs',
            recordClass: WBSchemaRows.WB_CubeSourceDataSourceDimensionRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_CubeSource_DataSource_Dimension,
            relations: WBSchemas_Other.WB_CubeSource_DataSource_Dimension_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_CubeSource_Perspective, {
            name: WBSchemas.esWB_CubeSource_Perspective,
            idAttribute: 'WB_CUBESOURCEPVR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_CubeSource_Perspective),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_CubeSource_Perspective),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_CubeSource_Perspective),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_CubeSource_Perspective),
            endpoint: this.sRESTBaseURL + '/api/WB_CubeSourcePVRs',
            recordClass: WBSchemaRows.WB_CubeSourcePerspectiveRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_CubeSource_Perspective,
            relations: WBSchemas_Other.WB_CubeSource_Perspective_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Metric View Definition ">

        this.defineMapper(WBSchemas.esWB_Metric_View, {
            name: WBSchemas.esWB_Metric_View,
            idAttribute: 'WB_MTRVIEW_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Metric_View),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Metric_View),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Metric_View),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Metric_View),
            endpoint: this.sRESTBaseURL + '/api/WB_MtrViews',
            recordClass: WBSchemaRows.WB_MetricViewRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Metric_View,
            relations: WBSchemas_Other.WB_Metric_View_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Metric_View_Metric, {
            name: WBSchemas.esWB_Metric_View_Metric,
            idAttribute: 'WB_MTRVIEWR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Metric_View_Metric),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Metric_View_Metric),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Metric_View_Metric),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Metric_View_Metric),
            endpoint: this.sRESTBaseURL + '/api/WB_MtrViewRs',
            recordClass: WBSchemaRows.WB_MetricViewMetricRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Metric_View_Metric,
            relations: WBSchemas_Other.WB_Metric_View_Metric_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Timing ">

        this.defineMapper(WBSchemas.esWB_Timing, {
            name: WBSchemas.esWB_Timing,
            idAttribute: 'WB_TIMING_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Timing),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Timing),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Timing),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Timing),
            endpoint: this.sRESTBaseURL + '/api/WB_Timings',
            recordClass: WBSchemaRows.WB_TimingRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Timing,
            relations: WBSchemas_Other.WB_Timing_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Report">
        // Report
        this.defineMapper(WBSchemas.esWB_Report, {
            name: WBSchemas.esWB_Report,
            idAttribute: 'WB_REPORT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Report),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Report),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Report),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Report),
            endpoint: this.sRESTBaseURL + '/api/WB_Reports',
            recordClass: WBSchemaRows.WB_ReportRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Report,
            relations: WBSchemas_Other.WB_Report_JSData_RELs,
            debug: bDebug
        })

        // Report Metric
        this.defineMapper(WBSchemas.esWB_Report_Metric, {
            name: WBSchemas.esWB_Report_Metric,
            idAttribute: 'WB_REPORTMETRICR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Report_Metric),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Report_Metric),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Report_Metric),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Report_Metric),
            endpoint: this.sRESTBaseURL + '/api/WB_ReportMetricRs',
            recordClass: WBSchemaRows.WB_ReportMetricRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Report_Metric,
            relations: WBSchemas_Other.WB_Report_Metric_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Report_Export, {
            name: WBSchemas.esWB_Report_Export,
            idAttribute: 'WB_REPORT_EXPORT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Report_Export),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Report_Export),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Report_Export),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Report_Export),
            endpoint: this.sRESTBaseURL + '/api/WB_Report_Exports',
            recordClass: WBSchemaRows.WB_ReportExportRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Report_Export,
            relations: WBSchemas_Other.WB_Report_Export_Relationship_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold desc="Define Mappers - Model - Reference ">

        // Model Predictor
        this.defineMapper(WBSchemas.esWB_Model_Predictor, {
            name: WBSchemas.esWB_Model_Predictor,
            idAttribute: 'WB_MODEL_PREDICTOR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Predictor),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Predictor),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Predictor),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Predictor),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Predictors',
            recordClass: WBSchemaRows.WB_ModelPredictorRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Predictor,
            relations: WBSchemas_PM.WB_Model_Predictor_JSData_RELs,
            debug: bDebug
        })

        // Model Function
        this.defineMapper(WBSchemas.esWB_Model_Function, {
            name: WBSchemas.esWB_Model_Function,
            idAttribute: 'WB_MODEL_FUNCTION_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Function),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Function),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Function),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Function),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Functions',
            recordClass: WBSchemaRows.WB_ModelFunctionRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Function,
            relations: WBSchemas_PM.WB_Model_Function_JSData_RELs,
            debug: bDebug
        })

        // Model Constant
        this.defineMapper(WBSchemas.esWB_Model_Constant, {
            name: WBSchemas.esWB_Model_Constant,
            idAttribute: 'WB_MODEL_CONSTANT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Constant),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Constant),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Constant),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Constant),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Constants',
            recordClass: WBSchemaRows.WB_ModelConstantRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Constant,
            relations: WBSchemas_PM.WB_Model_Constant_JSData_RELs,
            debug: bDebug
        })

        // Model Term
        this.defineMapper(WBSchemas.esWB_Model_Term, {
            name: WBSchemas.esWB_Model_Term,
            idAttribute: 'WB_MODEL_TERM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Term),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Term),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Term),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Term),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Terms',
            recordClass: WBSchemaRows.WB_ModelTermRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Term,
            relations: WBSchemas_PM.WB_Model_Term_JSData_RELs,
            debug: bDebug
        })

        // Model Volume Component
        this.defineMapper(WBSchemas.esWB_Model_Volume_Component, {
            name: WBSchemas.esWB_Model_Volume_Component,
            idAttribute: 'WB_MODEL_VOLUME_COMPONENT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Volume_Component),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Volume_Component),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Volume_Component),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Volume_Component),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Volume_Components',
            recordClass: WBSchemaRows.WB_ModelVolumeComponentRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Volume_Component,
            relations: WBSchemas_PM.WB_Model_Volume_Component_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Model Volume Decomp">
        // Model Volume ComponentR
        this.defineMapper(WBSchemas.esWB_Model_Volume_ComponentR, {
            name: WBSchemas.esWB_Model_Volume_ComponentR,
            idAttribute: 'WB_MODEL_VOLUME_COMPONENTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Volume_ComponentR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Volume_ComponentR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Volume_ComponentR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Volume_ComponentR),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Volume_ComponentRs',
            recordClass: WBSchemaRows.WB_ModelVolumeComponentRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Volume_Component_Relationship,
            relations: WBSchemas_PM.WB_Model_Volume_Component_Relationship_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold desc="Define Mappers - Model Volume Decomp">
        // Model Volume Component Item R
        this.defineMapper(WBSchemas.esWB_Model_Volume_Component_ItemR, {
            name: WBSchemas.esWB_Model_Volume_Component_ItemR,
            idAttribute: 'WB_MODEL_VOLUME_COMPONENT_ITEMR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Volume_Component_ItemR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Volume_Component_ItemR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Volume_Component_ItemR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Volume_Component_ItemR),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Volume_Component_ItemRs',
            recordClass: WBSchemaRows.WB_ModelVolumeComponentItemRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Volume_Component_ItemR,
            relations: WBSchemas_PM.WB_Model_Volume_Component_ItemR_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold desc="Define Mappers - Model">
        // Model
        this.defineMapper(WBSchemas.esWB_Model, {
            name: WBSchemas.esWB_Model,
            idAttribute: 'WB_MODEL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model),
            endpoint: this.sRESTBaseURL + '/api/WB_Models',
            recordClass: WBSchemaRows.WB_ModelRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model,
            relations: WBSchemas_PM.WB_Model_JSData_RELs,
            debug: bDebug
        })

        // Model Input
        this.defineMapper(WBSchemas.esWB_Model_Input, {
            name: WBSchemas.esWB_Model_Input,
            idAttribute: 'WB_MODEL_INPUT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Input),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Input),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Input),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Input),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Inputs',
            recordClass: WBSchemaRows.WB_ModelRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Input,
            relations: WBSchemas_PM.WB_Model_Input_JSData_RELs,
            debug: bDebug
        })

        // Model TagR
        this.defineMapper(WBSchemas.esWB_Model_Tag_Relationship, {
            name: WBSchemas.esWB_Model_Tag_Relationship,
            idAttribute: 'WB_MODEL_TAGR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Tag_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Tag_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Tag_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Tag_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_TagRs',
            recordClass: WBSchemaRows.WB_ModelTagRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Tag_Relationship,
            relations: WBSchemas_PM.WB_Model_Tag_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Model_Term_Relationship, {
            name: WBSchemas.esWB_Model_Term_Relationship,
            idAttribute: 'WB_MODEL_TERMR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Term_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Term_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Term_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Term_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_TermRs',
            recordClass: WBSchemaRows.WB_ModelTermRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Term_Relationship,
            relations: WBSchemas_PM.WB_Model_Term_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Model_Event_Type_Relationship, {
            name: WBSchemas.esWB_Model_Event_Type_Relationship,
            idAttribute: 'WB_MODEL_EVTTYPER_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Event_Type_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Event_Type_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Event_Type_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Event_Type_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_EvtTypeRs',
            recordClass: WBSchemaRows.WB_ModelEventTypeRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Event_Type_Relationship,
            relations: WBSchemas_PM.WB_Model_Event_Type_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Model_Compare, {
            name: WBSchemas.esWB_Model_Compare,
            idAttribute: 'WB_MODELCOMPARE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Compare),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Compare),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Compare),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Compare),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Compares',
            recordClass: WBSchemaRows.WB_ModelCompareRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Compare,
            relations: WBSchemas_PM.WB_Model_Compare_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Model_Compare_Relationship, {
            name: WBSchemas.esWB_Model_Compare_Relationship,
            idAttribute: 'WB_MODELCOMPARER_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Compare_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Compare_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Compare_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Compare_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_CompareRs',
            recordClass: WBSchemaRows.WB_ModelCompareRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_CompareR,
            relations: WBSchemas_PM.WB_Model_CompareR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Model_Cannibalization_Type, {
            name: WBSchemas.esWB_Model_Cannibalization_Type,
            idAttribute: 'WB_MODEL_CANNIBALIZATIONM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Cannibalization_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Cannibalization_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Cannibalization_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Cannibalization_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_CannibalizationMs',
            recordClass: WBSchemaRows.WB_ModelCannibalizationTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Cannibalization_Type,
            relations: WBSchemas_PM.WB_Model_Cannibalization_Type_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Model_Cannibalization_Relationship, {
            name: WBSchemas.esWB_Model_Cannibalization_Relationship,
            idAttribute: 'WB_MODEL_CANNR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Cannibalization_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Cannibalization_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Cannibalization_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Model_Cannibalization_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_CannRs',
            recordClass: WBSchemaRows.WB_ModelCannibalizationRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Cannibalization_Relationship,
            relations: WBSchemas_PM.WB_Model_Cannibalization_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Model_Cannibalization_Const_Relationship, {
            name: WBSchemas.esWB_Model_Cannibalization_Const_Relationship,
            idAttribute: 'WB_MODEL_CANNCR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Cannibalization_Const_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Cannibalization_Const_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Cannibalization_Const_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Model_Cannibalization_Const_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_CannCRs',
            recordClass: WBSchemaRows.WB_ModelCannibalizationConstRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Cannibalization_Const_Relationship,
            relations: WBSchemas_PM.WB_Model_Cannibalization_Const_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Model_Cannibalization_Term_Relationship, {
            name: WBSchemas.esWB_Model_Cannibalization_Term_Relationship,
            idAttribute: 'WB_MODEL_CANNTERMR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Cannibalization_Term_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Cannibalization_Term_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Cannibalization_Term_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Model_Cannibalization_Term_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_CannTermRs',
            recordClass: WBSchemaRows.WB_ModelCannibalizationTermRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Cannibalization_Term_Relationship,
            relations: WBSchemas_PM.WB_Model_Cannibalization_Term_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Model_Predictor_Relationship, {
            name: WBSchemas.esWB_Model_Predictor_Relationship,
            idAttribute: 'WB_MODEL_PREDICTORR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Predictor_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Predictor_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Predictor_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Predictor_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_PredictorRs',
            recordClass: WBSchemaRows.WB_ModelPredictorRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Predictor_Relationship,
            relations: WBSchemas_PM.WB_Model_Predictor_Relationship_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        this.defineMapper(WBSchemas.esWB_Model_Coefficient, {
            name: WBSchemas.esWB_Model_Coefficient,
            idAttribute: 'WB_MODEL_COEFFICIENT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Coefficient),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Coefficient),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Coefficient),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Coefficient),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Coefficients',
            recordClass: WBSchemaRows.WB_ModelCoefficientRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Coefficient,
            debug: bDebug
        })

        // <editor-fold desc="Define Mappers - Model Coefficient Set">
        // Model Coefficient
        this.defineMapper(WBSchemas.esWB_Model_Coeff_Set, {
            name: WBSchemas.esWB_Model_Coeff_Set,
            idAttribute: 'WB_MODEL_COEFF_SET_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Coeff_Set),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Coeff_Set),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Coeff_Set),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Coeff_Set),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Coeff_Sets',
            recordClass: WBSchemaRows.WB_ModelCoefficientSetRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Coeff_Set,
            relations: WBSchemas_PM.WB_Model_Coeff_Set_JSData_RELs,
            debug: bDebug
        })

        // Model Coefficient SetR
        this.defineMapper(WBSchemas.esWB_Model_Coeff_SetR, {
            name: WBSchemas.esWB_Model_Coeff_SetR,
            idAttribute: 'WB_MODEL_COEFF_SETR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Coeff_SetR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Coeff_SetR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Coeff_SetR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Coeff_SetR),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Coeff_SetRs',
            recordClass: WBSchemaRows.WB_ModelCoefficientSetRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Coeff_SetR,
            relations: WBSchemas_PM.WB_Model_Coeff_SetR_JSData_RELs,
            debug: bDebug
        })

        // Model Coefficient Set CR
        this.defineMapper(WBSchemas.esWB_Model_Coeff_SetCR, {
            name: WBSchemas.esWB_Model_Coeff_SetCR,
            idAttribute: 'WB_MODEL_COEFF_SETCR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Coeff_SetCR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Coeff_SetCR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Coeff_SetCR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Coeff_SetCR),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Coeff_SetCRs',
            recordClass: WBSchemaRows.WB_ModelCoefficientSetCRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Coeff_SetCR,
            relations: WBSchemas_PM.WB_Model_Coeff_SetCR_JSData_RELs,
            debug: bDebug
        })

        // Model Coefficient SetTR
        this.defineMapper(WBSchemas.esWB_Model_Coeff_SetTR, {
            name: WBSchemas.esWB_Model_Coeff_SetTR,
            idAttribute: 'WB_MODEL_COEFF_SETTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Coeff_SetTR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Coeff_SetTR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Coeff_SetTR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Coeff_SetTR),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Coeff_SetTRs',
            recordClass: WBSchemaRows.WB_ModelCoefficientSetTRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Coeff_SetTR,
            relations: WBSchemas_PM.WB_Model_Coeff_SetTR_JSData_RELs,
            debug: bDebug
        })

        // Model Coefficient Set ASR
        this.defineMapper(WBSchemas.esWB_Model_Coeff_SetASR, {
            name: WBSchemas.esWB_Model_Coeff_SetASR,
            idAttribute: 'WB_MODEL_COEFF_SETASR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Coeff_SetASR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Coeff_SetASR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Coeff_SetASR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Coeff_SetASR),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Coeff_SetASRs',
            recordClass: WBSchemaRows.WB_ModelCoefficientSetASRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Coeff_SetASR,
            relations: WBSchemas_PM.WB_Model_Coeff_SetASR_JSData_RELs,
            debug: bDebug
        })

        // Model Coefficient Set PMR
        this.defineMapper(WBSchemas.esWB_Model_Coeff_SetPMR, {
            name: WBSchemas.esWB_Model_Coeff_SetPMR,
            idAttribute: 'WB_MODEL_COEFF_SETPMR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Coeff_SetPMR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Coeff_SetPMR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Coeff_SetPMR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Coeff_SetPMR),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Coeff_SetPMRs',
            recordClass: WBSchemaRows.WB_ModelCoefficientSetPMRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Coeff_SetPMR,
            relations: WBSchemas_PM.WB_Model_Coeff_SetPMR_JSData_RELs,
            debug: bDebug
        })

        // Model Coefficient Set RR
        this.defineMapper(WBSchemas.esWB_Model_Coeff_SetRR, {
            name: WBSchemas.esWB_Model_Coeff_SetRR,
            idAttribute: 'WB_MODEL_COEFF_SETRR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Coeff_SetRR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Coeff_SetRR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Coeff_SetRR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Coeff_SetRR),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Coeff_SetRRs',
            recordClass: WBSchemaRows.WB_ModelCoefficientSetRRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Coeff_SetRR,
            relations: WBSchemas_PM.WB_Model_Coeff_SetRR_JSData_RELs,
            debug: bDebug
        })

        // Model Coefficient
        this.defineMapper(WBSchemas.esWB_Model_Coeff_Set_Rule, {
            name: WBSchemas.esWB_Model_Coeff_Set_Rule,
            idAttribute: 'WB_MODEL_COEFF_SET_RULE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Coeff_Set_Rule),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Coeff_Set_Rule),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Coeff_Set_Rule),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Coeff_Set_Rule),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Coeff_Set_Rules',
            recordClass: WBSchemaRows.WB_ModelCoefficientSetRuleRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Coeff_Set_Rule,
            relations: WBSchemas_PM.WB_Model_Coeff_Set_Rule_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Model_Profile, {
            name: WBSchemas.esWB_Model_Profile,
            idAttribute: 'WB_MODEL_PROFILE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Profile),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Profile),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Profile),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Profile),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Profiles',
            recordClass: WBSchemaRows.WB_ModelProfileRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Profile,
            relations: WBSchemas_PM.WB_Model_Profile_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Model_Profile_Predictor, {
            name: WBSchemas.esWB_Model_Profile_Predictor,
            idAttribute: 'WB_MODEL_PROFILEPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Profile_Predictor),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Profile_Predictor),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Profile_Predictor),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Profile_Predictor),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Profile_Predictors',
            recordClass: WBSchemaRows.WB_ModelProfilePredictorRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Profile_Predictor,
            relations: WBSchemas_PM.WB_Model_Profile_Predictor_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Model_Profile_Group, {
            name: WBSchemas.esWB_Model_Profile_Group,
            idAttribute: 'WB_MODEL_PROFILEGR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Profile_Group),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Profile_Group),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Profile_Group),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Profile_Group),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Profile_Groups',
            recordClass: WBSchemaRows.WB_ModelProfileGroupRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Profile_Group,
            relations: WBSchemas_PM.WB_Model_Profile_Group_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Model_Profile_Group_Predictor, {
            name: WBSchemas.esWB_Model_Profile_Group_Predictor,
            idAttribute: 'WB_MODEL_PROFILEGPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Profile_Group_Predictor),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Profile_Group_Predictor),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Profile_Group_Predictor),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Profile_Group_Predictor),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Profile_Group_Predictors',
            recordClass: WBSchemaRows.WB_ModelProfileGroupPredictorRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Profile_Group_Predictor,
            relations: WBSchemas_PM.WB_Model_Profile_Group_Predictor_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Model_Profile_Spend_Function, {
            name: WBSchemas.esWB_Model_Profile_Spend_Function,
            idAttribute: 'WB_MODEL_PROFILESFR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Profile_Spend_Function),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Profile_Spend_Function),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Profile_Spend_Function),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Profile_Spend_Function),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Profile_Spend_Functions',
            recordClass: WBSchemaRows.WB_ModelProfileSpendFunctionRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Profile_Spend_Function,
            relations: WBSchemas_PM.WB_Model_Profile_Spend_Function_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        this.defineMapper(WBSchemas.esWB_Model_DataSource, {
            name: WBSchemas.esWB_Model_DataSource,
            idAttribute: 'WB_MODEL_DATASOURCE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_DataSource),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_DataSource),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_DataSource),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_DataSource),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_DataSources',
            recordClass: WBSchemaRows.WB_ModelDataSourceRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_DataSource,
            relations: WBSchemas_PM.WB_Model_DataSource_JSData_RELs,
            debug: bDebug
        })

        // <editor-fold desc="Define Mappers - Modeling Session">
        // Modeling
        this.defineMapper(WBSchemas.esWB_Modeling_Session, {
            name: WBSchemas.esWB_Modeling_Session,
            idAttribute: 'WB_MODELING_SESSION_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Sessions',
            recordClass: WBSchemaRows.WB_ModelingSessionRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session,
            relations: WBSchemas_PM.WB_Modeling_Session_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Stage
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Stage, {
            name: WBSchemas.esWB_Modeling_Session_Stage,
            idAttribute: 'WB_MODELING_SESSION_STAGE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Stage),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Stage),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Stage),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_Stage),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_Stages',
            recordClass: WBSchemaRows.WB_ModelingSessionStageRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Stage,
            relations: WBSchemas_PM.WB_Modeling_Session_Stage_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Stage Algorithm
        this.defineMapper(WBSchemas.esWB_Modeling_Session_StageAlgorithm, {
            name: WBSchemas.esWB_Modeling_Session_StageAlgorithm,
            idAttribute: 'WB_MODELING_SESSION_STAGEAR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_StageAlgorithm),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_StageAlgorithm),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_StageAlgorithm),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Modeling_Session_StageAlgorithm
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_StageARs',
            recordClass: WBSchemaRows.WB_ModelingSessionStageARRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_StageAR,
            relations: WBSchemas_PM.WB_Modeling_Session_StageAR_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Stage Coeff Range Policy
        this.defineMapper(WBSchemas.esWB_Modeling_Session_StageCoeffRangePolicy, {
            name: WBSchemas.esWB_Modeling_Session_StageCoeffRangePolicy,
            idAttribute: 'WB_MODELING_SESSION_STAGECRP_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_StageCoeffRangePolicy),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_StageCoeffRangePolicy),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_StageCoeffRangePolicy),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Modeling_Session_StageCoeffRangePolicy
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_StageCRPs',
            recordClass: WBSchemaRows.WB_ModelingSessionStageCRPRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_StageCRP,
            relations: WBSchemas_PM.WB_Modeling_Session_StageCRP_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Stage Coeff Range Policy Type
        this.defineMapper(WBSchemas.esWB_Modeling_Session_StageCoeffRangePolicyMeta, {
            name: WBSchemas.esWB_Modeling_Session_StageCoeffRangePolicyMeta,
            idAttribute: 'WB_MODELING_SESSION_STAGECRP_META_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_StageCoeffRangePolicyMeta),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_StageCoeffRangePolicyMeta),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_StageCoeffRangePolicyMeta),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Modeling_Session_StageCoeffRangePolicyMeta
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_StageCRP_Meta',
            recordClass: WBSchemaRows.WB_ModelingSessionStageCRPMetaRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_StageCRP_Meta,
            relations: WBSchemas_PM.WB_Modeling_Session_StageCRP_Meta_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Stage Coeff Range Policy Relationship
        this.defineMapper(WBSchemas.esWB_Modeling_Session_StageCoeffRangePolicyR, {
            name: WBSchemas.esWB_Modeling_Session_StageCoeffRangePolicyR,
            idAttribute: 'WB_MODELING_SESSION_STAGECRPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_StageCoeffRangePolicyR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_StageCoeffRangePolicyR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_StageCoeffRangePolicyR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Modeling_Session_StageCoeffRangePolicyR
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_StageCRPRs',
            recordClass: WBSchemaRows.WB_ModelingSessionStageCRPRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_StageCRPR,
            relations: WBSchemas_PM.WB_Modeling_Session_StageCRPR_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Coefficient Rule
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Coeff_Rule, {
            name: WBSchemas.esWB_Modeling_Session_Coeff_Rule,
            idAttribute: 'WB_MODELING_SESSION_COEFF_RULE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Coeff_Rule),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Coeff_Rule),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Coeff_Rule),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_Coeff_Rule),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_Coeff_Rules',
            recordClass: WBSchemaRows.WB_ModelingSessionCoeffRuleRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Coeff_Rule,
            relations: WBSchemas_PM.WB_Modeling_Session_Coeff_Rule_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Coefficient Rule R
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Coeff_RuleR, {
            name: WBSchemas.esWB_Modeling_Session_Coeff_RuleR,
            idAttribute: 'WB_MODELING_SESSION_COEFF_RULER_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Coeff_RuleR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Coeff_RuleR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Coeff_RuleR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_Coeff_RuleR),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_Coeff_RuleRs',
            recordClass: WBSchemaRows.WB_ModelingSessionCoeffRuleRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Coeff_RuleR,
            relations: WBSchemas_PM.WB_Modeling_Session_Coeff_RuleR_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Coefficient Group
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Coeff_Grp, {
            name: WBSchemas.esWB_Modeling_Session_Coeff_Grp,
            idAttribute: 'WB_MODELING_SESSION_COEFF_GRP_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Coeff_Grp),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Coeff_Grp),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Coeff_Grp),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_Coeff_Grp),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_Coeff_Grps',
            recordClass: WBSchemaRows.WB_ModelingSessionCoeffGrpRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Coeff_Grp,
            relations: WBSchemas_PM.WB_Modeling_Session_Coeff_Grp_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Coefficient Group Set
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Coeff_GrpSt, {
            name: WBSchemas.esWB_Modeling_Session_Coeff_GrpSt,
            idAttribute: 'WB_MODELING_SESSION_COEFF_GRPST_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Coeff_GrpSt),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Coeff_GrpSt),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Coeff_GrpSt),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_Coeff_GrpSt),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_Coeff_GrpSts',
            recordClass: WBSchemaRows.WB_ModelingSessionCoeffGrpStRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Coeff_GrpSt,
            relations: WBSchemas_PM.WB_Modeling_Session_Coeff_GrpSt_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Coefficient Group SetR
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Coeff_GrpStR, {
            name: WBSchemas.esWB_Modeling_Session_Coeff_GrpStR,
            idAttribute: 'WB_MODELING_SESSION_COEFF_GRPSTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Coeff_GrpStR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Coeff_GrpStR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Coeff_GrpStR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_Coeff_GrpStR),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_Coeff_GrpStRs',
            recordClass: WBSchemaRows.WB_ModelingSessionCoeffGrpStRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Coeff_GrpStR,
            relations: WBSchemas_PM.WB_Modeling_Session_Coeff_GrpStR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Modeling_Session_CustR, {
            name: WBSchemas.esWB_Modeling_Session_CustR,
            idAttribute: 'WB_MODELING_SESSION_CUSTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_CustR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_CustR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_CustR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_CustR),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_CustRs',
            recordClass: WBSchemaRows.WB_ModelingSessionCustRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_CustR,
            relations: WBSchemas_PM.WB_Modeling_Session_CustR_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session ProdR
        this.defineMapper(WBSchemas.esWB_Modeling_Session_ProdR, {
            name: WBSchemas.esWB_Modeling_Session_ProdR,
            idAttribute: 'WB_MODELING_SESSION_PRODR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_ProdR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_ProdR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_ProdR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_ProdR),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_ProdRs',
            recordClass: WBSchemaRows.WB_ModelingSessionProdRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_ProdR,
            relations: WBSchemas_PM.WB_Modeling_Session_ProdR_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Range
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Range, {
            name: WBSchemas.esWB_Modeling_Session_Range,
            idAttribute: 'WB_MODELING_SESSION_RANGE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Range),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Range),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Range),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_Range),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_Ranges',
            recordClass: WBSchemaRows.WB_ModelingSessionRangeRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Range,
            relations: WBSchemas_PM.WB_Modeling_Session_Range_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Trial
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Trial, {
            name: WBSchemas.esWB_Modeling_Session_Trial,
            idAttribute: 'WB_MODELING_SESSION_TRIAL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Trial),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Trial),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Trial),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_Trial),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_Trials',
            recordClass: WBSchemaRows.WB_ModelingSessionTrialRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Trial,
            relations: WBSchemas_PM.WB_Modeling_Session_Trial_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Trial Metrics
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Trial_Metric, {
            name: WBSchemas.esWB_Modeling_Session_Trial_Metric,
            idAttribute: 'WB_MODELING_SESSION_TRIALMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Trial_Metric),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Trial_Metric),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Trial_Metric),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_Trial_Metric),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_TrialMts',
            recordClass: WBSchemaRows.WB_ModelingSessionTrialMetricRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Trial_Metric,
            relations: WBSchemas_PM.WB_Modeling_Session_Trial_Metric_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Trial Predictor Metrics
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Trial_Predictor_Metric, {
            name: WBSchemas.esWB_Modeling_Session_Trial_Predictor_Metric,
            idAttribute: 'WB_MODELING_SESSION_TRIAL_PMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Trial_Predictor_Metric),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Trial_Predictor_Metric),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Trial_Predictor_Metric),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Modeling_Session_Trial_Predictor_Metric
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_TrialPMts',
            recordClass: WBSchemaRows.WB_ModelingSessionTrialPredictorMetricRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Trial_Pred_Metric,
            relations: WBSchemas_PM.WB_Modeling_Session_Trial_Pred_Metric_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Trial PredictorR Metrics
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Trial_PredictorR_Metric, {
            name: WBSchemas.esWB_Modeling_Session_Trial_PredictorR_Metric,
            idAttribute: 'WB_MODELING_SESSION_TRIAL_PRMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Trial_PredictorR_Metric),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Trial_PredictorR_Metric),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Trial_PredictorR_Metric),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Modeling_Session_Trial_PredictorR_Metric
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_TrialPRMts',
            recordClass: WBSchemaRows.WB_ModelingSessionTrialPredictorRMetricRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Trial_PredR_Metric,
            relations: WBSchemas_PM.WB_Modeling_Session_Trial_PredR_Metric_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Log
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Log, {
            name: WBSchemas.esWB_Modeling_Session_Log,
            idAttribute: 'WB_MODELING_SESSION_LOG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Log),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Log),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Log),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_Log),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_Logs',
            recordClass: WBSchemaRows.WB_ModelingSessionLogRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Log,
            relations: WBSchemas_PM.WB_Modeling_Session_log_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Trial Rsd Mnt
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Trial_Residual_Metric, {
            name: WBSchemas.esWB_Modeling_Session_Trial_Residual_Metric,
            idAttribute: 'WB_MODELING_SESSION_TRIALRSDMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Trial_Residual_Metric),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Trial_Residual_Metric),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Trial_Residual_Metric),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Modeling_Session_Trial_Residual_Metric
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_TrialRsdMts',
            recordClass: WBSchemaRows.WB_ModelingSessionTrialResidualMetricRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Trial_Rsd_Metric,
            relations: WBSchemas_PM.WB_Modeling_Session_Trial_Rsd_Metric_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Trial Partition Stage
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Trial_PartitionStage, {
            name: WBSchemas.esWB_Modeling_Session_Trial_PartitionStage,
            idAttribute: 'WB_MODELING_SESSION_TRIALPS_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Trial_PartitionStage),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Trial_PartitionStage),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Trial_PartitionStage),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Modeling_Session_Trial_PartitionStage
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_TrialPs',
            recordClass: WBSchemaRows.WB_ModelingSessionTrialPartitionStageRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_TrialPartitionStage,
            relations: WBSchemas_PM.WB_Modeling_Session_TrialPartitionStage_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Trial Partition Stage Submodel
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Trial_PartitionStageS, {
            name: WBSchemas.esWB_Modeling_Session_Trial_PartitionStageS,
            idAttribute: 'WB_MODELING_SESSION_TRIALPSS_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Trial_PartitionStageS),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Trial_PartitionStageS),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Trial_PartitionStageS),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Modeling_Session_Trial_PartitionStageS
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_TrialPSSes',
            recordClass: WBSchemaRows.WB_ModelingSessionTrialPartitionStageSRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_TrialPartitionStageS,
            relations: WBSchemas_PM.WB_Modeling_Session_TrialPartitionStageS_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Trial Partition Stage Submodel Log
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Trial_PartitionStageSL, {
            name: WBSchemas.esWB_Modeling_Session_Trial_PartitionStageSL,
            idAttribute: 'WB_MODELING_SESSION_TRIALPSSL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Trial_PartitionStageSL),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Trial_PartitionStageSL),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Trial_PartitionStageSL),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Modeling_Session_Trial_PartitionStageSL
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_TrialPSSLs',
            recordClass: WBSchemaRows.WB_ModelingSessionTrialPartitionStageSLRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_TrialPartitionStageSL,
            relations: WBSchemas_PM.WB_Modeling_Session_TrialPartitionStageSL_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Observation
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Observation, {
            name: WBSchemas.esWB_Modeling_Session_Observation,
            idAttribute: 'WB_MODELING_SESSION_OBS_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Observation),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Observation),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Observation),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_Observation),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_Obs',
            recordClass: WBSchemaRows.WB_ModelingSessionObservationRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Observation,
            relations: WBSchemas_PM.WB_Modeling_Session_Observation_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Tag Rule Relationship
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Tag_RuleR, {
            name: WBSchemas.esWB_Modeling_Session_Tag_RuleR,
            idAttribute: 'WB_MODELING_SESSION_TAG_RULER_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Tag_RuleR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Tag_RuleR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Tag_RuleR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_Tag_RuleR),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_Tag_RuleRs',
            recordClass: WBSchemaRows.WB_ModelingSessionTagRuleRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Tag_RuleR,
            relations: WBSchemas_PM.WB_Modeling_Session_Tag_RuleR_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Reference Predictor Relationship
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Reference_Predictor_Relationship, {
            name: WBSchemas.esWB_Modeling_Session_Reference_Predictor_Relationship,
            idAttribute: 'WB_MODELING_SESSION_REFPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Reference_Predictor_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Reference_Predictor_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(
                WBSchemas.esWB_Modeling_Session_Reference_Predictor_Relationship
            ),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Modeling_Session_Reference_Predictor_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_RefPRs',
            recordClass: WBSchemaRows.WB_ModelingSessionRefPredictorRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Reference_Predictor_Relationship,
            relations: WBSchemas_PM.WB_Modeling_Session_Reference_Predictor_Relationship_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Reference Relationship
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Reference_Relationship, {
            name: WBSchemas.esWB_Modeling_Session_Reference_Relationship,
            idAttribute: 'WB_MODELING_SESSION_REFR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Reference_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Reference_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Reference_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Modeling_Session_Reference_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_RefRs',
            recordClass: WBSchemaRows.WB_ModelingSessionRefRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Reference_Relationship,
            relations: WBSchemas_PM.WB_Modeling_Session_Reference_Relationship_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Cann Type Relationship
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Cannibalization_Type_Relationship, {
            name: WBSchemas.esWB_Modeling_Session_Cannibalization_Type_Relationship,
            idAttribute: 'WB_MODELING_SESSION_CANNMR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Cannibalization_Type_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Cannibalization_Type_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(
                WBSchemas.esWB_Modeling_Session_Cannibalization_Type_Relationship
            ),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Modeling_Session_Cannibalization_Type_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_CannMRs',
            recordClass: WBSchemaRows.WB_ModelingSessionCannMRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Cannibalization_Type_Relationship,
            relations: WBSchemas_PM.WB_Modeling_Session_Cannibalization_Type_Relationship_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Cann Power Metric Relationship
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Cannibalization_Power_Metric_Relationship, {
            name: WBSchemas.esWB_Modeling_Session_Cannibalization_Power_Metric_Relationship,
            idAttribute: 'WB_MODELING_SESSION_CANNPMR_ID',
            keyGenerator: this.getKeyGenByMapper(
                WBSchemas.esWB_Modeling_Session_Cannibalization_Power_Metric_Relationship
            ),
            recTableName: this.getTableNameByMapper(
                WBSchemas.esWB_Modeling_Session_Cannibalization_Power_Metric_Relationship
            ),
            recLogicalKeys: this.getLogicalKeysByMapper(
                WBSchemas.esWB_Modeling_Session_Cannibalization_Power_Metric_Relationship
            ),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Modeling_Session_Cannibalization_Power_Metric_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_CannPMRs',
            recordClass: WBSchemaRows.WB_ModelingSessionCannPMRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Cannibalization_Power_Metric_Relationship,
            relations: WBSchemas_PM.WB_Modeling_Session_Cannibalization_Power_Metric_Relationship_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Affinity Set Relationship
        this.defineMapper(WBSchemas.esWB_Modeling_Session_AffinitySet_Relationship, {
            name: WBSchemas.esWB_Modeling_Session_AffinitySet_Relationship,
            idAttribute: 'WB_MODELING_SESSION_ASR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_AffinitySet_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_AffinitySet_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_AffinitySet_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Modeling_Session_AffinitySet_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_ASRs',
            recordClass: WBSchemaRows.WB_ModelingSessionASRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_AffinitySet_Relationship,
            relations: WBSchemas_PM.WB_Modeling_Session_AffinitySet_Relationship_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Predictor Relationship
        this.defineMapper(WBSchemas.esWB_Modeling_SessionPR, {
            name: WBSchemas.esWB_Modeling_SessionPR,
            idAttribute: 'WB_MODELING_SESSIONPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_SessionPR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_SessionPR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_SessionPR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_SessionPR),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_SessionPRs',
            recordClass: WBSchemaRows.WB_ModelingSessionPRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_SessionPR,
            relations: WBSchemas_PM.WB_Modeling_SessionPR_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Elasticity
        this.defineMapper(WBSchemas.esWB_Modeling_Session_El, {
            name: WBSchemas.esWB_Modeling_Session_El,
            idAttribute: 'WB_MODELING_SESSION_EL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_El),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_El),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_El),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_El),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_Els',
            recordClass: WBSchemaRows.WB_ModelingSessionElRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_El,
            relations: WBSchemas_PM.WB_Modeling_Session_El_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Elasticity R
        this.defineMapper(WBSchemas.esWB_Modeling_Session_ElR, {
            name: WBSchemas.esWB_Modeling_Session_ElR,
            idAttribute: 'WB_MODELING_SESSION_ELR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_ElR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_ElR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_ElR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_ElR),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_ElRs',
            recordClass: WBSchemaRows.WB_ModelingSessionElRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_ElR,
            relations: WBSchemas_PM.WB_Modeling_Session_ElR_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Elasticity CR
        this.defineMapper(WBSchemas.esWB_Modeling_Session_ElCR, {
            name: WBSchemas.esWB_Modeling_Session_ElCR,
            idAttribute: 'WB_MODELING_SESSION_ELCR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_ElCR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_ElCR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_ElCR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_ElCR),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_ElCRs',
            recordClass: WBSchemaRows.WB_ModelingSessionElCRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_ElCR,
            relations: WBSchemas_PM.WB_Modeling_Session_ElCR_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session TermR
        this.defineMapper(WBSchemas.esWB_Modeling_Session_TermR, {
            name: WBSchemas.esWB_Modeling_Session_TermR,
            idAttribute: 'WB_MODELING_SESSION_TERMR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_TermR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_TermR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_TermR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_TermR),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_TermRs',
            recordClass: WBSchemaRows.WB_ModelingSessionTermRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_TermR,
            relations: WBSchemas_PM.WB_Modeling_Session_TermR_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session ConstantR
        this.defineMapper(WBSchemas.esWB_Modeling_Session_ConstantR, {
            name: WBSchemas.esWB_Modeling_Session_ConstantR,
            idAttribute: 'WB_MODELING_SESSION_CONSTANTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_ConstantR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_ConstantR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_ConstantR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_ConstantR),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_ConstantRs',
            recordClass: WBSchemaRows.WB_ModelingSessionConstantRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_ConstantR,
            relations: WBSchemas_PM.WB_Modeling_Session_ConstantR_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session ConstantFR
        this.defineMapper(WBSchemas.esWB_Modeling_Session_ConstantFR, {
            name: WBSchemas.esWB_Modeling_Session_ConstantFR,
            idAttribute: 'WB_MODELING_SESSION_CONSTANTFR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_ConstantFR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_ConstantFR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_ConstantFR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Modeling_Session_ConstantFR),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_ConstantFRs',
            recordClass: WBSchemaRows.WB_ModelingSessionConstantFRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_ConstantFR,
            relations: WBSchemas_PM.WB_Modeling_Session_ConstantFR_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Seasonality Metrics
        this.defineMapper(WBSchemas.esWB_Modeling_Session_SeasonalityMt, {
            name: WBSchemas.esWB_Modeling_Session_SeasonalityMt,
            idAttribute: 'WB_MODELING_SESSION_SEASONALITYMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_SeasonalityMt),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_SeasonalityMt),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_SeasonalityMt),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Modeling_Session_SeasonalityMt
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_SeasonalityMts',
            recordClass: WBSchemaRows.WB_ModelingSessionSeasonalityMtRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_SeasonalityMt,
            relations: WBSchemas_PM.WB_Modeling_Session_SeasonalityMt_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Seasonality De-promoted Metrics
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Seasonality_DepMt, {
            name: WBSchemas.esWB_Modeling_Session_Seasonality_DepMt,
            idAttribute: 'WB_MODELING_SESSION_SEASONALITY_DEPMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Seasonality_DepMt),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Seasonality_DepMt),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Seasonality_DepMt),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Modeling_Session_Seasonality_DepMt
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_Seasonality_DepMts',
            recordClass: WBSchemaRows.WB_ModelingSessionSeasonalityDepMtRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Seasonality_DepMt,
            relations: WBSchemas_PM.WB_Modeling_Session_Seasonality_DepMt_JSData_RELs,
            debug: bDebug
        })

        // Modeling Session Seasonality Coefficient
        this.defineMapper(WBSchemas.esWB_Modeling_Session_Seasonality_Coeff, {
            name: WBSchemas.esWB_Modeling_Session_Seasonality_Coeff,
            idAttribute: 'WB_MODELING_SESSION_SEASONALITY_COEFF_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Modeling_Session_Seasonality_Coeff),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Modeling_Session_Seasonality_Coeff),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Modeling_Session_Seasonality_Coeff),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Modeling_Session_Seasonality_Coeff
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Modeling_Session_Seasonality_Coeffs',
            recordClass: WBSchemaRows.WB_ModelingSessionSeasonalityCoeffRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Modeling_Session_Seasonality_Coeff,
            relations: WBSchemas_PM.WB_Modeling_Session_Seasonality_Coeff_JSData_RELs,
            debug: bDebug
        })

        // Model Coefficient Set Constant R
        this.defineMapper(WBSchemas.esWB_Model_Coeff_SetConstR, {
            name: WBSchemas.esWB_Model_Coeff_SetConstR,
            idAttribute: 'WB_MODEL_COEFF_SETCONSTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_Coeff_SetConstR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_Coeff_SetConstR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_Coeff_SetConstR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_Coeff_SetConstR),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_Coeff_SetConstRs',
            recordClass: WBSchemaRows.WB_ModelCoeffSetConstRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_Coeff_SetConstR,
            relations: WBSchemas_PM.WB_Model_Coeff_SetConstR_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Published Model">

        // Published Model
        this.defineMapper(WBSchemas.esWB_Published_Model, {
            name: WBSchemas.esWB_Published_Model,
            idAttribute: 'WB_PUBLISHED_MODEL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Published_Model),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Published_Model),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Published_Model),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Published_Model),
            endpoint: this.sRESTBaseURL + '/api/WB_Published_Models',
            recordClass: WBSchemaRows.WB_Published_ModelRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Published_Model,
            relations: WBSchemas_PM.WB_Published_Model_JSData_RELs,
            debug: bDebug
        })
        //</editor-fold>

        // <editor-fold desc="Define Mappers - Custom Column">
        // Custom Column
        this.defineMapper(WBSchemas.esWB_Custom_Column, {
            name: WBSchemas.esWB_Custom_Column,
            idAttribute: 'WB_CUSTOM_COLUMN_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Custom_Column),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Custom_Column),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Custom_Column),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Custom_Column),
            endpoint: this.sRESTBaseURL + '/api/WB_Custom_Columns',
            recordClass: WBSchemaRows.WB_CustomColumnRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Custom_Column,
            relations: WBSchemas_Other.WB_Custom_Column_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold desc="Define Mappers - Affinity Set">
        // Affinity Set
        this.defineMapper(WBSchemas.esWB_AffinitySet, {
            name: WBSchemas.esWB_AffinitySet,
            idAttribute: 'WB_AFFINITYSET_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_AffinitySet),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_AffinitySet),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_AffinitySet),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_AffinitySet),
            endpoint: this.sRESTBaseURL + '/api/WB_AffinitySets',
            recordClass: WBSchemaRows.WB_AffinitySetRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_AffinitySet,
            relations: WBSchemas_PM.WB_AffinitySet_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_AffinityGroup, {
            name: WBSchemas.esWB_AffinityGroup,
            idAttribute: 'WB_AFFINITYGROUP_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_AffinityGroup),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_AffinityGroup),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_AffinityGroup),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_AffinityGroup),
            endpoint: this.sRESTBaseURL + '/api/WB_AffinityGroups',
            recordClass: WBSchemaRows.WB_AffinityGroupRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_AffinityGroup,
            relations: WBSchemas_PM.WB_AffinityGroup_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_AffinityGroupItem, {
            name: WBSchemas.esWB_AffinityGroupItem,
            idAttribute: 'WB_AFFINITYGROUPITEM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_AffinityGroupItem),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_AffinityGroupItem),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_AffinityGroupItem),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_AffinityGroupItem),
            endpoint: this.sRESTBaseURL + '/api/WB_AffinityGroupItems',
            recordClass: WBSchemaRows.WB_AffinityGroupItemRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_AffinityGroupItem,
            relations: WBSchemas_PM.WB_AffinityGroupItem_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - UI Framework">
        // UI Profile
        this.defineMapper(WBSchemas.esWB_UI_Profile, {
            name: WBSchemas.esWB_UI_Profile,
            idAttribute: 'WB_UI_PROFILE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_UI_Profile),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_UI_Profile),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_UI_Profile),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_UI_Profile),
            endpoint: this.sRESTBaseURL + '/api/WB_UI_Profiles',
            recordClass: WBSchemaRows.WB_UIProfileRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_UI_Profile,
            relations: WBSchemas_Other.WB_UI_Profile_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // UI PA-AppSuite Type
        this.defineMapper(WBSchemas.esWB_UI_AppSuite_Type, {
            name: WBSchemas.esWB_UI_AppSuite_Type,
            idAttribute: 'WB_UI_APPSUITEM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_UI_AppSuite_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_UI_AppSuite_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_UI_AppSuite_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_UI_AppSuite_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_UI_AppSuiteMs',
            recordClass: WBSchemaRows.WB_UIAppSuiteTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_UI_AppSuite_Type,
            relations: WBSchemas_Other.WB_UI_AppSuite_Type_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_UI_AppSuite, {
            name: WBSchemas.esWB_UI_AppSuite,
            idAttribute: 'WB_UI_APPSUITE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_UI_AppSuite),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_UI_AppSuite),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_UI_AppSuite),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_UI_AppSuite),
            endpoint: this.sRESTBaseURL + '/api/WB_UI_AppSuites',
            recordClass: WBSchemaRows.WB_UIAppSuiteRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_UI_AppSuite,
            relations: WBSchemas_Other.WB_UI_AppSuite_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_UI_Application, {
            name: WBSchemas.esWB_UI_Application,
            idAttribute: 'WB_UI_APP_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_UI_Application),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_UI_Application),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_UI_Application),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_UI_Application),
            endpoint: this.sRESTBaseURL + '/api/WB_UI_Apps',
            recordClass: WBSchemaRows.WB_UIApplicationRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_UI_Application,
            relations: WBSchemas_Other.WB_UI_Application_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_UI_AppSuite_App_Relationship, {
            name: WBSchemas.esWB_UI_AppSuite_App_Relationship,
            idAttribute: 'WB_UI_APPSUITEAR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_UI_AppSuite_App_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_UI_AppSuite_App_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_UI_AppSuite_App_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_UI_AppSuite_App_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_UI_AppSuiteARs',
            recordClass: WBSchemaRows.WB_UIAppSuiteAppRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_UI_AppSuite_App_Relationship,
            relations: WBSchemas_Other.WB_UI_AppSuite_App_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_UI_AppModule, {
            name: WBSchemas.esWB_UI_AppModule,
            idAttribute: 'WB_UI_APPMODULE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_UI_AppModule),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_UI_AppModule),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_UI_AppModule),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_UI_AppModule),
            endpoint: this.sRESTBaseURL + '/api/WB_UI_AppModules',
            recordClass: WBSchemaRows.WB_UIAppModuleRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_UI_AppModule,
            relations: WBSchemas_Other.WB_UI_AppModule_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_UI_AppExecutable, {
            name: WBSchemas.esWB_UI_AppExecutable,
            idAttribute: 'WB_UI_APPEXEC_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_UI_AppExecutable),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_UI_AppExecutable),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_UI_AppExecutable),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_UI_AppExecutable),
            endpoint: this.sRESTBaseURL + '/api/WB_UI_AppExecs',
            recordClass: WBSchemaRows.WB_UIAppExecutableRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_UI_AppExecutable,
            relations: WBSchemas_Other.WB_UI_AppExecutable_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_UI_App_Report, {
            name: WBSchemas.esWB_UI_App_Report,
            idAttribute: 'WB_UI_APP_REPORT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_UI_App_Report),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_UI_App_Report),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_UI_App_Report),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_UI_App_Report),
            endpoint: this.sRESTBaseURL + '/api/WB_UI_App_Reports',
            recordClass: WBSchemaRows.WB_UIAppReportRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_UI_App_Report,
            relations: WBSchemas_Other.WB_UI_App_Report_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_UI_Usage, {
            name: WBSchemas.esWB_UI_Usage,
            idAttribute: 'WB_UI_USAGE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_UI_Usage),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_UI_Usage),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_UI_Usage),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_UI_Usage),
            endpoint: this.sRESTBaseURL + '/api/WB_UI_Usages',
            recordClass: WBSchemaRows.WB_UIUsageRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_UI_Usage,
            relations: WBSchemas_Other.WB_UI_Usage_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Messages">
        this.defineMapper(WBSchemas.esWB_MessageThread, {
            name: WBSchemas.esWB_MessageThread,
            idAttribute: 'WB_MESSAGETHREAD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_MessageThread),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_MessageThread),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_MessageThread),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_MessageThread),
            endpoint: this.sRESTBaseURL + '/api/WB_MessageThreads',
            recordClass: WBSchemaRows.WB_MessageThreadRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_MessageThread,
            relations: WBSchemas_Other.WB_MessageThread_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Message, {
            name: WBSchemas.esWB_Message,
            idAttribute: 'WB_MESSAGE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Message),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Message),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Message),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Message),
            endpoint: this.sRESTBaseURL + '/api/WB_Messages',
            recordClass: WBSchemaRows.WB_MessageRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Message,
            relations: WBSchemas_Other.WB_Message_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_MessageTo, {
            name: WBSchemas.esWB_MessageTo,
            idAttribute: 'WB_MESSAGE_TO_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_MessageTo),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_MessageTo),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_MessageTo),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_MessageTo),
            endpoint: this.sRESTBaseURL + '/api/WB_MessageTos',
            recordClass: WBSchemaRows.WB_MessageToRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Message_To,
            relations: WBSchemas_Other.WB_Message_To_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="License">
        // License
        this.defineMapper(WBSchemas.esWB_License, {
            name: WBSchemas.esWB_License,
            idAttribute: 'WB_LICENSE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_License),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_License),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_License),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_License),
            endpoint: this.sRESTBaseURL + '/api/WB_Licenses',
            recordClass: WBSchemaRows.WB_LicenseRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_License,
            relations: WBSchemas_Other.WB_License_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold desc="Define Mappers - Simulation Input">
        this.defineMapper(WBSchemas.esWB_Simulation_Input, {
            name: WBSchemas.esWB_Simulation_Input,
            idAttribute: 'WB_SIMULATION_INPUT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Simulation_Input),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Simulation_Input),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Simulation_Input),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Simulation_Input),
            endpoint: this.sRESTBaseURL + '/api/WB_Simulation_Inputs',
            recordClass: WBSchemaRows.WB_SimulationInputRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Simulation_Input,
            relations: WBSchemas_PM.WB_Simulation_Input_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Simulation Input Relationship">
        this.defineMapper(WBSchemas.esWB_Simulation_Input_Relationship, {
            name: WBSchemas.esWB_Simulation_Input_Relationship,
            idAttribute: 'WB_SIMULATION_INPUTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Simulation_Input_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Simulation_Input_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Simulation_Input_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Simulation_Input_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_Simulation_InputRs',
            recordClass: WBSchemaRows.WB_SimulationInputRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Simulation_Input_Relationship,
            relations: WBSchemas_PM.WB_Simulation_Input_Relationship_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Simulation Predictor Relationship">
        this.defineMapper(WBSchemas.esWB_Simulation_Predictor_Relationship, {
            name: WBSchemas.esWB_Simulation_Predictor_Relationship,
            idAttribute: 'WB_SIMULATION_PREDICTORR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Simulation_Predictor_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Simulation_Predictor_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Simulation_Predictor_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Simulation_Predictor_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Simulation_PredictorRs',
            recordClass: WBSchemaRows.WB_SimulationPredictorRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Simulation_Predictor_Relationship,
            relations: WBSchemas_PM.WB_Simulation_Predictor_Relationship_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Simulation">

        this.defineMapper(WBSchemas.esWB_Simulation, {
            name: WBSchemas.esWB_Simulation,
            idAttribute: 'WB_SIMULATION_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Simulation),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Simulation),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Simulation),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Simulation),
            endpoint: this.sRESTBaseURL + '/api/WB_Simulations',
            recordClass: WBSchemaRows.WB_SimulationRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Simulation,
            relations: WBSchemas_PM.WB_Simulation_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Simulation_Relationship, {
            name: WBSchemas.esWB_Simulation_Relationship,
            idAttribute: 'WB_SIMULATIONSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Simulation_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Simulation_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Simulation_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Simulation_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_SimulationSRs',
            recordClass: WBSchemaRows.WB_SimulationRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Simulation_Relationship,
            relations: WBSchemas_PM.WB_Simulation_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Simulation_Product, {
            name: WBSchemas.esWB_Simulation_Product,
            idAttribute: 'WB_SIMULATION_PRD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Simulation_Product),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Simulation_Product),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Simulation_Product),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Simulation_Product),
            endpoint: this.sRESTBaseURL + '/api/WB_Simulation_Prds',
            recordClass: WBSchemaRows.WB_SimulationProductRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Simulation_Product,
            relations: WBSchemas_PM.WB_Simulation_Product_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Model_API_Log, {
            name: WBSchemas.esWB_Model_API_Log,
            idAttribute: 'WB_MODEL_API_LOG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Model_API_Log),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Model_API_Log),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Model_API_Log),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Model_API_Log),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_API_Logs',
            recordClass: WBSchemaRows.WB_ModelAPILogRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Model_API_Log,
            relations: WBSchemas_PM.WB_Model_API_Log_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Scenario Plan">
        this.defineMapper(WBSchemas.esWB_ScnPlan, {
            name: WBSchemas.esWB_ScnPlan,
            idAttribute: 'WB_SCNPLAN_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlan),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlan),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlan),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlan),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlans',
            recordClass: WBSchemaRows.WB_ScenarioPlanRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Scenario_Plan,
            relations: WBSchemas_PM.WB_Scenario_Plan_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlan_EvtTypeR, {
            name: WBSchemas.esWB_ScnPlan_EvtTypeR,
            idAttribute: 'WB_SCNPLAN_EVTTYPER_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlan_EvtTypeR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlan_EvtTypeR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlan_EvtTypeR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlan_EvtTypeR),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlan_EvtTypeRs',
            recordClass: WBSchemaRows.WB_ScenarioPlanEventTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Scenario_Plan_EventType,
            relations: WBSchemas_PM.WB_Scenario_Plan_EventType_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlan_CustR, {
            name: WBSchemas.esWB_ScnPlan_CustR,
            idAttribute: 'WB_SCNPLAN_CUSTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlan_CustR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlan_CustR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlan_CustR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlan_CustR),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlan_CustRs',
            recordClass: WBSchemaRows.WB_ScenarioPlanCustomerRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Scenario_Plan_Customer,
            relations: WBSchemas_PM.WB_Scenario_Plan_Customer_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlan_PrdR, {
            name: WBSchemas.esWB_ScnPlan_PrdR,
            idAttribute: 'WB_SCNPLAN_PRDR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlan_PrdR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlan_PrdR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlan_PrdR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlan_PrdR),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlan_PrdRs',
            recordClass: WBSchemaRows.WB_ScenarioPlanProductRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Scenario_Plan_Product,
            relations: WBSchemas_PM.WB_Scenario_Plan_Product_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlan_PredictorR, {
            name: WBSchemas.esWB_ScnPlan_PredictorR,
            idAttribute: 'WB_SCNPLAN_PREDICTORR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlan_PredictorR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlan_PredictorR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlan_PredictorR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlan_PredictorR),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlan_PredictorRs',
            recordClass: WBSchemaRows.WB_ScenarioPlanPredictorRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Scenario_Plan_Predictor,
            relations: WBSchemas_PM.WB_Scenario_Plan_Predictor_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlan_InputR, {
            name: WBSchemas.esWB_ScnPlan_InputR,
            idAttribute: 'WB_SCNPLAN_INPUTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlan_InputR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlan_InputR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlan_InputR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlan_InputR),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlan_InputRs',
            recordClass: WBSchemaRows.WB_ScenarioPlanInputRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Scenario_Plan_Input,
            relations: WBSchemas_PM.WB_Scenario_Plan_Input_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlan_CoeffR, {
            name: WBSchemas.esWB_ScnPlan_CoeffR,
            idAttribute: 'WB_SCNPLAN_COEFFR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlan_CoeffR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlan_CoeffR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlan_CoeffR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlan_CoeffR),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlan_CoeffRs',
            recordClass: WBSchemaRows.WB_ScenarioPlanCoefficientRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Scenario_Plan_Coefficient,
            relations: WBSchemas_PM.WB_Scenario_Plan_Coefficient_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Scenario Planner">
        this.defineMapper(WBSchemas.esWB_ScnPlanner, {
            name: WBSchemas.esWB_ScnPlanner,
            idAttribute: 'WB_SCNPLANNER_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlanner),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlanner),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlanner),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlanner),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlanners',
            recordClass: WBSchemaRows.WB_ScenarioPlannerRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_ScnPlanner,
            relations: WBSchemas_PM.WB_ScnPlanner_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlannerCalc, {
            name: WBSchemas.esWB_ScnPlannerCalc,
            idAttribute: 'WB_SCNPLANNERCALC_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlannerCalc),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlannerCalc),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlannerCalc),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlannerCalc),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlannerCalcs',
            recordClass: WBSchemaRows.WB_ScenarioPlannerCalcRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_ScnPlannerCalc,
            relations: WBSchemas_PM.WB_ScnPlannerCalc_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlannerCalcLog, {
            name: WBSchemas.esWB_ScnPlannerCalcLog,
            idAttribute: 'WB_SCNPLANNERCALCLOG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlannerCalcLog),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlannerCalcLog),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlannerCalcLog),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlannerCalcLog),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlannerCalcLogs',
            recordClass: WBSchemaRows.WB_ScenarioPlannerCalcLogRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_ScnPlannerCalcLog,
            relations: WBSchemas_PM.WB_ScnPlannerCalcLog_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlannerCnstr, {
            name: WBSchemas.esWB_ScnPlannerCnstr,
            idAttribute: 'WB_SCNPLANNERCNSTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlannerCnstr),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlannerCnstr),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlannerCnstr),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlannerCnstr),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlannerCnstrs',
            recordClass: WBSchemaRows.WB_ScenarioPlannerConstraintRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_ScnPlannerCnstr,
            relations: WBSchemas_PM.WB_ScnPlannerCnstr_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlannerCR, {
            name: WBSchemas.esWB_ScnPlannerCR,
            idAttribute: 'WB_SCNPLANNERCR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlannerCR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlannerCR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlannerCR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlannerCR),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlannerCRs',
            recordClass: WBSchemaRows.WB_ScenarioPlannerCustomerRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_ScnPlannerCR,
            relations: WBSchemas_PM.WB_ScnPlannerCR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlannerESR, {
            name: WBSchemas.esWB_ScnPlannerESR,
            idAttribute: 'WB_SCNPLANNERESR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlannerESR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlannerESR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlannerESR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlannerESR),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlannerESRs',
            recordClass: WBSchemaRows.WB_ScenarioPlannerEventTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_ScnPlannerESR,
            relations: WBSchemas_PM.WB_ScnPlannerESR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlannerPPR, {
            name: WBSchemas.esWB_ScnPlannerPPR,
            idAttribute: 'WB_SCNPLANNERPPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlannerPPR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlannerPPR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlannerPPR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlannerPPR),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlannerPPRs',
            recordClass: WBSchemaRows.WB_ScenarioPlannerPeriodPartitionRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_ScnPlannerPPR,
            relations: WBSchemas_PM.WB_ScnPlannerPPR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlannerPR, {
            name: WBSchemas.esWB_ScnPlannerPR,
            idAttribute: 'WB_SCNPLANNERPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlannerPR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlannerPR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlannerPR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlannerPR),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlannerPRs',
            recordClass: WBSchemaRows.WB_ScenarioPlannerProductRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_ScnPlannerPR,
            relations: WBSchemas_PM.WB_ScnPlannerPR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlannerPrdPR, {
            name: WBSchemas.esWB_ScnPlannerPrdPR,
            idAttribute: 'WB_SCNPLANNERPRDPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlannerPrdPR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlannerPrdPR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlannerPrdPR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlannerPrdPR),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlannerPrdPRs',
            recordClass: WBSchemaRows.WB_ScenarioPlannerProductPartitionRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_ScnPlannerPrdPR,
            relations: WBSchemas_PM.WB_ScnPlannerPrdPR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlannerPrdPPR, {
            name: WBSchemas.esWB_ScnPlannerPrdPPR,
            idAttribute: 'WB_SCNPLANNERPRDPPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlannerPrdPPR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlannerPrdPPR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlannerPrdPPR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlannerPrdPPR),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlannerPrdPPRs',
            recordClass: WBSchemaRows.WB_ScenarioPlannerProductPartitionRelRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_ScnPlannerPrdPPR,
            relations: WBSchemas_PM.WB_ScnPlannerPrdPPR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlannerPrmR, {
            name: WBSchemas.esWB_ScnPlannerPrmR,
            idAttribute: 'WB_SCNPLANNERPRMR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlannerPrmR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlannerPrmR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlannerPrmR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlannerPrmR),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlannerPrmRs',
            recordClass: WBSchemaRows.WB_ScenarioPlannerPromotionRelRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_ScnPlannerPrmR,
            relations: WBSchemas_PM.WB_ScnPlannerPrmR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlannerTrial, {
            name: WBSchemas.esWB_ScnPlannerTrial,
            idAttribute: 'WB_SCNPLANNERTRIAL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlannerTrial),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlannerTrial),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlannerTrial),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlannerTrial),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlannerTrials',
            recordClass: WBSchemaRows.WB_ScenarioPlannerTrialRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_ScnPlannerTrial,
            relations: WBSchemas_PM.WB_ScnPlannerTrial_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlannerTrialPR, {
            name: WBSchemas.esWB_ScnPlannerTrialPR,
            idAttribute: 'WB_SCNPLANNERTRIALPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlannerTrialPR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlannerTrialPR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlannerTrialPR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlannerTrialPR),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlannerTrialPRs',
            recordClass: WBSchemaRows.WB_ScenarioPlannerTrialPRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_ScnPlannerTrialPR,
            relations: WBSchemas_PM.WB_ScnPlannerTrialPR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlannerTrialCNR, {
            name: WBSchemas.esWB_ScnPlannerTrialCNR,
            idAttribute: 'WB_SCNPLANNERTRIALCNR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlannerTrialCNR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlannerTrialCNR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlannerTrialCNR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlannerTrialCNR),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlannerTrialCNRs',
            recordClass: WBSchemaRows.WB_ScenarioPlannerTrialCNRRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_ScnPlannerTrialCNR,
            relations: WBSchemas_PM.WB_ScnPlannerTrialCNR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnPlannerTrialEvt, {
            name: WBSchemas.esWB_ScnPlannerTrialEvt,
            idAttribute: 'WB_SCNPLANNERTRIALEVT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnPlannerTrialEvt),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnPlannerTrialEvt),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnPlannerTrialEvt),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnPlannerTrialEvt),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnPlannerTrialEvts',
            recordClass: WBSchemaRows.WB_ScenarioPlannerTrialEventRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_ScnPlannerTrialEvt,
            relations: WBSchemas_PM.WB_ScnPlannerTrialEvt_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ScnConstraint, {
            name: WBSchemas.esWB_ScnConstraint,
            idAttribute: 'WB_SCNCONSTRAINTM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ScnConstraint),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ScnConstraint),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ScnConstraint),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ScnConstraint),
            endpoint: this.sRESTBaseURL + '/api/WB_ScnConstraintMs',
            recordClass: WBSchemaRows.WB_ScenarioConstraintRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_ScnConstraint,
            relations: WBSchemas_PM.WB_ScnConstraint_JSData_RELs,
            debug: bDebug
        })

        // <editor-fold desc="Define Mappers - CBO">

        this.defineMapper(WBSchemas.esWB_CBO, {
            name: WBSchemas.esWB_CBO,
            idAttribute: 'WB_CBO_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_CBO),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_CBO),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_CBO),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_CBO),
            endpoint: this.sRESTBaseURL + '/api/WB_CBOs',
            recordClass: WBSchemaRows.WB_CBORow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_CBO,
            relations: WBSchemas_PM.WB_CBO_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_CBO_Input_Relationship, {
            name: WBSchemas.esWB_CBO_Input_Relationship,
            idAttribute: 'WB_CBO_INPUTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_CBO_Input_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_CBO_Input_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_CBO_Input_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_CBO_Input_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_CBO_InputRs',
            recordClass: WBSchemaRows.WB_CBOInputRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_CBO_Input_Relationship,
            relations: WBSchemas_PM.WB_CBO_Input_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_CBO_Set, {
            name: WBSchemas.esWB_CBO_Set,
            idAttribute: 'WB_CBO_SET_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_CBO_Set),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_CBO_Set),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_CBO_Set),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_CBO_Set),
            endpoint: this.sRESTBaseURL + '/api/WB_CBO_SETs',
            recordClass: WBSchemaRows.WB_CBOSetRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_CBO_SET,
            relations: WBSchemas_PM.WB_CBO_SET_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_CBO_Set_Item, {
            name: WBSchemas.esWB_CBO_Set_Item,
            idAttribute: 'WB_CBO_SET_ITEM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_CBO_Set_Item),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_CBO_Set_Item),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_CBO_Set_Item),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_CBO_Set_Item),
            endpoint: this.sRESTBaseURL + '/api/WB_CBO_SET_ITEMs',
            recordClass: WBSchemaRows.WB_CBOSetItemRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_CBO_SET_ITEM,
            relations: WBSchemas_PM.WB_CBO_SET_ITEM_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_CBO_Set_Predictor, {
            name: WBSchemas.esWB_CBO_Set_Predictor,
            idAttribute: 'WB_CBO_SET_PR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_CBO_Set_Predictor),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_CBO_Set_Predictor),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_CBO_Set_Predictor),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_CBO_Set_Predictor),
            endpoint: this.sRESTBaseURL + '/api/WB_CBO_SET_PRs',
            recordClass: WBSchemaRows.WB_CBOSetPredictorRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_CBO_SET_PR,
            relations: WBSchemas_PM.WB_CBO_SET_PR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_CBO_Constraint, {
            name: WBSchemas.esWB_CBO_Constraint,
            idAttribute: 'WB_CBO_CSTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_CBO_Constraint),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_CBO_Constraint),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_CBO_Constraint),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_CBO_Constraint),
            endpoint: this.sRESTBaseURL + '/api/WB_CBO_Cstrs',
            recordClass: WBSchemaRows.WB_CBOConstraintRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_CBO_Constraint,
            relations: WBSchemas_PM.WB_CBO_Constraint_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Spend Function">
        this.defineMapper(WBSchemas.esWB_Spend_Function, {
            name: WBSchemas.esWB_Spend_Function,
            idAttribute: 'WB_SPEND_FUNCTION_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Spend_Function),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Spend_Function),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Spend_Function),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Spend_Function),
            endpoint: this.sRESTBaseURL + '/api/WB_Spend_Functions',
            recordClass: WBSchemaRows.WB_SpendFunctionRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Spend_Function,
            relations: WBSchemas_PM.WB_Spend_Function_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Spend_Function_Input_Relationship, {
            name: WBSchemas.esWB_Spend_Function_Input_Relationship,
            idAttribute: 'WB_SPEND_FUNCTION_INPUTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Spend_Function_Input_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Spend_Function_Input_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Spend_Function_Input_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Spend_Function_Input_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Spend_Function_InputRs',
            recordClass: WBSchemaRows.WB_SpendFunctionInputRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Spend_Function_Input_Relationship,
            relations: WBSchemas_PM.WB_Spend_Function_Input_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Spend_Function_Term_Relationship, {
            name: WBSchemas.esWB_Spend_Function_Term_Relationship,
            idAttribute: 'WB_SPEND_FUNCTION_TERMR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Spend_Function_Term_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Spend_Function_Term_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Spend_Function_Term_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Spend_Function_Term_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Spend_Function_TermRs',
            recordClass: WBSchemaRows.WB_SpendFunctionTermRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Spend_Function_Term_Relationship,
            relations: WBSchemas_PM.WB_Spend_Function_Term_Relationship_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Spend Function Relationship">
        this.defineMapper(WBSchemas.esWB_Spend_Function_Relationship, {
            name: WBSchemas.esWB_Spend_Function_Relationship,
            idAttribute: 'WB_SPEND_FUNCTIONR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Spend_Function_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Spend_Function_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Spend_Function_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Spend_Function_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_Spend_FunctionRs',
            recordClass: WBSchemaRows.WB_SpendFunctionRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Spend_Function_Relationship,
            relations: WBSchemas_PM.WB_Spend_Function_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Spend_Function_Relationship_Scope, {
            name: WBSchemas.esWB_Spend_Function_Relationship_Scope,
            idAttribute: 'WB_SPEND_FUNCTIONCPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Spend_Function_Relationship_Scope),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Spend_Function_Relationship_Scope),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Spend_Function_Relationship_Scope),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Spend_Function_Relationship_Scope
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Spend_FunctionCPRs',
            recordClass: WBSchemaRows.WB_SpendFunctionRelationshipScopeRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Spend_Function_Relationship_Scope,
            relations: WBSchemas_PM.WB_Spend_Function_Relationship_Scope_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Define Mappers - Metric Output Tables">
        this.defineMapper(WBSchemas.esWB_Simulation_Metrics, {
            name: WBSchemas.esWB_Simulation_Metrics,
            idAttribute: 'WB_SIMMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Simulation_Metrics),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Simulation_Metrics),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Simulation_Metrics),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Simulation_Metrics),
            endpoint: this.sRESTBaseURL + '/api/WB_SimMts',
            recordClass: WBSchemaRows.WB_SimulationMetricsRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Simulation_Metrics,
            relations: WBSchemas_PM.WB_Simulation_Metrics_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Simulation_Metrics_CBO_Set, {
            name: WBSchemas.esWB_Simulation_Metrics_CBO_Set,
            idAttribute: 'WB_SIMMTSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Simulation_Metrics_CBO_Set),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Simulation_Metrics_CBO_Set),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Simulation_Metrics_CBO_Set),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Simulation_Metrics_CBO_Set),
            endpoint: this.sRESTBaseURL + '/api/WB_SimMtSRs',
            recordClass: WBSchemaRows.WB_SimulationMetricsCBOSetRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Simulation_Metrics_CBO_Set,
            relations: WBSchemas_PM.WB_Simulation_Metrics_CBO_Set_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Simulation_Spend_Metrics, {
            name: WBSchemas.esWB_Simulation_Spend_Metrics,
            idAttribute: 'WB_SIMSMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Simulation_Spend_Metrics),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Simulation_Spend_Metrics),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Simulation_Spend_Metrics),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Simulation_Spend_Metrics),
            endpoint: this.sRESTBaseURL + '/api/WB_SimSMts',
            recordClass: WBSchemaRows.WB_SimulationSpendMetricsRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Simulation_Spend_Metrics,
            relations: WBSchemas_PM.WB_Simulation_Spend_Metrics_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Benchmark">
        // Benchmark
        this.defineMapper(WBSchemas.esWB_Benchmark, {
            name: WBSchemas.esWB_Benchmark,
            idAttribute: 'WB_BENCHMARK_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Benchmark),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Benchmark),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Benchmark),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Benchmark),
            endpoint: this.sRESTBaseURL + '/api/WB_Benchmarks',
            recordClass: WBSchemaRows.WB_BenchmarkRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Benchmark,
            relations: WBSchemas_Other.WB_Benchmark_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // Target
        this.defineMapper(WBSchemas.esWB_Target, {
            name: WBSchemas.esWB_Target,
            idAttribute: 'WB_TARGET_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Target),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Target),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Target),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Target),
            endpoint: this.sRESTBaseURL + '/api/WB_Targets',
            recordClass: WBSchemaRows.WB_TargetRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Target,
            relations: WBSchemas_Other.WB_Target_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold desc="Partition">
        // Partition
        this.defineMapper(WBSchemas.esWB_Partition, {
            name: WBSchemas.esWB_Partition,
            idAttribute: 'WB_PARTITION_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Partition),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Partition),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Partition),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Partition),
            endpoint: this.sRESTBaseURL + '/api/WB_Partitions',
            recordClass: WBSchemaRows.WB_PartitionRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_Partition,
            relations: WBSchemas_Cntrl.WB_Partition_JSData_RELs,
            debug: bDebug
        })

        // PartitionR
        this.defineMapper(WBSchemas.esWB_PartitionR, {
            name: WBSchemas.esWB_PartitionR,
            idAttribute: 'WB_PARTITIONR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PartitionR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PartitionR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PartitionR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PartitionR),
            endpoint: this.sRESTBaseURL + '/api/WB_PartitionRs',
            recordClass: WBSchemaRows.WB_PartitionRRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_PartitionR,
            relations: WBSchemas_Cntrl.WB_PartitionR_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold desc="Control Point Type">
        // Control Point Type

        this.defineMapper(WBSchemas.esWB_ControlPoint_Type, {
            name: WBSchemas.esWB_ControlPoint_Type,
            idAttribute: 'WB_CONTROL_POINTM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ControlPoint_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ControlPoint_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ControlPoint_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ControlPoint_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_ControlPointMs',
            recordClass: WBSchemaRows.WB_ControlPointTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_Control_Point_Type,
            relations: WBSchemas_Cntrl.WB_Control_Point_Type_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ControlPoint_Type_Process, {
            name: WBSchemas.esWB_ControlPoint_Type_Process,
            idAttribute: 'WB_CONTROL_POINTMPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ControlPoint_Type_Process),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ControlPoint_Type_Process),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ControlPoint_Type_Process),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ControlPoint_Type_Process),
            endpoint: this.sRESTBaseURL + '/api/WB_ControlPointMPRs',
            recordClass: WBSchemaRows.WB_ControlPointTypeProcessRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_Control_Point_Type_Process,
            relations: WBSchemas_Cntrl.WB_Control_Point_Type_Process_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ControlPoint_Type_Process_Input, {
            name: WBSchemas.esWB_ControlPoint_Type_Process_Input,
            idAttribute: 'WB_CONTROL_POINTMPIR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ControlPoint_Type_Process_Input),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ControlPoint_Type_Process_Input),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ControlPoint_Type_Process_Input),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_ControlPoint_Type_Process_Input
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_ControlPointMPIRs',
            recordClass: WBSchemaRows.WB_ControlPointTypeProcessInputRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_Control_Point_Type_Process_Input,
            relations: WBSchemas_Cntrl.WB_Control_Point_Type_Process_Input_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Control Point">
        // Control Point
        this.defineMapper(WBSchemas.esWB_ControlPoint, {
            name: WBSchemas.esWB_ControlPoint,
            idAttribute: 'WB_CONTROL_POINT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ControlPoint),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ControlPoint),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ControlPoint),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ControlPoint),
            endpoint: this.sRESTBaseURL + '/api/WB_ControlPoints',
            recordClass: WBSchemaRows.WB_ControlPointRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_Control_Point,
            relations: WBSchemas_Cntrl.WB_Control_Point_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ControlPoint_Partition, {
            name: WBSchemas.esWB_ControlPoint_Partition,
            idAttribute: 'WB_CONTROL_POINTPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ControlPoint_Partition),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ControlPoint_Partition),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ControlPoint_Partition),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ControlPoint_Partition),
            endpoint: this.sRESTBaseURL + '/api/WB_ControlPointPRs',
            recordClass: WBSchemaRows.WB_ControlPointPartitionRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_Control_Point_Partition,
            relations: WBSchemas_Cntrl.WB_Control_Point_Partition_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ControlPoint_Partition_Input, {
            name: WBSchemas.esWB_ControlPoint_Partition_Input,
            idAttribute: 'WB_CONTROL_POINTPIR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ControlPoint_Partition_Input),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ControlPoint_Partition_Input),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ControlPoint_Partition_Input),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ControlPoint_Partition_Input),
            endpoint: this.sRESTBaseURL + '/api/WB_ControlPointPIRs',
            recordClass: WBSchemaRows.WB_ControlPointPartitionInputRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_Control_Point_Partition_Input,
            relations: WBSchemas_Cntrl.WB_Control_Point_Partition_Input_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Control Point - LE Spend">
        // Control Point LE Spend
        this.defineMapper(WBSchemas.esWB_LE_Spend, {
            name: WBSchemas.esWB_LE_Spend,
            idAttribute: 'WB_LE_SPEND_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_LE_Spend),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_LE_Spend),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_LE_Spend),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_LE_Spend),
            endpoint: this.sRESTBaseURL + '/api/WB_LE_Spends',
            recordClass: WBSchemaRows.WB_LESpendRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_LE_Spend,
            relations: WBSchemas_Cntrl.WB_LE_Spend_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_LE_Spend_Partition, {
            name: WBSchemas.esWB_LE_Spend_Partition,
            idAttribute: 'WB_LE_SPENDPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_LE_Spend_Partition),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_LE_Spend_Partition),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_LE_Spend_Partition),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_LE_Spend_Partition),
            endpoint: this.sRESTBaseURL + '/api/WB_LE_SpendPRs',
            recordClass: WBSchemaRows.WB_LESpendPartitionRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_LE_Spend_Partition,
            relations: WBSchemas_Cntrl.WB_LE_Spend_Partition_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_LE_Spend_Partition_Input, {
            name: WBSchemas.esWB_LE_Spend_Partition_Input,
            idAttribute: 'WB_LE_SPENDPSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_LE_Spend_Partition_Input),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_LE_Spend_Partition_Input),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_LE_Spend_Partition_Input),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_LE_Spend_Partition_Input),
            endpoint: this.sRESTBaseURL + '/api/WB_LE_SpendPSRs',
            recordClass: WBSchemaRows.WB_LESpendPartitionInputRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_LE_Spend_Partition_Input,
            relations: WBSchemas_Cntrl.WB_LE_Spend_Partition_Input_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Control Point - Act Spend">
        // Control Point Act Spend
        this.defineMapper(WBSchemas.esWB_Act_Spend, {
            name: WBSchemas.esWB_Act_Spend,
            idAttribute: 'WB_ACT_SPEND_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Act_Spend),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Act_Spend),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Act_Spend),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Act_Spend),
            endpoint: this.sRESTBaseURL + '/api/WB_Act_Spends',
            recordClass: WBSchemaRows.WB_ActSpendRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_Act_Spend,
            relations: WBSchemas_Cntrl.WB_Act_Spend_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Act_Spend_Partition, {
            name: WBSchemas.esWB_Act_Spend_Partition,
            idAttribute: 'WB_ACT_SPENDPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Act_Spend_Partition),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Act_Spend_Partition),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Act_Spend_Partition),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Act_Spend_Partition),
            endpoint: this.sRESTBaseURL + '/api/WB_Act_SpendPRs',
            recordClass: WBSchemaRows.WB_ActSpendPartitionRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_Act_Spend_Partition,
            relations: WBSchemas_Cntrl.WB_Act_Spend_Partition_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Act_Spend_Partition_Input, {
            name: WBSchemas.esWB_Act_Spend_Partition_Input,
            idAttribute: 'WB_ACT_SPENDPSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Act_Spend_Partition_Input),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Act_Spend_Partition_Input),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Act_Spend_Partition_Input),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Act_Spend_Partition_Input),
            endpoint: this.sRESTBaseURL + '/api/WB_Act_SpendPSRs',
            recordClass: WBSchemaRows.WB_ActSpendPartitionInputRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_Act_Spend_Partition_Input,
            relations: WBSchemas_Cntrl.WB_Act_Spend_Partition_Input_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold desc="Control Point - Classification">
        // Control Point Classification
        this.defineMapper(WBSchemas.esWB_Classification, {
            name: WBSchemas.esWB_Classification,
            idAttribute: 'WB_CLASSIFICATION_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Classification),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Classification),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Classification),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Classification),
            endpoint: this.sRESTBaseURL + '/api/WB_Classifications',
            recordClass: WBSchemaRows.WB_ClassificationRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_Classification,
            relations: WBSchemas_Cntrl.WB_Classification_JSData_RELs,
            debug: bDebug
        })

        // Control Point Classification Step
        this.defineMapper(WBSchemas.esWB_Classification_Step, {
            name: WBSchemas.esWB_Classification_Step,
            idAttribute: 'WB_CLASSIFICATION_STEP_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Classification_Step),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Classification_Step),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Classification_Step),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Classification_Step),
            endpoint: this.sRESTBaseURL + '/api/WB_Classification_Steps',
            recordClass: WBSchemaRows.WB_ClassificationStepRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_Classification_Step,
            relations: WBSchemas_Cntrl.WB_Classification_Step_JSData_RELs,
            debug: bDebug
        })

        // Control Point Classification StepR
        this.defineMapper(WBSchemas.esWB_Classification_StepR, {
            name: WBSchemas.esWB_Classification_StepR,
            idAttribute: 'WB_CLASSIFICATION_STEPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Classification_StepR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Classification_StepR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Classification_StepR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Classification_StepR),
            endpoint: this.sRESTBaseURL + '/api/WB_Classification_StepRs',
            recordClass: WBSchemaRows.WB_ClassificationStepRRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_Classification_StepR,
            relations: WBSchemas_Cntrl.WB_Classification_StepR_JSData_RELs,
            debug: bDebug
        })

        // Control Point Classification Control
        this.defineMapper(WBSchemas.esWB_Classification_Control, {
            name: WBSchemas.esWB_Classification_Control,
            idAttribute: 'WB_CLASSIFICATION_CONTROL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Classification_Control),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Classification_Control),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Classification_Control),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Classification_Control),
            endpoint: this.sRESTBaseURL + '/api/WB_Classification_Controls',
            recordClass: WBSchemaRows.WB_ClassificationControlRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_Classification_Control,
            relations: WBSchemas_Cntrl.WB_Classification_Control_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Classification_Control_Partition, {
            name: WBSchemas.esWB_Classification_Control_Partition,
            idAttribute: 'WB_CLASSIFICATION_CONTROLPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Classification_Control_Partition),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Classification_Control_Partition),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Classification_Control_Partition),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Classification_Control_Partition
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Classification_ControlPRs',
            recordClass: WBSchemaRows.WB_ClassificationControlPartitionRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_Classification_Control_Partition,
            relations: WBSchemas_Cntrl.WB_Classification_Control_Partition_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold  Description=Power Metric>
        this.defineMapper(WBSchemas.esWB_Power_Metric, {
            name: WBSchemas.esWB_Power_Metric,
            idAttribute: 'WB_MODEL_POWERMETRIC_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Power_Metric),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Power_Metric),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Power_Metric),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Power_Metric),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_PowerMetrics',
            recordClass: WBSchemaRows.WB_PowerMetricRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Power_Metric,
            relations: WBSchemas_PM.WB_Power_Metric_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Power_Metric_Relationship, {
            name: WBSchemas.esWB_Power_Metric_Relationship,
            idAttribute: 'WB_MODEL_POWERMETRICR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Power_Metric_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Power_Metric_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Power_Metric_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Power_Metric_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_Model_PowerMetricRs',
            recordClass: WBSchemaRows.WB_PowerMetricRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_Power_Metric_Relationship,
            relations: WBSchemas_PM.WB_Power_Metric_Relationship_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>
        // Currency
        // <editor-fold>
        this.defineMapper(WBSchemas.esWB_Currency, {
            name: WBSchemas.esWB_Currency,
            idAttribute: 'WB_CURRENCY_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Currency),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Currency),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Currency),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Currency),
            endpoint: this.sRESTBaseURL + '/api/WB_Currencies',
            recordClass: WBSchemaRows.WB_CurrencyRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Currency,
            relations: WBSchemas_DataFactory.WB_Currency_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_CurrencyT, {
            name: WBSchemas.esWB_CurrencyT,
            idAttribute: 'WB_CURRENCYT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_CurrencyT),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_CurrencyT),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_CurrencyT),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_CurrencyT),
            endpoint: this.sRESTBaseURL + '/api/WB_CurrencyTs',
            recordClass: WBSchemaRows.WB_CurrencyTRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_CurrencyT,
            relations: WBSchemas_DataFactory.WB_CurrencyT_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // Exchange Rate
        //<editor-fold>
        this.defineMapper(WBSchemas.esWB_ExchangeRateM, {
            name: WBSchemas.esWB_ExchangeRateM,
            idAttribute: 'WB_EXCHANGERATEM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ExchangeRateM),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ExchangeRateM),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ExchangeRateM),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ExchangeRateM),
            endpoint: this.sRESTBaseURL + '/api/WB_ExchangeRateMs',
            recordClass: WBSchemaRows.WB_ExchangeRateMRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_ExchangeRateM,
            relations: WBSchemas_Other.WB_ExchangeRateM_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ExchangeRateMT, {
            name: WBSchemas.esWB_ExchangeRateMT,
            idAttribute: 'WB_EXCHANGERATEMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ExchangeRateMT),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ExchangeRateMT),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ExchangeRateMT),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ExchangeRateMT),
            endpoint: this.sRESTBaseURL + '/api/WB_ExchangeRateMTs',
            recordClass: WBSchemaRows.WB_ExchangeRateMTRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_ExchangeRateMT,
            relations: WBSchemas_Other.WB_ExchangeRateMT_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_ExchangeRateMR, {
            name: WBSchemas.esWB_ExchangeRateMR,
            idAttribute: 'WB_EXCHANGERATEMR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_ExchangeRateMR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_ExchangeRateMR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_ExchangeRateMR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_ExchangeRateMR),
            endpoint: this.sRESTBaseURL + '/api/WB_ExchangeRateMRs',
            recordClass: WBSchemaRows.WB_ExchangeRateMRRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_ExchangeRateMR,
            relations: WBSchemas_Other.WB_ExchangeRateMR_JSData_RELs,
            debug: bDebug
        })
        //</editor-fold>

        // Data Object
        // <editor-fold>
        this.defineMapper(WBSchemas.esWB_DataObject, {
            name: WBSchemas.esWB_DataObject,
            idAttribute: 'WB_DATAOBJECT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_DataObject),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_DataObject),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_DataObject),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_DataObject),
            endpoint: this.sRESTBaseURL + '/api/WB_DataObjects',
            recordClass: WBSchemaRows.WB_DataObjectRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_DataObject,
            relations: WBSchemas_Other.WB_DataObject_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_DataObject_Text, {
            name: WBSchemas.esWB_DataObject_Text,
            idAttribute: 'WB_DATAOBJECTT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_DataObject_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_DataObject_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_DataObject_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_DataObject_Text),
            endpoint: this.sRESTBaseURL + '/api/WB_DataObjectTs',
            recordClass: WBSchemaRows.WB_DataObjectTextRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_DataObjectT,
            relations: WBSchemas_Other.WB_DataObjectT_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_DataObject_Column, {
            name: WBSchemas.esWB_DataObject_Column,
            idAttribute: 'WB_DATAOBJECT_COLUMN_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_DataObject_Column),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_DataObject_Column),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_DataObject_Column),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_DataObject_Column),
            endpoint: this.sRESTBaseURL + '/api/WB_DataObject_Columns',
            recordClass: WBSchemaRows.WB_DataObjectColumnRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_DataObject_Column,
            relations: WBSchemas_Other.WB_DataObject_Column_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_DataObject_Column_Text, {
            name: WBSchemas.esWB_DataObject_Column_Text,
            idAttribute: 'WB_DATAOBJECT_COLUMNT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_DataObject_Column_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_DataObject_Column_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_DataObject_Column_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_DataObject_Column_Text),
            endpoint: this.sRESTBaseURL + '/api/WB_DataObject_ColumnTs',
            recordClass: WBSchemaRows.WB_DataObjectColumnTextRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_DataObject_ColumnT,
            relations: WBSchemas_Other.WB_DataObject_ColumnT_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_DataObject_Message, {
            name: WBSchemas.esWB_DataObject_Message,
            idAttribute: 'WB_DATAOBJECT_MESSAGE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_DataObject_Message),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_DataObject_Message),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_DataObject_Message),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_DataObject_Message),
            endpoint: this.sRESTBaseURL + '/api/WB_DataObject_Messages',
            recordClass: WBSchemaRows.WB_DataObjectMessageRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_DataObject_Message,
            relations: WBSchemas_Other.WB_DataObject_Message_JSData_RELs,
            debug: bDebug
        })
        this.defineMapper(WBSchemas.esWB_DataObject_Message_Text, {
            name: WBSchemas.esWB_DataObject_Message_Text,
            idAttribute: 'WB_DATAOBJECT_MESSAGET_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_DataObject_Message_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_DataObject_Message_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_DataObject_Message_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_DataObject_Message_Text),
            endpoint: this.sRESTBaseURL + '/api/WB_DataObject_MessageTs',
            recordClass: WBSchemaRows.WB_DataObjectMessageTextRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_DataObject_MessageT,
            relations: WBSchemas_Other.WB_DataObject_MessageT_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold  Description="Circular Config">

        this.defineMapper(WBSchemas.esWB_Circular_Config, {
            name: WBSchemas.esWB_Circular_Config,
            idAttribute: 'WB_CIRCULARCONFIG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Circular_Config),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Circular_Config),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Circular_Config),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Circular_Config),
            endpoint: this.sRESTBaseURL + '/api/WB_Circular_Configs',
            recordClass: WBSchemaRows.WB_CircularConfigRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Circular_Config,
            relations: WBSchemas_Other.WB_Circular_Config_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Circular_ConfigCR, {
            name: WBSchemas.esWB_Circular_ConfigCR,
            idAttribute: 'WB_CIRCULARCONFIGCR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Circular_ConfigCR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Circular_ConfigCR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Circular_ConfigCR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Circular_ConfigCR),
            endpoint: this.sRESTBaseURL + '/api/WB_Circular_ConfigCRs',
            recordClass: WBSchemaRows.WB_CircularConfigCRRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Circular_ConfigCR,
            relations: WBSchemas_Other.WB_Circular_ConfigCR_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold  Description="Circular">

        this.defineMapper(WBSchemas.esWB_Circular_AdBlock, {
            name: WBSchemas.esWB_Circular_AdBlock,
            idAttribute: 'WB_CIRCULARADBLOCK_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Circular_AdBlock),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Circular_AdBlock),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Circular_AdBlock),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Circular_AdBlock),
            endpoint: this.sRESTBaseURL + '/api/WB_Circular_AdBlocks',
            recordClass: WBSchemaRows.WB_CircularAdBlockRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Circular_AdBlock,
            relations: WBSchemas_Other.WB_Circular_AdBlock_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Circular, {
            name: WBSchemas.esWB_Circular,
            idAttribute: 'WB_CIRCULAR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Circular),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Circular),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Circular),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Circular),
            endpoint: this.sRESTBaseURL + '/api/WB_Circulars',
            recordClass: WBSchemaRows.WB_CircularRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Circular,
            relations: WBSchemas_Other.WB_Circular_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_CircularCR, {
            name: WBSchemas.esWB_CircularCR,
            idAttribute: 'WB_CIRCULARCR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_CircularCR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_CircularCR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_CircularCR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_CircularCR),
            endpoint: this.sRESTBaseURL + '/api/WB_CircularCRs',
            recordClass: WBSchemaRows.WB_CircularCRRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_CircularCR,
            relations: WBSchemas_Other.WB_CircularCR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_CircularPR, {
            name: WBSchemas.esWB_CircularPR,
            idAttribute: 'WB_CIRCULARPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_CircularPR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_CircularPR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_CircularPR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_CircularPR),
            endpoint: this.sRESTBaseURL + '/api/WB_CircularPRs',
            recordClass: WBSchemaRows.WB_CircularPRRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_CircularPR,
            relations: WBSchemas_Other.WB_CircularPR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Schedule, {
            name: WBSchemas.esWB_Schedule,
            idAttribute: 'WB_SCHEDULE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Schedule),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Schedule),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Schedule),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Schedule),
            endpoint: this.sRESTBaseURL + '/api/WB_Schedules',
            recordClass: WBSchemaRows.WB_ScheduleRow,
            applySchema: applySchema,
            schema: WBSchemas_Process.WB_Schedule,
            relations: WBSchemas_Process.WB_Schedule_JSData_RELs,
            debug: bDebug
        })

        //Lift API
        this.defineMapper(WBSchemas.esWB_LiftAPI, {
            name: WBSchemas.esWB_LiftAPI,
            idAttribute: 'WB_LIFTAPI_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_LiftAPI),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_LiftAPI),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_LiftAPI),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_LiftAPI),
            endpoint: this.sRESTBaseURL + '/api/WB_LiftAPIs',
            recordClass: WBSchemaRows.WB_LiftAPIRow,
            applySchema: applySchema,
            schema: WBSchemas_PM.WB_LiftAPI,
            relations: WBSchemas_PM.WB_LiftAPI_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // Search
        // <editor-fold>
        this.defineMapper(WBSchemas.esWB_Search, {
            name: WBSchemas.esWB_Search,
            idAttribute: 'WB_SEARCH_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Search),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Search),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Search),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Search),
            endpoint: this.sRESTBaseURL + '/api/WB_Searches',
            recordClass: WBSchemaRows.WB_SearchRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Search,
            relations: WBSchemas_Other.WB_Search_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Search_Profile, {
            name: WBSchemas.esWB_Search_Profile,
            idAttribute: 'WB_SEARCH_PROFILE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Search_Profile),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Search_Profile),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Search_Profile),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Search_Profile),
            endpoint: this.sRESTBaseURL + '/api/WB_Search_Profiles',
            recordClass: WBSchemaRows.WB_SearchProfileRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Search_Profile,
            relations: WBSchemas_Other.WB_Search_Profile_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Search_ProfileCR, {
            name: WBSchemas.esWB_Search_ProfileCR,
            idAttribute: 'WB_SEARCH_PROFILECR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Search_ProfileCR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Search_ProfileCR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Search_ProfileCR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Search_ProfileCR),
            endpoint: this.sRESTBaseURL + '/api/WB_Search_ProfileCRs',
            recordClass: WBSchemaRows.WB_SearchProfileCRRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Search_ProfileCR,
            relations: WBSchemas_Other.WB_Search_ProfileCR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Search_ProfileFCR, {
            name: WBSchemas.esWB_Search_ProfileFCR,
            idAttribute: 'WB_SEARCH_PROFILEFCR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Search_ProfileFCR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Search_ProfileFCR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Search_ProfileFCR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Search_ProfileFCR),
            endpoint: this.sRESTBaseURL + '/api/WB_Search_ProfileFCRs',
            recordClass: WBSchemaRows.WB_SearchProfileFCRRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Search_ProfileFCR,
            relations: WBSchemas_Other.WB_Search_ProfileFCR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Search_ProfileGCR, {
            name: WBSchemas.esWB_Search_ProfileGCR,
            idAttribute: 'WB_SEARCH_PROFILEGCR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Search_ProfileGCR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Search_ProfileGCR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Search_ProfileGCR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Search_ProfileGCR),
            endpoint: this.sRESTBaseURL + '/api/WB_Search_ProfileGCRs',
            recordClass: WBSchemaRows.WB_SearchProfileGCRRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Search_ProfileGCR,
            relations: WBSchemas_Other.WB_Search_ProfileGCR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Hierarchy, {
            name: WBSchemas.esWB_Hierarchy,
            idAttribute: 'WB_HIERARCHY_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Hierarchy),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Hierarchy),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Hierarchy),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Hierarchy),
            endpoint: this.sRESTBaseURL + '/api/WB_Hierarchies',
            recordClass: WBSchemaRows.WB_HierarchyRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Hierarchy,
            relations: WBSchemas_DataFactory.WB_Hierarchy_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Hierarchy_Level, {
            name: WBSchemas.esWB_Hierarchy_Level,
            idAttribute: 'WB_HIERARCHYLR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Hierarchy_Level),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Hierarchy_Level),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Hierarchy_Level),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Hierarchy_Level),
            endpoint: this.sRESTBaseURL + '/api/WB_HierarchyLRs',
            recordClass: WBSchemaRows.WB_HierarchyLevelRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Hierarchy_Level,
            relations: WBSchemas_DataFactory.WB_Hierarchy_Level_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Hierarchy_MDScope, {
            name: WBSchemas.esWB_Hierarchy_MDScope,
            idAttribute: 'WB_HIERARCHYSCR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Hierarchy_MDScope),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Hierarchy_MDScope),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Hierarchy_MDScope),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Hierarchy_MDScope),
            endpoint: this.sRESTBaseURL + '/api/WB_HierarchySCRs',
            recordClass: WBSchemaRows.WB_HierarchyMDScopeRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Hierarchy_MDScope,
            relations: WBSchemas_DataFactory.WB_Hierarchy_MDScope_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Hierarchy_Period, {
            name: WBSchemas.esWB_Hierarchy_Period,
            idAttribute: 'WB_HIERARCHYMPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Hierarchy_Period),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Hierarchy_Period),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Hierarchy_Period),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Hierarchy_Period),
            endpoint: this.sRESTBaseURL + '/api/WB_HierarchyMPRs',
            recordClass: WBSchemaRows.WB_HierarchyPeriodRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Hierarchy_Period,
            relations: WBSchemas_DataFactory.WB_Hierarchy_Period_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Hierarchy_SalesArea, {
            name: WBSchemas.esWB_Hierarchy_SalesArea,
            idAttribute: 'WB_HIERARCHYSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Hierarchy_SalesArea),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Hierarchy_SalesArea),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Hierarchy_SalesArea),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Hierarchy_SalesArea),
            endpoint: this.sRESTBaseURL + '/api/WB_HierarchySRs',
            recordClass: WBSchemaRows.WB_HierarchySalesAreaRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Hierarchy_SalesArea,
            relations: WBSchemas_DataFactory.WB_Hierarchy_SalesArea_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Hierarchy_Attribute_Generation, {
            name: WBSchemas.esWB_Hierarchy_Attribute_Generation,
            idAttribute: 'WB_HIERARCHYAGR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Hierarchy_Attribute_Generation),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Hierarchy_Attribute_Generation),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Hierarchy_Attribute_Generation),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_Hierarchy_Attribute_Generation
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_HierarchyAGRs',
            recordClass: WBSchemaRows.WB_HierarchyAttributeGenerationRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Hierarchy_Attribute_Generation,
            relations: WBSchemas_DataFactory.WB_Hierarchy_Attribute_Generation_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Hierarchy_Entity_Generation, {
            name: WBSchemas.esWB_Hierarchy_Entity_Generation,
            idAttribute: 'WB_HIERARCHYEGR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Hierarchy_Entity_Generation),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Hierarchy_Entity_Generation),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Hierarchy_Entity_Generation),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Hierarchy_Entity_Generation),
            endpoint: this.sRESTBaseURL + '/api/WB_HierarchyEGRs',
            recordClass: WBSchemaRows.WB_HierarchyEntityGenerationRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Hierarchy_Entity_Generation,
            relations: WBSchemas_DataFactory.WB_Hierarchy_Entity_Generation_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Hierarchy_Intelligent_Key, {
            name: WBSchemas.esWB_Hierarchy_Intelligent_Key,
            idAttribute: 'WB_HIERARCHYIKR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Hierarchy_Intelligent_Key),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Hierarchy_Intelligent_Key),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Hierarchy_Intelligent_Key),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Hierarchy_Intelligent_Key),
            endpoint: this.sRESTBaseURL + '/api/WB_HierarchyIKRs',
            recordClass: WBSchemaRows.WB_HierarchyIntelligentKeyRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Hierarchy_Intelligent_Key,
            relations: WBSchemas_DataFactory.WB_Hierarchy_Intelligent_Key_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Hierarchy_Export, {
            name: WBSchemas.esWB_Hierarchy_Export,
            idAttribute: 'WB_HIERARCHY_EXPORT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Hierarchy_Export),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Hierarchy_Export),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Hierarchy_Export),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Hierarchy_Export),
            endpoint: this.sRESTBaseURL + '/api/WB_Hierarchy_Exports',
            recordClass: WBSchemaRows.WB_HierarchyExportRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Hierarchy_Export,
            relations: WBSchemas_DataFactory.WB_Hierarchy_Export_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold Label>

        this.defineMapper(WBSchemas.esWB_Label_Type, {
            name: WBSchemas.esWB_Label_Type,
            idAttribute: 'WB_LABELM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Label_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Label_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Label_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Label_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_LabelMs',
            recordClass: WBSchemaRows.WB_LabelTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Label_Type,
            relations: WBSchemas_DataFactory.WB_Label_Type_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Label, {
            name: WBSchemas.esWB_Label,
            idAttribute: 'WB_LABEL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Label),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Label),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Label),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Label),
            endpoint: this.sRESTBaseURL + '/api/WB_Labels',
            recordClass: WBSchemaRows.WB_LabelRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Label,
            relations: WBSchemas_DataFactory.WB_Label_JSData_RELs,
            debug: bDebug
        })


        // </editor-fold>


        // <editor-fold Policy Area>
        this.defineMapper(WBSchemas.esWB_PolicyArea, {
            name: WBSchemas.esWB_PolicyArea,
            idAttribute: 'WB_POLICYAREA_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PolicyArea),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PolicyArea),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PolicyArea),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PolicyArea),
            endpoint: this.sRESTBaseURL + '/api/WB_PolicyAreas',
            recordClass: WBSchemaRows.WB_PolicyAreaRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Policy_Area,
            relations: WBSchemas_DataFactory.WB_Policy_Area_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Policy_Term_Type, {
            name: WBSchemas.esWB_Policy_Term_Type,
            idAttribute: 'WB_POLICYTERMM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Policy_Term_Type),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Policy_Term_Type),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Policy_Term_Type),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Policy_Term_Type),
            endpoint: this.sRESTBaseURL + '/api/WB_PolicyTermMs',
            recordClass: WBSchemaRows.WB_PolicyTermTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Policy_Term_Type,
            relations: WBSchemas_DataFactory.WB_Policy_Term_Type_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        // <editor-fold Policy>
        this.defineMapper(WBSchemas.esWB_Policy, {
            name: WBSchemas.esWB_Policy,
            idAttribute: 'WB_POLICY_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Policy),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Policy),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Policy),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Policy),
            endpoint: this.sRESTBaseURL + '/api/WB_Policies',
            recordClass: WBSchemaRows.WB_PolicyRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Policy,
            relations: WBSchemas_DataFactory.WB_Policy_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Policy_Term, {
            name: WBSchemas.esWB_Policy_Term,
            idAttribute: 'WB_POLICYTERM_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Policy_Term),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Policy_Term),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Policy_Term),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Policy_Term),
            endpoint: this.sRESTBaseURL + '/api/WB_PolicyTerms',
            recordClass: WBSchemaRows.WB_PolicyTermRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Policy_Term,
            relations: WBSchemas_DataFactory.WB_Policy_Term_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Policy_Procedure, {
            name: WBSchemas.esWB_Policy_Procedure,
            idAttribute: 'WB_POLICYPLR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Policy_Procedure),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Policy_Procedure),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Policy_Procedure),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Policy_Procedure),
            endpoint: this.sRESTBaseURL + '/api/WB_PolicyPLRs',
            recordClass: WBSchemaRows.WB_PolicyProcedureRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Policy_Procedure,
            relations: WBSchemas_DataFactory.WB_Policy_Procedure_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Policy_SalesArea, {
            name: WBSchemas.esWB_Policy_SalesArea,
            idAttribute: 'WB_POLICYSAR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Policy_SalesArea),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Policy_SalesArea),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Policy_SalesArea),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Policy_SalesArea),
            endpoint: this.sRESTBaseURL + '/api/WB_PolicySARs',
            recordClass: WBSchemaRows.WB_PolicySalesAreaRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Policy_SalesArea,
            relations: WBSchemas_DataFactory.WB_Policy_SalesArea_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Policy_FundType, {
            name: WBSchemas.esWB_Policy_FundType,
            idAttribute: 'WB_POLICYFTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Policy_FundType),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Policy_FundType),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Policy_FundType),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Policy_FundType),
            endpoint: this.sRESTBaseURL + '/api/WB_PolicyFTRs',
            recordClass: WBSchemaRows.WB_PolicyFundTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Policy_FundType,
            relations: WBSchemas_DataFactory.WB_Policy_FundType_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Policy_Cube, {
            name: WBSchemas.esWB_Policy_Cube,
            idAttribute: 'WB_POLICYCUBE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Policy_Cube),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Policy_Cube),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Policy_Cube),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Policy_Cube),
            endpoint: this.sRESTBaseURL + '/api/WB_PolicyCubes',
            recordClass: WBSchemaRows.WB_PolicyCubeRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Policy_Cube,
            relations: WBSchemas_DataFactory.WB_Policy_Cube_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Policy_Cube_TermR, {
            name: WBSchemas.esWB_Policy_Cube_TermR,
            idAttribute: 'WB_POLICYCUBETR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Policy_Cube_TermR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Policy_Cube_TermR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Policy_Cube_TermR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Policy_Cube_TermR),
            endpoint: this.sRESTBaseURL + '/api/WB_PolicyCubeTRs',
            recordClass: WBSchemaRows.WB_PolicyCubeTermRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Policy_Cube_Term,
            relations: WBSchemas_DataFactory.WB_Policy_Cube_Term_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Policy_Cube_Process, {
            name: WBSchemas.esWB_Policy_Cube_Process,
            idAttribute: 'WB_POLICYCUBEPROCESS_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Policy_Cube_Process),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Policy_Cube_Process),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Policy_Cube_Process),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Policy_Cube_Process),
            endpoint: this.sRESTBaseURL + '/api/WB_PolicyCubeProcesses',
            recordClass: WBSchemaRows.WB_PolicyCubeProcessRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Policy_Cube_Process,
            relations: WBSchemas_DataFactory.WB_Policy_Cube_Process_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Policy_Cube_ProcessR, {
            name: WBSchemas.esWB_Policy_Cube_ProcessR,
            idAttribute: 'WB_POLICYCUBEPROCESSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Policy_Cube_ProcessR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Policy_Cube_ProcessR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Policy_Cube_ProcessR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Policy_Cube_ProcessR),
            endpoint: this.sRESTBaseURL + '/api/WB_PolicyCubeProcessRs',
            recordClass: WBSchemaRows.WB_PolicyCubeProcessRRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Policy_Cube_ProcessR,
            relations: WBSchemas_DataFactory.WB_Policy_Cube_ProcessR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Policy_Term_Value, {
            name: WBSchemas.esWB_Policy_Term_Value,
            idAttribute: 'WB_POLICYTERMVALUE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Policy_Term_Value),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Policy_Term_Value),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Policy_Term_Value),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Policy_Term_Value),
            endpoint: this.sRESTBaseURL + '/api/WB_PolicyTermValues',
            recordClass: WBSchemaRows.WB_PolicyTermValueRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Policy_Term_Value,
            relations: WBSchemas_DataFactory.WB_Policy_Term_Value_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Policy_SpendType_TermR, {
            name: WBSchemas.esWB_Policy_SpendType_TermR,
            idAttribute: 'WB_POLICYSPENDTYPETERMR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Policy_SpendType_TermR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Policy_SpendType_TermR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Policy_SpendType_TermR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Policy_SpendType_TermR),
            endpoint: this.sRESTBaseURL + '/api/WB_PolicySpendTypeTermRs',
            recordClass: WBSchemaRows.WB_PolicySpendTypeTermRRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Policy_SpendType_TermR,
            relations: WBSchemas_DataFactory.WB_Policy_SpendType_TermR_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold Tree>

        this.defineMapper(WBSchemas.esWB_Tree, {
            name: WBSchemas.esWB_Tree,
            idAttribute: 'WB_TREE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Tree),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Tree),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Tree),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Tree),
            endpoint: this.sRESTBaseURL + '/api/WB_Trees',
            recordClass: WBSchemaRows.WB_TreeRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Tree,
            relations: WBSchemas_DataFactory.WB_Tree_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Tree_Folder, {
            name: WBSchemas.esWB_Tree_Folder,
            idAttribute: 'WB_TREE_FOLDER_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Tree_Folder),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Tree_Folder),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Tree_Folder),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Tree_Folder),
            endpoint: this.sRESTBaseURL + '/api/WB_Tree_Folders',
            recordClass: WBSchemaRows.WB_TreeFolderRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Tree_Folder,
            relations: WBSchemas_DataFactory.WB_Tree_Folder_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold>

        // <editor-fold DataMart>

        this.defineMapper(WBSchemas.esWB_DataMart, {
            name: WBSchemas.esWB_DataMart,
            idAttribute: 'WB_DATAMART_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_DataMart),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_DataMart),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_DataMart),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_DataMart),
            endpoint: this.sRESTBaseURL + '/api/WB_DataMarts',
            recordClass: WBSchemaRows.WB_DataMartRow,
            applySchema: applySchema,
            schema: WBSchemas_DM.WB_DataMart,
            relations: WBSchemas_DM.WB_DataMart_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_DataMart_Plan_Relationship, {
            name: WBSchemas.esWB_DataMart_Plan_Relationship,
            idAttribute: 'WB_DATAMARTPLNR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_DataMart_Plan_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_DataMart_Plan_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_DataMart_Plan_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_DataMart_Plan_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_DataMartPlnRs',
            recordClass: WBSchemaRows.WB_DataMartPlanRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DM.WB_DataMart_Plan_Relationship,
            relations: WBSchemas_DM.WB_DataMart_Plan_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_DataMart_Scope_Customer_Relationship, {
            name: WBSchemas.esWB_DataMart_Scope_Customer_Relationship,
            idAttribute: 'WB_DATAMARTCHR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_DataMart_Scope_Customer_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_DataMart_Scope_Customer_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_DataMart_Scope_Customer_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_DataMart_Scope_Customer_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_DataMartCHRs',
            recordClass: WBSchemaRows.WB_DataMartScopeCustomerRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DM.WB_DataMart_Scope_Customer_Relationship,
            relations: WBSchemas_DM.WB_DataMart_Scope_Customer_Relationship_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_DataMart_Scope_Customer_Relationship_Filter, {
            name: WBSchemas.esWB_DataMart_Scope_Customer_Relationship_Filter,
            idAttribute: 'WB_DATAMARTCHNR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_DataMart_Scope_Customer_Relationship_Filter),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_DataMart_Scope_Customer_Relationship_Filter),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_DataMart_Scope_Customer_Relationship_Filter),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_DataMart_Scope_Customer_Relationship_Filter
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_DataMartCHNRs',
            recordClass: WBSchemaRows.WB_DataMartScopeCustomerRelationshipFilterRow,
            applySchema: applySchema,
            schema: WBSchemas_DM.WB_DataMart_Scope_Customer_Relationship_Filter,
            relations: WBSchemas_DM.WB_DataMart_Scope_Customer_Relationship_Filter_JSData_RELs,
            debug: bDebug
        })
        this.defineMapper(WBSchemas.esWB_DataMart_Scope_Product_Relationship, {
            name: WBSchemas.esWB_DataMart_Scope_Product_Relationship,
            idAttribute: 'WB_DATAMARTPHR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_DataMart_Scope_Product_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_DataMart_Scope_Product_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_DataMart_Scope_Product_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_DataMart_Scope_Product_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_DataMartPHRs',
            recordClass: WBSchemaRows.WB_DataMartScopeProductRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DM.WB_DataMart_Scope_Product_Relationship,
            relations: WBSchemas_DM.WB_DataMart_Scope_Product_Relationship_JSData_RELs,
            debug: bDebug
        })
        this.defineMapper(WBSchemas.esWB_DataMart_Scope_Product_Relationship_Filter, {
            name: WBSchemas.esWB_DataMart_Scope_Product_Relationship_Filter,
            idAttribute: 'WB_DATAMARTPHNR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_DataMart_Scope_Product_Relationship_Filter),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_DataMart_Scope_Product_Relationship_Filter),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_DataMart_Scope_Product_Relationship_Filter),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_DataMart_Scope_Product_Relationship_Filter
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_DataMartPHNRs',
            recordClass: WBSchemaRows.WB_DataMartScopeProductRelationshipFilterRow,
            applySchema: applySchema,
            schema: WBSchemas_DM.WB_DataMart_Scope_Product_Relationship_Filter,
            relations: WBSchemas_DM.WB_DataMart_Scope_Product_Relationship_Filter_JSData_RELs,
            debug: bDebug
        })
        this.defineMapper(WBSchemas.esWB_DataMart_Aggregation_Customer_Relationship, {
            name: WBSchemas.esWB_DataMart_Aggregation_Customer_Relationship,
            idAttribute: 'WB_DATAMARTCAR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_DataMart_Aggregation_Customer_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_DataMart_Aggregation_Customer_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_DataMart_Aggregation_Customer_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_DataMart_Aggregation_Customer_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_DataMartCARs',
            recordClass: WBSchemaRows.WB_DataMartAggregationCustomerRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DM.WB_DataMart_Aggregation_Customer_Relationship,
            relations: WBSchemas_DM.WB_DataMart_Aggregation_Customer_Relationship_JSData_RELs,
            debug: bDebug
        })
        this.defineMapper(WBSchemas.esWB_DataMart_Restatement_Customer_Relationship, {
            name: WBSchemas.esWB_DataMart_Restatement_Customer_Relationship,
            idAttribute: 'WB_DATAMARTCRR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_DataMart_Restatement_Customer_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_DataMart_Restatement_Customer_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_DataMart_Restatement_Customer_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_DataMart_Restatement_Customer_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_DataMartCRRs',
            recordClass: WBSchemaRows.WB_DataMartRestatementCustomerRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DM.WB_DataMart_Restatement_Customer_Relationship,
            relations: WBSchemas_DM.WB_DataMart_Restatement_Customer_Relationship_JSData_RELs,
            debug: bDebug
        })
        this.defineMapper(WBSchemas.esWB_DataMart_Aggregation_Product_Relationship, {
            name: WBSchemas.esWB_DataMart_Aggregation_Product_Relationship,
            idAttribute: 'WB_DATAMARTPAR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_DataMart_Aggregation_Product_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_DataMart_Aggregation_Product_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_DataMart_Aggregation_Product_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_DataMart_Aggregation_Product_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_DataMartPARs',
            recordClass: WBSchemaRows.WB_DataMartAggregationProductRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DM.WB_DataMart_Aggregation_Product_Relationship,
            relations: WBSchemas_DM.WB_DataMart_Aggregation_Product_Relationship_JSData_RELs,
            debug: bDebug
        })
        this.defineMapper(WBSchemas.esWB_DataMart_Restatement_Product_Relationship, {
            name: WBSchemas.esWB_DataMart_Restatement_Product_Relationship,
            idAttribute: 'WB_DATAMARTPRR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_DataMart_Restatement_Product_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_DataMart_Restatement_Product_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_DataMart_Restatement_Product_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_DataMart_Restatement_Product_Relationship
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_DataMartPRRs',
            recordClass: WBSchemaRows.WB_DataMartRestatementProductRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DM.WB_DataMart_Restatement_Product_Relationship,
            relations: WBSchemas_DM.WB_DataMart_Restatement_Product_Relationship_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold>

        this.defineMapper(WBSchemas.esWB_Product_Condition_Type_Text, {
            name: WBSchemas.esWB_Product_Condition_Type_Text,
            idAttribute: 'PROD_CNDMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Condition_Type_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Condition_Type_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Condition_Type_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_Condition_Type_Text),
            endpoint: this.sRESTBaseURL + '/api/Prod_CndMTs',
            recordClass: WBSchemaRows.WB_ProductConditionTypeTextRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Condition_Type_Text,
            relations: WBSchemas_DataFactory.WB_Product_Condition_Type_Text_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_General_Ledger_Account_Text, {
            name: WBSchemas.esWB_General_Ledger_Account_Text,
            idAttribute: 'WB_GLACCTT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_General_Ledger_Account_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_General_Ledger_Account_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_General_Ledger_Account_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_General_Ledger_Account_Text),
            endpoint: this.sRESTBaseURL + '/api/WB_GLAcctTs',
            recordClass: WBSchemaRows.WB_GeneralLedgerAccountTextRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_General_Ledger_Account_Text,
            relations: WBSchemas_DataFactory.WB_General_Ledger_Account_Text_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Product_Group_Text, {
            name: WBSchemas.esWB_Product_Group_Text,
            idAttribute: 'WB_PRDGRPT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Group_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Group_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Group_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_Group_Text),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdGrpTs',
            recordClass: WBSchemaRows.WB_ProductGroupTextRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Group_Text,
            relations: WBSchemas_DataFactory.WB_Product_Group_Text_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Finished_Product_Text, {
            name: WBSchemas.esWB_Finished_Product_Text,
            idAttribute: 'WB_PRODT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Finished_Product_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Finished_Product_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Finished_Product_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Finished_Product_Text),
            endpoint: this.sRESTBaseURL + '/api/WB_ProdTs',
            recordClass: WBSchemaRows.WB_FinishedProductTextRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Finished_Product_Text,
            relations: WBSchemas_DataFactory.WB_Finished_Product_Text_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_UOM_Text, {
            name: WBSchemas.esWB_UOM_Text,
            idAttribute: 'WB_UOMT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_UOM_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_UOM_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_UOM_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_UOM_Text),
            endpoint: this.sRESTBaseURL + '/api/WB_UOMTs',
            recordClass: WBSchemaRows.WB_UOMTextRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_UOM_Text,
            relations: WBSchemas_DataFactory.WB_UOM_Text_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Product_Hierarchy_Text, {
            name: WBSchemas.esWB_Product_Hierarchy_Text,
            idAttribute: 'WB_PRDHT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Product_Hierarchy_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Product_Hierarchy_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Product_Hierarchy_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Product_Hierarchy_Text),
            endpoint: this.sRESTBaseURL + '/api/WB_PrdHTs',
            recordClass: WBSchemaRows.WB_ProductHierarchyTextRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Product_Hierarchy_Text,
            relations: WBSchemas_DataFactory.WB_Product_Hierarchy_Text_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Customer_Hierarchy_Text, {
            name: WBSchemas.esWB_Customer_Hierarchy_Text,
            idAttribute: 'WB_CUSTHT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Customer_Hierarchy_Text),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Customer_Hierarchy_Text),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Customer_Hierarchy_Text),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Customer_Hierarchy_Text),
            endpoint: this.sRESTBaseURL + '/api/WB_CustHTs',
            recordClass: WBSchemaRows.WB_CustomerHierarchyTextRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_Customer_Hierarchy_Text,
            relations: WBSchemas_DataFactory.WB_Customer_Hierarchy_Text_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_CustomerCR, {
            name: WBSchemas.esWB_CustomerCR,
            idAttribute: 'WB_CUSTCR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_CustomerCR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_CustomerCR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_CustomerCR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_CustomerCR),
            endpoint: this.sRESTBaseURL + '/api/WB_CustCRs',
            recordClass: WBSchemaRows.WB_CustomerCRRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_CustomerCR,
            relations: WBSchemas_DataFactory.WB_CustomerCR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_CustomerSAR, {
            name: WBSchemas.esWB_CustomerSAR,
            idAttribute: 'WB_CUSTSAR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_CustomerSAR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_CustomerSAR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_CustomerSAR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_CustomerSAR),
            endpoint: this.sRESTBaseURL + '/api/WB_CustSARs',
            recordClass: WBSchemaRows.WB_CustomerSARRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_CustomerSAR,
            relations: WBSchemas_DataFactory.WB_CustomerSAR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Staging, {
            name: WBSchemas.esWB_Staging,
            idAttribute: 'WB_STAGING_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Staging),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Staging),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Staging),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Staging),
            endpoint: this.sRESTBaseURL + '/api/WB_Stagings',
            recordClass: WBSchemaRows.WB_StagingRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Staging,
            relations: WBSchemas_Other.WB_Staging_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Staging_ClientRaw, {
            name: WBSchemas.esWB_Staging_ClientRaw,
            idAttribute: 'WB_STAGING_CLIENTRAW_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Staging_ClientRaw),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Staging_ClientRaw),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Staging_ClientRaw),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Staging_ClientRaw),
            endpoint: this.sRESTBaseURL + '/api/WB_Staging_ClientRaws',
            recordClass: WBSchemaRows.WB_StagingClientRawRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Staging_ClientRaw,
            relations: WBSchemas_Other.WB_Staging_ClientRaw_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Staging_Archive, {
            name: WBSchemas.esWB_Staging_Archive,
            idAttribute: 'WB_STAGING_ARCHIVE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Staging_Archive),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Staging_Archive),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Staging_Archive),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Staging_Archive),
            endpoint: this.sRESTBaseURL + '/api/WB_Staging_Archives',
            recordClass: WBSchemaRows.WB_StagingArchiveRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_Staging_Archive,
            relations: WBSchemas_Other.WB_Staging_Archive_JSData_RELs,
            debug: bDebug
        })
        // </editor-fold> // Define Mappers

        // <editor-fold desc="Control Point - Evt Volume">
        // Control Point Evt Volume
        this.defineMapper(WBSchemas.esWB_EvtVolume, {
            name: WBSchemas.esWB_EvtVolume,
            idAttribute: 'WB_EVTVOLUME_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_EvtVolume),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_EvtVolume),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_EvtVolume),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_EvtVolume),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtVolumes',
            recordClass: WBSchemaRows.WB_EvtVolumeRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_EvtVolume,
            relations: WBSchemas_Cntrl.WB_EvtVolume_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_EvtVolumePR, {
            name: WBSchemas.esWB_EvtVolumePR,
            idAttribute: 'WB_EVTVOLUMEPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_EvtVolumePR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_EvtVolumePR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_EvtVolumePR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_EvtVolumePR),
            endpoint: this.sRESTBaseURL + '/api/WB_EvtVolumePRs',
            recordClass: WBSchemaRows.WB_EvtVolumePartitionRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_EvtVolume_Partition,
            relations: WBSchemas_Cntrl.WB_EvtVolume_Partition_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PEA_Event_Override, {
            name: WBSchemas.esWB_PEA_Event_Override,
            idAttribute: 'WB_OVERRIDE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PEA_Event_Override),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PEA_Event_Override),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PEA_Event_Override),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PEA_Event_Override),
            endpoint: this.sRESTBaseURL + '/api/WB_Overrides',
            recordClass: WBSchemaRows.WB_PEAEventOverrideRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_PEA_Event_Override,
            relations: WBSchemas_Other.WB_PEA_Event_Override_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PEA_Event_ProductGroup_Override, {
            name: WBSchemas.esWB_PEA_Event_ProductGroup_Override,
            idAttribute: 'WB_OVERRIDE_PRDGRP_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PEA_Event_ProductGroup_Override),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PEA_Event_ProductGroup_Override),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PEA_Event_ProductGroup_Override),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_PEA_Event_ProductGroup_Override
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Override_PrdGrps',
            recordClass: WBSchemaRows.WB_PEAEventProductGroupOverrideRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_PEA_Event_ProductGroup_Override,
            relations: WBSchemas_Other.WB_PEA_Event_ProductGroup_Override_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PEA_Event_Spend_Override, {
            name: WBSchemas.esWB_PEA_Event_Spend_Override,
            idAttribute: 'WB_OVERRIDE_EVTS_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PEA_Event_Spend_Override),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PEA_Event_Spend_Override),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PEA_Event_Spend_Override),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PEA_Event_Spend_Override),
            endpoint: this.sRESTBaseURL + '/api/WB_Override_Tactics',
            recordClass: WBSchemaRows.WB_PEAEventSpendOverrideRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_PEA_Event_Spend_Override,
            relations: WBSchemas_Other.WB_PEA_Event_Spend_Override_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PEA_Event_Spend_ProductGroup_Override, {
            name: WBSchemas.esWB_PEA_Event_Spend_ProductGroup_Override,
            idAttribute: 'WB_OVERRIDE_EVTSPG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PEA_Event_Spend_ProductGroup_Override),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PEA_Event_Spend_ProductGroup_Override),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PEA_Event_Spend_ProductGroup_Override),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_PEA_Event_Spend_ProductGroup_Override
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Override_EvtSPGs',
            recordClass: WBSchemaRows.WB_PEAEventSpendProductGroupOverrideRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_PEA_Event_Spend_ProductGroup_Override,
            relations: WBSchemas_Other.WB_PEA_Event_Spend_ProductGroup_Override_JSData_RELs,
            debug: bDebug
        })

        // </editor-fold> // Evt Volume

        // <editor-fold desc="Control Point - Vol Decomp">
        // Control Point Vol Decomp
        this.defineMapper(WBSchemas.esWB_VolDecomp, {
            name: WBSchemas.esWB_VolDecomp,
            idAttribute: 'WB_VOL_DECOMP_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_VolDecomp),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_VolDecomp),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_VolDecomp),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_VolDecomp),
            endpoint: this.sRESTBaseURL + '/api/WB_Vol_Decomps',
            recordClass: WBSchemaRows.WB_VolDecompRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_VolDecomp,
            relations: WBSchemas_Cntrl.WB_VolDecomp_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_VolDecompPR, {
            name: WBSchemas.esWB_VolDecompPR,
            idAttribute: 'WB_VOL_DECOMPPR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_VolDecompPR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_VolDecompPR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_VolDecompPR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_VolDecompPR),
            endpoint: this.sRESTBaseURL + '/api/WB_Vol_DecompPRs',
            recordClass: WBSchemaRows.WB_VolDecompPartitionRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_VolDecomp_Partition,
            relations: WBSchemas_Cntrl.WB_VolDecomp_Partition_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_VolDecompPR_BS_Detail, {
            name: WBSchemas.esWB_VolDecompPR_BS_Detail,
            idAttribute: 'WB_VOL_DECOMPPR_BS_DETAIL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_VolDecompPR_BS_Detail),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_VolDecompPR_BS_Detail),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_VolDecompPR_BS_Detail),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_VolDecompPR_BS_Detail),
            endpoint: this.sRESTBaseURL + '/api/WB_Vol_DecompPR_BS_Details',
            recordClass: WBSchemaRows.WB_VolDecompPRBSDetailRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_VolDecompPR_BS_Detail,
            relations: WBSchemas_Cntrl.WB_VolDecompPR_BS_Detail_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_VolDecompPR_BS_Default, {
            name: WBSchemas.esWB_VolDecompPR_BS_Default,
            idAttribute: 'WB_VOL_DECOMPPR_BS_DEFAULT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_VolDecompPR_BS_Default),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_VolDecompPR_BS_Default),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_VolDecompPR_BS_Default),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_VolDecompPR_BS_Default),
            endpoint: this.sRESTBaseURL + '/api/WB_Vol_DecompPR_BS_Defaults',
            recordClass: WBSchemaRows.WB_VolDecompPRBSDefaultRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_VolDecompPR_BS_Default,
            relations: WBSchemas_Cntrl.WB_VolDecompPR_BS_Default_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_VolDecompPR_EDLP_Detail, {
            name: WBSchemas.esWB_VolDecompPR_EDLP_Detail,
            idAttribute: 'WB_VOL_DECOMPPR_EDLP_DETAIL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_VolDecompPR_EDLP_Detail),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_VolDecompPR_EDLP_Detail),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_VolDecompPR_EDLP_Detail),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_VolDecompPR_EDLP_Detail),
            endpoint: this.sRESTBaseURL + '/api/WB_Vol_DecompPR_EDLP_Details',
            recordClass: WBSchemaRows.WB_VolDecompPREDLPDetailRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_VolDecompPR_EDLP_Detail,
            relations: WBSchemas_Cntrl.WB_VolDecompPR_EDLP_Detail_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_VolDecompPR_EDLP_Default, {
            name: WBSchemas.esWB_VolDecompPR_EDLP_Default,
            idAttribute: 'WB_VOL_DECOMPPR_EDLP_DEFAULT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_VolDecompPR_EDLP_Default),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_VolDecompPR_EDLP_Default),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_VolDecompPR_EDLP_Default),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_VolDecompPR_EDLP_Default),
            endpoint: this.sRESTBaseURL + '/api/WB_Vol_DecompPR_EDLP_Defaults',
            recordClass: WBSchemaRows.WB_VolDecompPREDLPDefaultRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_VolDecompPR_EDLP_Default,
            relations: WBSchemas_Cntrl.WB_VolDecompPR_EDLP_Default_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_VolDecompPR_Merch_Default, {
            name: WBSchemas.esWB_VolDecompPR_Merch_Default,
            idAttribute: 'WB_VOL_DECOMPPR_MERCH_DEFAULT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_VolDecompPR_Merch_Default),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_VolDecompPR_Merch_Default),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_VolDecompPR_Merch_Default),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_VolDecompPR_Merch_Default),
            endpoint: this.sRESTBaseURL + '/api/WB_Vol_DecompPR_Merch_Defaults',
            recordClass: WBSchemaRows.WB_VolDecompPRMerchDefaultRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_VolDecompPR_Merch_Default,
            relations: WBSchemas_Cntrl.WB_VolDecompPR_Merch_Default_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_VolDecompPR_PassThrough_Default, {
            name: WBSchemas.esWB_VolDecompPR_PassThrough_Default,
            idAttribute: 'WB_VOL_DECOMPPR_PASSTHROUGH_DEFAULT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_VolDecompPR_PassThrough_Default),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_VolDecompPR_PassThrough_Default),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_VolDecompPR_PassThrough_Default),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_VolDecompPR_PassThrough_Default
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Vol_DecompPR_PassThrough_Defaults',
            recordClass: WBSchemaRows.WB_VolDecompPRPassThroughDefaultRow,
            applySchema: applySchema,
            schema: WBSchemas_Cntrl.WB_VolDecompPR_PassThrough_Default,
            relations: WBSchemas_Cntrl.WB_VolDecompPR_PassThrough_Default_JSData_RELs,
            debug: bDebug
        })

        // PPA
        this.defineMapper(WBSchemas.esWB_PPA_Model, {
            name: WBSchemas.esWB_PPA_Model,
            idAttribute: 'WB_PPA_MODEL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Model),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Model),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Model),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Model),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Models',
            recordClass: WBSchemaRows.WB_PPA_ModelRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Model,
            relations: WBSchemas_PPA.WB_PPA_Model_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_Manufacturer, {
            name: WBSchemas.esWB_PPA_Manufacturer,
            idAttribute: 'WB_PPA_MANUFACTURER_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Manufacturer),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Manufacturer),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Manufacturer),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Manufacturer),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Manufacturers',
            recordClass: WBSchemaRows.WB_PPA_ManufacturerRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Manufacturer,
            relations: WBSchemas_PPA.WB_PPA_Manufacturer_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_Market, {
            name: WBSchemas.esWB_PPA_Market,
            idAttribute: 'WB_PPA_MARKET_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Market),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Market),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Market),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Market),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Markets',
            recordClass: WBSchemaRows.WB_PPA_MarketRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Market,
            relations: WBSchemas_PPA.WB_PPA_Market_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_Channel, {
            name: WBSchemas.esWB_PPA_Channel,
            idAttribute: 'WB_PPA_CHANNEL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Channel),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Channel),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Channel),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Channel),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Channels',
            recordClass: WBSchemaRows.WB_PPA_ChannelRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Channel,
            relations: WBSchemas_PPA.WB_PPA_Channel_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_Brand, {
            name: WBSchemas.esWB_PPA_Brand,
            idAttribute: 'WB_PPA_BRAND_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Brand),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Brand),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Brand),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Brand),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Brands',
            recordClass: WBSchemaRows.WB_PPA_BrandRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Brand,
            relations: WBSchemas_PPA.WB_PPA_Brand_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_SubBrand, {
            name: WBSchemas.esWB_PPA_SubBrand,
            idAttribute: 'WB_PPA_SUBBRAND_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_SubBrand),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_SubBrand),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_SubBrand),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_SubBrand),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_SubBrands',
            recordClass: WBSchemaRows.WB_PPA_SubBrandRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_SubBrand,
            relations: WBSchemas_PPA.WB_PPA_SubBrand_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_Attribute, {
            name: WBSchemas.esWB_PPA_Attribute,
            idAttribute: 'WB_PPA_ATTRIBUTE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Attribute),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Attribute),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Attribute),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Attribute),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Attributes',
            recordClass: WBSchemaRows.WB_PPA_AttributeRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Attribute,
            relations: WBSchemas_PPA.WB_PPA_Attribute_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_Attribute_Level, {
            name: WBSchemas.esWB_PPA_Attribute_Level,
            idAttribute: 'WB_PPA_ATTRIBUTE_LEVEL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Attribute_Level),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Attribute_Level),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Attribute_Level),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Attribute_Level),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Attribute_Levels',
            recordClass: WBSchemaRows.WB_PPA_Attribute_LevelRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Attribute_Level,
            relations: WBSchemas_PPA.WB_PPA_Attribute_Level_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_PricePrdType, {
            name: WBSchemas.esWB_PPA_PricePrdType,
            idAttribute: 'WB_PPA_PRICEPRDTYPE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_PricePrdType),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_PricePrdType),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_PricePrdType),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_PricePrdType),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_PricePrdTypes',
            recordClass: WBSchemaRows.WB_PPA_PricePrdTypeRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_PricePrdType,
            relations: WBSchemas_PPA.WB_PPA_PricePrdType_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_PricePrdType_Level, {
            name: WBSchemas.esWB_PPA_PricePrdType_Level,
            idAttribute: 'WB_PPA_PRICEPRDTYPE_LEVEL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_PricePrdType_Level),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_PricePrdType_Level),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_PricePrdType_Level),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_PricePrdType_Level),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_PricePrdType_Levels',
            recordClass: WBSchemaRows.WB_PPA_PricePrdType_LevelRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_PricePrdType_Level,
            relations: WBSchemas_PPA.WB_PPA_PricePrdType_Level_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_Product, {
            name: WBSchemas.esWB_PPA_Product,
            idAttribute: 'WB_PPA_PRODUCT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Product),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Product),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Product),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Product),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Products',
            recordClass: WBSchemaRows.WB_PPA_ProductRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Product,
            relations: WBSchemas_PPA.WB_PPA_Product_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_Product_Attribute, {
            name: WBSchemas.esWB_PPA_Product_Attribute,
            idAttribute: 'WB_PPA_PRODUCT_ATTRIBUTE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Product_Attribute),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Product_Attribute),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Product_Attribute),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Product_Attribute),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Product_Attributes',
            recordClass: WBSchemaRows.WB_PPA_Product_AttributeRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Product_Attribute,
            relations: WBSchemas_PPA.WB_PPA_Product_Attribute_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_Consumer_Segment, {
            name: WBSchemas.esWB_PPA_Consumer_Segment,
            idAttribute: 'WB_PPA_CONSUMER_SEGMENT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Consumer_Segment),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Consumer_Segment),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Consumer_Segment),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Consumer_Segment),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Consumer_Segments',
            recordClass: WBSchemaRows.WB_PPA_Consumer_SegmentRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Consumer_Segment,
            relations: WBSchemas_PPA.WB_PPA_Consumer_Segment_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_Respondent, {
            name: WBSchemas.esWB_PPA_Respondent,
            idAttribute: 'WB_PPA_RESPONDENT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Respondent),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Respondent),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Respondent),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Respondent),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Respondents',
            recordClass: WBSchemaRows.WB_PPA_RespondentRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Respondent,
            relations: WBSchemas_PPA.WB_PPA_Respondent_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_Adjustment, {
            name: WBSchemas.esWB_PPA_Adjustment,
            idAttribute: 'WB_PPA_ADJUSTMENT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Adjustment),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Adjustment),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Adjustment),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Adjustment),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Adjustments',
            recordClass: WBSchemaRows.WB_PPA_AdjustmentRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Adjustment,
            relations: WBSchemas_PPA.WB_PPA_Adjustment_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_Condition, {
            name: WBSchemas.esWB_PPA_Condition,
            idAttribute: 'WB_PPA_CONDITION_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Condition),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Condition),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Condition),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Condition),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Conditions',
            recordClass: WBSchemaRows.WB_PPA_ConditionRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Condition,
            relations: WBSchemas_PPA.WB_PPA_Condition_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_Market_Share, {
            name: WBSchemas.esWB_PPA_Market_Share,
            idAttribute: 'WB_PPA_MARKET_SHARE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Market_Share),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Market_Share),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Market_Share),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Market_Share),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Market_Shares',
            recordClass: WBSchemaRows.WB_PPA_Market_ShareRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Market_Share,
            relations: WBSchemas_PPA.WB_PPA_Market_Share_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_PricePrdType_Coeff, {
            name: WBSchemas.esWB_PPA_PricePrdType_Coeff,
            idAttribute: 'WB_PPA_PRICEPRDTYPE_COEFF_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_PricePrdType_Coeff),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_PricePrdType_Coeff),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_PricePrdType_Coeff),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_PricePrdType_Coeff),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_PricePrdType_Coeffs',
            recordClass: WBSchemaRows.WB_PPA_PricePrdType_CoeffRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_PricePrdType_Coeff,
            relations: WBSchemas_PPA.WB_PPA_PricePrdType_Coeff_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_Scenario, {
            name: WBSchemas.esWB_PPA_Scenario,
            idAttribute: 'WB_PPA_SCENARIO_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Scenario),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Scenario),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Scenario),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Scenario),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Scenarios',
            recordClass: WBSchemaRows.WB_PPA_ScenarioRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Scenario,
            relations: WBSchemas_PPA.WB_PPA_Scenario_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_Scenario_Prd, {
            name: WBSchemas.esWB_PPA_Scenario_Prd,
            idAttribute: 'WB_PPA_SCENARIO_PRD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Scenario_Prd),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Scenario_Prd),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Scenario_Prd),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Scenario_Prd),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Scenario_Prds',
            recordClass: WBSchemaRows.WB_PPA_Scenario_PrdRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Scenario_Prd,
            relations: WBSchemas_PPA.WB_PPA_Scenario_Prd_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_Market_CSR, {
            name: WBSchemas.esWB_PPA_Market_CSR,
            idAttribute: 'WB_PPA_MARKET_CSR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Market_CSR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Market_CSR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Market_CSR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Market_CSR),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Market_CSRs',
            recordClass: WBSchemaRows.WB_PPA_Market_CSRRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Market_CSR,
            relations: WBSchemas_PPA.WB_PPA_Market_CSR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_Market_CSRR, {
            name: WBSchemas.esWB_PPA_Market_CSRR,
            idAttribute: 'WB_PPA_MARKET_CSRR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Market_CSRR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Market_CSRR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Market_CSRR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Market_CSRR),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Market_CSRRs',
            recordClass: WBSchemaRows.WB_PPA_Market_CSRRRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Market_CSRR,
            relations: WBSchemas_PPA.WB_PPA_Market_CSRR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_PriceLevel, {
            name: WBSchemas.esWB_PPA_PriceLevel,
            idAttribute: 'WB_PPA_PRICELEVEL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_PriceLevel),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_PriceLevel),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_PriceLevel),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_PriceLevel),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_PriceLevels',
            recordClass: WBSchemaRows.WB_PPA_PriceLevelRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_PriceLevel,
            relations: WBSchemas_PPA.WB_PPA_PriceLevel_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_PPA_Respondent_Utility, {
            name: WBSchemas.esWB_PPA_Respondent_Utility,
            idAttribute: 'WB_PPA_RESPONDENT_UTILITY_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_PPA_Respondent_Utility),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_PPA_Respondent_Utility),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_PPA_Respondent_Utility),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_PPA_Respondent_Utility),
            endpoint: this.sRESTBaseURL + '/api/WB_PPA_Respondent_Utilitys',
            recordClass: WBSchemaRows.WB_PPA_Respondent_UtilityRow,
            applySchema: applySchema,
            schema: WBSchemas_PPA.WB_PPA_Respondent_Utility,
            relations: WBSchemas_PPA.WB_PPA_Respondent_Utility_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_FC_Override, {
            name: WBSchemas.esWB_FC_Override,
            idAttribute: 'WB_OVRD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_FC_Override),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_FC_Override),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_FC_Override),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_FC_Override),
            endpoint: this.sRESTBaseURL + '/api/WB_Ovrds',
            recordClass: WBSchemaRows.WB_FC_OverrideRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_FC_Override,
            relations: WBSchemas_PEA.WB_FC_Override_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_FC_Override_Log, {
            name: WBSchemas.esWB_FC_Override_Log,
            idAttribute: 'WB_OVRD_LOG_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_FC_Override_Log),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_FC_Override_Log),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_FC_Override_Log),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_FC_Override_Log),
            endpoint: this.sRESTBaseURL + '/api/WB_Ovrd_Logs',
            recordClass: WBSchemaRows.WB_FC_Override_LogRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_FC_Override_Log,
            relations: WBSchemas_PEA.WB_FC_Override_Log_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_FC_Override_Event_Volume, {
            name: WBSchemas.esWB_FC_Override_Event_Volume,
            idAttribute: 'WB_OVRD_EVTVOL_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_FC_Override_Event_Volume),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_FC_Override_Event_Volume),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_FC_Override_Event_Volume),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_FC_Override_Event_Volume),
            endpoint: this.sRESTBaseURL + '/api/WB_Ovrd_EvtVols',
            recordClass: WBSchemaRows.WB_FC_Override_Event_VolumeRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_FC_Override_Event_Volume,
            relations: WBSchemas_PEA.WB_FC_Override_Event_Volume_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_FC_Override_Event_Spend, {
            name: WBSchemas.esWB_FC_Override_Event_Spend,
            idAttribute: 'WB_OVRD_EVTS_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_FC_Override_Event_Spend),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_FC_Override_Event_Spend),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_FC_Override_Event_Spend),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_FC_Override_Event_Spend),
            endpoint: this.sRESTBaseURL + '/api/WB_Ovrd_EvtSes',
            recordClass: WBSchemaRows.WB_FC_Override_Event_SpendRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_FC_Override_Event_Spend,
            relations: WBSchemas_PEA.WB_FC_Override_Event_Spend_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_FC_Override_Event_Date, {
            name: WBSchemas.esWB_FC_Override_Event_Date,
            idAttribute: 'WB_OVRD_EVTDT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_FC_Override_Event_Date),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_FC_Override_Event_Date),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_FC_Override_Event_Date),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_FC_Override_Event_Date),
            endpoint: this.sRESTBaseURL + '/api/WB_Ovrd_EvtDts',
            recordClass: WBSchemaRows.WB_FC_Override_Event_DateRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_FC_Override_Event_Date,
            relations: WBSchemas_PEA.WB_FC_Override_Event_Date_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_FC_Override_Event_Merge, {
            name: WBSchemas.esWB_FC_Override_Event_Merge,
            idAttribute: 'WB_OVRD_EVTMERGE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_FC_Override_Event_Merge),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_FC_Override_Event_Merge),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_FC_Override_Event_Merge),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_FC_Override_Event_Merge),
            endpoint: this.sRESTBaseURL + '/api/WB_Ovrd_EvtMerges',
            recordClass: WBSchemaRows.WB_FC_Override_Event_MergeRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_FC_Override_Event_Merge,
            relations: WBSchemas_PEA.WB_FC_Override_Event_Merge_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_FC_Override_Event_MergeR, {
            name: WBSchemas.esWB_FC_Override_Event_MergeR,
            idAttribute: 'WB_OVRD_EVTMERGER_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_FC_Override_Event_MergeR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_FC_Override_Event_MergeR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_FC_Override_Event_MergeR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_FC_Override_Event_MergeR),
            endpoint: this.sRESTBaseURL + '/api/WB_Ovrd_EvtMergeRs',
            recordClass: WBSchemaRows.WB_FC_Override_Event_MergeRRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_FC_Override_Event_MergeR,
            relations: WBSchemas_PEA.WB_FC_Override_Event_MergeR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_FC_Override_Event_Source_Customer, {
            name: WBSchemas.esWB_FC_Override_Event_Source_Customer,
            idAttribute: 'WB_OVRD_EVTSCST_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_FC_Override_Event_Source_Customer),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_FC_Override_Event_Source_Customer),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_FC_Override_Event_Source_Customer),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_FC_Override_Event_Source_Customer
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Ovrd_EvtSCsts',
            recordClass: WBSchemaRows.WB_FC_Override_Event_Source_CustomerRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_FC_Override_Event_Source_Customer,
            relations: WBSchemas_PEA.WB_FC_Override_Event_Source_Customer_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_FC_Override_Event_Spend_Redistribution, {
            name: WBSchemas.esWB_FC_Override_Event_Spend_Redistribution,
            idAttribute: 'WB_OVRD_SPNDRDST_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_FC_Override_Event_Spend_Redistribution),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_FC_Override_Event_Spend_Redistribution),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_FC_Override_Event_Spend_Redistribution),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_FC_Override_Event_Spend_Redistribution
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Ovrd_SpndRdsts',
            recordClass: WBSchemaRows.WB_FC_Override_Event_Spend_RedistributionRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_FC_Override_Event_Spend_Redistribution,
            relations: WBSchemas_PEA.WB_FC_Override_Event_Spend_Redistribution_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_FC_Override_Event_Spend_RedistributionR, {
            name: WBSchemas.esWB_FC_Override_Event_Spend_RedistributionR,
            idAttribute: 'WB_OVRD_SPNDRDSTR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_FC_Override_Event_Spend_RedistributionR),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_FC_Override_Event_Spend_RedistributionR),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_FC_Override_Event_Spend_RedistributionR),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(
                WBSchemas.esWB_FC_Override_Event_Spend_RedistributionR
            ),
            endpoint: this.sRESTBaseURL + '/api/WB_Ovrd_SpndRdstRs',
            recordClass: WBSchemaRows.WB_FC_Override_Event_Spend_RedistributionRRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_FC_Override_Event_Spend_RedistributionR,
            relations: WBSchemas_PEA.WB_FC_Override_Event_Spend_RedistributionR_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_FC_Override_Event_Product, {
            name: WBSchemas.esWB_FC_Override_Event_Product,
            idAttribute: 'WB_OVRD_EVTPRD_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_FC_Override_Event_Product),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_FC_Override_Event_Product),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_FC_Override_Event_Product),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_FC_Override_Event_Product),
            endpoint: this.sRESTBaseURL + '/api/WB_Ovrd_EvtPrds',
            recordClass: WBSchemaRows.WB_FC_Override_Event_ProductRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_FC_Override_Event_Product,
            relations: WBSchemas_PEA.WB_FC_Override_Event_Product_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_FC_Override_Customer, {
            name: WBSchemas.esWB_FC_Override_Customer,
            idAttribute: 'WB_OVRD_CR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_FC_Override_Customer),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_FC_Override_Customer),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_FC_Override_Customer),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_FC_Override_Customer),
            endpoint: this.sRESTBaseURL + '/api/WB_Ovrd_CRs',
            recordClass: WBSchemaRows.WB_FC_Override_CustomerRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_FC_Override_Customer,
            relations: WBSchemas_PEA.WB_FC_Override_Customer_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_FC_Override_Product, {
            name: WBSchemas.esWB_FC_Override_Product,
            idAttribute: 'WB_OVRD_PR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_FC_Override_Product),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_FC_Override_Product),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_FC_Override_Product),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_FC_Override_Product),
            endpoint: this.sRESTBaseURL + '/api/WB_Ovrd_PRs',
            recordClass: WBSchemaRows.WB_FC_Override_ProductRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_FC_Override_Product,
            relations: WBSchemas_PEA.WB_FC_Override_Product_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_FC_Override_Consumption, {
            name: WBSchemas.esWB_FC_Override_Consumption,
            idAttribute: 'WB_OVRD_CNS_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_FC_Override_Consumption),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_FC_Override_Consumption),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_FC_Override_Consumption),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_FC_Override_Consumption),
            endpoint: this.sRESTBaseURL + '/api/WB_Ovrd_Cnses',
            recordClass: WBSchemaRows.WB_FC_Override_ConsumptionRow,
            applySchema: applySchema,
            schema: WBSchemas_PEA.WB_FC_Override_Consumption,
            relations: WBSchemas_PEA.WB_FC_Override_Consumption_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_UI_Layout, {
            name: WBSchemas.esWB_UI_Layout,
            idAttribute: 'WB_UI_LAYOUT_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_UI_Layout),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_UI_Layout),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_UI_Layout),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_UI_Layout),
            endpoint: this.sRESTBaseURL + '/api/WB_UI_Layouts',
            recordClass: WBSchemaRows.WB_UI_LayoutRow,
            applySchema: applySchema,
            schema: WBSchemas_Other.WB_UI_Layout,
            relations: WBSchemas_Other.WB_UI_Layout_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Funding_Rule, {
            name: WBSchemas.esWB_Funding_Rule,
            idAttribute: 'WB_FUNDINGRULE_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Funding_Rule),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Funding_Rule),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Funding_Rule),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Funding_Rule),
            endpoint: this.sRESTBaseURL + '/api/WB_FundingRules',
            recordClass: WBSchemaRows.WB_FundingRuleRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_FundingRule,
            relations: WBSchemas_DataFactory.WB_FundingRule_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Funding_Rule_Set, {
            name: WBSchemas.esWB_Funding_Rule_Set,
            idAttribute: 'WB_FUNDINGRULESET_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Funding_Rule_Set),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Funding_Rule_Set),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Funding_Rule_Set),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Funding_Rule_Set),
            endpoint: this.sRESTBaseURL + '/api/WB_FundingRuleSets',
            recordClass: WBSchemaRows.WB_FundingRuleSetRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_FundingRuleSet,
            relations: WBSchemas_DataFactory.WB_FundingRuleSet_JSData_RELs,
            debug: bDebug
        })

        this.defineMapper(WBSchemas.esWB_Funding_Rule_Set_Relationship, {
            name: WBSchemas.esWB_Funding_Rule_Set_Relationship,
            idAttribute: 'WB_FUNDINGRULESETR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_Funding_Rule_Set_Relationship),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_Funding_Rule_Set_Relationship),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_Funding_Rule_Set_Relationship),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_Funding_Rule_Set_Relationship),
            endpoint: this.sRESTBaseURL + '/api/WB_FundingRuleSetRs',
            recordClass: WBSchemaRows.WB_FundingRuleSetRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_FundingRuleSetR,
            relations: WBSchemas_DataFactory.WB_FundingRuleSetR_JSData_RELs,
            debug: bDebug
        })


        // Master Data Set - Source
        this.defineMapper(WBSchemas.esWB_External_Product_RelationShip, {
            name: WBSchemas.esWB_External_Product_RelationShip,
            idAttribute: 'WB_EXTPRDR_ID',
            keyGenerator: this.getKeyGenByMapper(WBSchemas.esWB_External_Product_RelationShip),
            recTableName: this.getTableNameByMapper(WBSchemas.esWB_External_Product_RelationShip),
            recLogicalKeys: this.getLogicalKeysByMapper(WBSchemas.esWB_External_Product_RelationShip),
            recNonParentForeignKeys: this.getNonParentForeignKeysByMapper(WBSchemas.esWB_External_Product_RelationShip),
            endpoint: this.sRESTBaseURL + '/api/WB_ExtPrdRs',
            recordClass: WBSchemaRows.WB_ExternalProductRelationshipRow,
            applySchema: applySchema,
            schema: WBSchemas_DataFactory.WB_External_Product_Relationship,
            relations: WBSchemas_DataFactory.WB_External_Product_Relationship_JSData_RELs,
            debug: bDebug
        })

    }
}
// </editor-fold>

export { PAdbContext }
